import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import NotificationBell from "@/components/NotificationBell";
import PromotionalPopup from "@/components/PromotionalPopup";
import UserDropdown from "@/components/UserDropdown";
import AvatarUpload from "@/components/AvatarUpload";
import PhoneInput from "@/components/PhoneInput";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Layout,
  Key,
  Settings,
  Users,
  CreditCard,
  LogOut,
  Plus,
  Trash2,
  Edit,
  Eye,
  EyeOff,
  Copy,
  DollarSign,
  Book,
  Terminal,
  Calendar,
  BarChart3,
  Building2,
  Clock,
  Shield,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  AlertCircle,
  Mail,
  Lock,
  User,
  Check,
  X,
  Send,
  Code,
  ExternalLink,
  UserPlus,
  Sparkles,
  ArrowUpRight,
  ArrowDownRight,
  BookOpen,
  Zap,
  Download,
  MapPin,
  FileText,
  Smartphone,
  Phone,
  Monitor,
  Tablet,
  Globe,
  RefreshCw,
  Fingerprint,
  LogIn,
  AlertTriangle,
  Link2 as Link,
  Search,
  Headphones,
  CheckCircle,
  Activity,
  Bell,
  Paperclip,
  MoreVertical,
  Grid3X3,
  List,
  ArrowUpDown,
  FileEdit,
  CheckCircle2,
  Circle,
  Menu,
  Gift,
  Info,
  HelpCircle
} from "lucide-react";
import CanvasWalkthrough, { useCanvasWalkthrough, STEPS_DASHBOARD } from "../components/CanvasWalkthrough";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

import MultiAPIKeys from "@/components/MultiAPIKeys";
import { getApiUrl, getBaseUrl, PRODUCTION_URL } from "@/config/appConfig";

// Use centralized config for API URL (auto-detects environment)
const getAPI = () => getApiUrl();

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder');

// Card Element styling - hide ZIP since we collect postal code separately
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#374151',
      '::placeholder': { color: '#9CA3AF' },
    },
    invalid: { color: '#EF4444' }
  },
  hidePostalCode: true  // We collect postal code in billing address form
};

// Country list for billing address
const COUNTRIES = [
  { code: 'GB', name: 'United Kingdom', postcodeLabel: 'Postcode', postcodePattern: '[A-Za-z]{1,2}[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}' },
  { code: 'US', name: 'United States', postcodeLabel: 'ZIP Code', postcodePattern: '[0-9]{5}(-[0-9]{4})?' },
  { code: 'CA', name: 'Canada', postcodeLabel: 'Postal Code', postcodePattern: '[A-Za-z][0-9][A-Za-z] ?[0-9][A-Za-z][0-9]' },
  { code: 'AU', name: 'Australia', postcodeLabel: 'Postcode', postcodePattern: '[0-9]{4}' },
  { code: 'DE', name: 'Germany', postcodeLabel: 'PLZ', postcodePattern: '[0-9]{5}' },
  { code: 'FR', name: 'France', postcodeLabel: 'Code Postal', postcodePattern: '[0-9]{5}' },
  { code: 'IE', name: 'Ireland', postcodeLabel: 'Eircode', postcodePattern: '[A-Za-z0-9]{3} ?[A-Za-z0-9]{4}' },
  { code: 'NL', name: 'Netherlands', postcodeLabel: 'Postcode', postcodePattern: '[0-9]{4} ?[A-Za-z]{2}' },
  { code: 'ES', name: 'Spain', postcodeLabel: 'Código Postal', postcodePattern: '[0-9]{5}' },
  { code: 'IT', name: 'Italy', postcodeLabel: 'CAP', postcodePattern: '[0-9]{5}' },
];

// Payment Method Form Component with Billing Address
function PaymentMethodForm({ onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    address: {
      line1: '',
      city: '',
      postal_code: '',
      country: 'GB'  // Default to UK
    }
  });

  const selectedCountry = COUNTRIES.find(c => c.code === billingDetails.address.country) || COUNTRIES[0];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    // Validate billing details
    if (!billingDetails.name || !billingDetails.address.line1 || !billingDetails.address.city || !billingDetails.address.postal_code) {
      setError("Please fill in all billing details");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get setup intent from backend
      const setupRes = await axios.post(`${getAPI()}/subscriptions/setup-payment-method`, {}, { withCredentials: true });
      const { client_secret, setup_intent_id } = setupRes.data;

      // Confirm card setup with Stripe including billing details
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(client_secret, {
        payment_method: { 
          card: elements.getElement(CardElement),
          billing_details: billingDetails
        }
      });

      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
        return;
      }

      // Confirm with backend (pass billing details)
      const confirmRes = await axios.post(
        `${getAPI()}/subscriptions/confirm-payment-method?setup_intent_id=${setup_intent_id}`,
        { billing_details: billingDetails },
        { withCredentials: true }
      );

      toast.success("Card added successfully!");
      onSuccess(confirmRes.data.payment_method);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to add card");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Cardholder Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Cardholder Name *</label>
        <Input
          value={billingDetails.name}
          onChange={(e) => setBillingDetails({...billingDetails, name: e.target.value})}
          placeholder="John Smith"
          required
        />
      </div>

      {/* Card Element */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Card Details *</label>
        <div className="p-4 border rounded-lg bg-gray-50">
          <CardElement options={cardElementOptions} />
        </div>
      </div>

      {/* Billing Address */}
      <div className="pt-2 border-t">
        <p className="text-sm font-medium text-gray-700 mb-3">Billing Address</p>
        
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Address Line 1 *</label>
            <Input
              value={billingDetails.address.line1}
              onChange={(e) => setBillingDetails({
                ...billingDetails, 
                address: {...billingDetails.address, line1: e.target.value}
              })}
              placeholder="123 High Street"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">City *</label>
              <Input
                value={billingDetails.address.city}
                onChange={(e) => setBillingDetails({
                  ...billingDetails, 
                  address: {...billingDetails.address, city: e.target.value}
                })}
                placeholder="London"
                required
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{selectedCountry.postcodeLabel} *</label>
              <Input
                value={billingDetails.address.postal_code}
                onChange={(e) => setBillingDetails({
                  ...billingDetails, 
                  address: {...billingDetails.address, postal_code: e.target.value.toUpperCase()}
                })}
                placeholder={selectedCountry.code === 'GB' ? 'SW1A 1AA' : '12345'}
                pattern={selectedCountry.postcodePattern}
                inputMode="text"  // Allow letters for UK postcodes
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Country *</label>
            <select
              value={billingDetails.address.country}
              onChange={(e) => setBillingDetails({
                ...billingDetails, 
                address: {...billingDetails.address, country: e.target.value, postal_code: ''}
              })}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              {COUNTRIES.map(country => (
                <option key={country.code} value={country.code}>{country.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error && <p className="text-red-500 text-sm">{error}</p>}

      {/* Fee Notice */}
      <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
        <p className="text-xs text-amber-800">
          <strong>Payment Processing:</strong> Stripe charges 1.5% + 20p per transaction for UK cards 
          (2.5% + 20p for EU cards, 3.25% + 20p for international cards).
        </p>
      </div>

      <div className="flex gap-2">
        <Button type="submit" disabled={!stripe || loading} className="flex-1">
          {loading ? "Processing..." : "Add Card"}
        </Button>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
      <p className="text-xs text-gray-500 text-center">
        Your card will be securely stored for Pay As You Go charges (£0.50/seat)
      </p>
    </form>
  );
}

function AnalyticsTab() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [detailView, setDetailView] = useState(null); // 'events' | 'seats' | null
  const [dateRange, setDateRange] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const API = `${getAPI()}`;

  useEffect(() => { fetchDashboard(); }, []);
const fetchDashboard = async () => {
    setLoading(true);
    try {
      const [res, layoutRes] = await Promise.all([
        axios.get(`${API}/organisers/analytics`, { withCredentials: true }),
        axios.get(`${API}/admin/layouts`, { withCredentials: true })
      ]);
      const layouts = layoutRes?.data?.layouts || layoutRes?.data || [];
      setData({
        organisers: res?.data?.organisers || [],
        layouts: Array.isArray(layouts) ? layouts : [],
      });
    } catch {}
    setLoading(false);
  };

  const fmtNum = (n) => (n || 0).toLocaleString();
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

  const filterByDate = (items) => {
    if (dateRange === 'all') return items;
    const now = new Date();
    let from;
    if (dateRange === 'today') { from = new Date(now.getFullYear(), now.getMonth(), now.getDate()); }
    else if (dateRange === '7d') { from = new Date(now - 7 * 86400000); }
    else if (dateRange === '30d') { from = new Date(now - 30 * 86400000); }
    else if (dateRange === '90d') { from = new Date(now - 90 * 86400000); }
    else if (dateRange === 'custom' && customFrom) { from = new Date(customFrom); }
    else return items;
    const to = (dateRange === 'custom' && customTo) ? new Date(customTo + 'T23:59:59') : now;
    return items.filter(i => {
      const d = new Date(i.created_at || i.updated_at || '');
      return d >= from && d <= to;
    });
  };

  const exportCSV = () => {
    if (!data?.layouts) return;
    const filtered = filterByDate(data.layouts);
    const headers = ['Name','Organiser','Event','Seats','Status','Created'];
    const rows = filtered.map(l => [
      l.name, l.organiser_name || 'Unassigned', l.event_name || '-',
      l.total_seats || l.seat_count || 0, l.is_published ? 'Published' : 'Draft',
      fmtDate(l.created_at)
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `seatpilot-analytics-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="w-8 h-8 animate-spin text-violet-500" /></div>;

  const layouts = data?.layouts || [];
  const organisers = data?.organisers || [];
  const filtered = filterByDate(layouts);
  const totalSeats = filtered.reduce((a, l) => a + (l.total_seats || l.seat_count || 0), 0);
  const publishedCount = filtered.filter(l => l.is_published).length;
  const eventsLinked = filtered.filter(l => l.event_id);
  const maxSeats = Math.max(1, ...filtered.map(l => l.total_seats || l.seat_count || 0));

  // Chart data: layouts created per week (last 12 weeks)
  const trendData = (() => {
    const weeks = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const start = new Date(now - i * 7 * 86400000);
      const end = new Date(now - (i - 1) * 7 * 86400000);
      const label = start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
      const count = layouts.filter(l => { const d = new Date(l.created_at || ''); return d >= start && d < end; }).length;
      weeks.push({ name: label, layouts: count });
    }
    return weeks;
  })();

  // Donut data: seats by organiser
  const orgDonut = organisers.slice(0, 5).map((o, i) => ({
    name: o.organiser_name, value: o.total_seats || 0,
    color: ['#8b5cf6','#6366f1','#3b82f6','#10b981','#f59e0b'][i]
  })).filter(o => o.value > 0);

  // Organiser detail view
  if (selectedOrg) {
    const orgUserIds = selectedOrg.user_ids || [];
    const orgLayouts = layouts.filter(l => {
      // Match by organiser_id, user_id (in user_ids list), or organiser_name
      if (l.organiser_id && l.organiser_id === selectedOrg.organiser_id) return true;
      if (l.user_id && orgUserIds.includes(l.user_id)) return true;
      if (l.organiser_name && l.organiser_name === selectedOrg.organiser_name) return true;
      return false;
    });
    const orgSeats = orgLayouts.reduce((a, l) => a + (l.total_seats || l.seat_count || 0), 0);

    return (
      <div className="space-y-6" data-testid="analytics-tab">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setSelectedOrg(null)} className="gap-1" data-testid="back-button">
            <ChevronLeft className="w-4 h-4" /> Back to Analytics
          </Button>
        </div>
        <Card className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center text-lg font-bold text-white">
              {(selectedOrg.organiser_name || '?')[0].toUpperCase()}
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{selectedOrg.organiser_name}</h2>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                {selectedOrg.organiser_email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {selectedOrg.organiser_email}</span>}
                <span>{orgLayouts.length} layouts</span>
                <span>{fmtNum(orgSeats)} seats</span>
                <span>{selectedOrg.events_count || 0} events</span>
              </div>
            </div>
          </div>
          <div className="space-y-2 mt-4">
            {orgLayouts.map((layout, idx) => {
              const seats = layout.total_seats || layout.seat_count || 0;
              return (
                <div key={layout.id || idx} className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:border-violet-200 hover:bg-violet-50/20 transition-colors cursor-pointer" onClick={() => navigate(`/builder/${layout.id}`)}>
                  <Layout className="w-4 h-4 text-violet-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{layout.name}</p>
                    <p className="text-[10px] text-gray-400">{fmtDate(layout.created_at)}{layout.event_name ? ` | ${layout.event_name}` : ' | No event linked'}</p>
                  </div>
                  <span className="text-xs font-semibold text-gray-600">{seats} seats</span>
                  <span className={`px-2 py-0.5 text-[10px] rounded font-medium ${layout.is_published ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-50 text-gray-500 border border-gray-200'}`}>{layout.is_published ? 'live' : 'draft'}</span>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    );
  }

  // Events detail view
  if (detailView === 'events') {
    return (
      <div className="space-y-6" data-testid="analytics-tab">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setDetailView(null)} className="gap-1"><ChevronLeft className="w-4 h-4" /> Back to Analytics</Button>
          <h1 className="text-xl font-bold text-gray-900">Linked Events ({eventsLinked.length})</h1>
        </div>
        <div className="space-y-2">
          {eventsLinked.map((l, i) => (
            <Card key={l.id || i} className="p-4 hover:border-violet-200 transition-colors cursor-pointer" onClick={() => navigate(`/builder/${l.id}`)}>
              <div className="flex items-center gap-4">
                <Calendar className="w-5 h-5 text-blue-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800">{l.event_name || 'Unnamed Event'}</p>
                  <p className="text-xs text-gray-400">Layout: {l.name} | {l.event_date || 'No date set'} | {fmtNum(l.total_seats || l.seat_count || 0)} seats</p>
                </div>
                <span className={`px-2 py-0.5 text-[10px] rounded font-medium ${l.is_published ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-50 text-gray-500 border border-gray-200'}`}>{l.is_published ? 'live' : 'draft'}</span>
              </div>
            </Card>
          ))}
          {eventsLinked.length === 0 && <p className="text-center text-gray-400 py-10">No events linked to any layout yet</p>}
        </div>
      </div>
    );
  }

  // Main analytics
  return (
    <div className="space-y-6" data-testid="analytics-tab">
      {/* Header with date range + export */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics & Reporting</h1>
          <p className="text-sm text-gray-500 mt-1">Real-time insights into your seating operations</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)} className="text-xs border rounded-lg px-2.5 py-1.5 text-gray-600 bg-white" data-testid="date-range-filter">
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="custom">Custom Range</option>
          </select>
          {dateRange === 'custom' && (
            <div className="flex items-center gap-1">
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="text-xs border rounded px-2 py-1.5" />
              <span className="text-gray-400 text-xs">to</span>
              <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="text-xs border rounded px-2 py-1.5" />
            </div>
          )}
          <Button onClick={exportCSV} variant="outline" size="sm" className="gap-1.5 text-xs" data-testid="export-csv">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </Button>
          <Button onClick={fetchDashboard} variant="outline" size="sm" className="gap-1.5 text-xs">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* KPI Cards - all clickable */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-5 border-l-4 border-l-violet-500 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/dashboard?tab=layouts')} data-testid="stat-layouts">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Layouts</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{fmtNum(filtered.length)}</p>
          <div className="flex gap-3 mt-2 text-xs">
            <span className="text-green-600">{publishedCount} live</span>
            <span className="text-amber-600">{filtered.length - publishedCount} draft</span>
          </div>
        </Card>
        <Card className="p-5 border-l-4 border-l-blue-500 cursor-pointer hover:shadow-md transition-shadow" data-testid="stat-seats">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Seat Capacity</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{fmtNum(totalSeats)}</p>
          <p className="text-xs text-gray-400 mt-2">Across {filtered.length} layouts</p>
        </Card>
        <Card className="p-5 border-l-4 border-l-green-500 cursor-pointer hover:shadow-md transition-shadow" data-testid="stat-organisers">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Organisers</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{fmtNum(organisers.length)}</p>
          <p className="text-xs text-gray-400 mt-2">Using your platform</p>
        </Card>
        <Card className="p-5 border-l-4 border-l-amber-500 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setDetailView('events')} data-testid="stat-events">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Linked Events</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{fmtNum(eventsLinked.length)}</p>
          <p className="text-xs text-violet-500 mt-2 hover:underline">View events →</p>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Creation Trend */}
        <Card className="p-6" data-testid="creation-trend">
          <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Activity className="w-4 h-4 text-violet-500" /> Layout Creation Trend
          </h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendData}>
                <defs>
                  <linearGradient id="colorLayouts" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{fontSize: 10}} stroke="#9ca3af" />
                <YAxis tick={{fontSize: 10}} stroke="#9ca3af" allowDecimals={false} />
                <Tooltip contentStyle={{fontSize: 12, borderRadius: 8}} />
                <Area type="monotone" dataKey="layouts" stroke="#8b5cf6" fill="url(#colorLayouts)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Seats by Organiser */}
        <Card className="p-6" data-testid="seats-by-org">
          <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-500" /> Seats by Organiser
          </h3>
          {orgDonut.length > 0 ? (
            <div className="h-48 flex items-center">
              <ResponsiveContainer width="55%" height="100%">
                <PieChart>
                  <Pie data={orgDonut} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="value" stroke="none">
                    {orgDonut.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{fontSize: 12, borderRadius: 8}} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2 pl-2">
                {orgDonut.map((o, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{background: o.color}} />
                    <span className="text-gray-700 truncate flex-1">{o.name}</span>
                    <span className="font-semibold text-gray-900">{fmtNum(o.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No seat data yet</div>
          )}
        </Card>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Layout Performance - 3 cols */}
        <Card className="lg:col-span-3 p-6" data-testid="layout-performance">
          <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-violet-500" /> Layout Performance
          </h3>
          {filtered.length > 0 ? (
            <div className="space-y-2">
              {filtered.slice(0, 10).map((layout, idx) => {
                const seats = layout.total_seats || layout.seat_count || 0;
                const barWidth = seats > 0 ? Math.max(8, (seats / maxSeats) * 100) : 3;
                return (
                  <div key={layout.id || idx} className="flex items-center gap-3 group hover:bg-gray-50 rounded-lg p-1.5 -mx-1.5 transition-colors cursor-pointer" onClick={() => navigate(`/builder/${layout.id}`)}>
                    <div className="w-36 min-w-0 shrink-0">
                      <p className="text-sm font-medium text-gray-800 truncate group-hover:text-violet-700" title={layout.name}>{layout.name}</p>
                      <p className="text-[10px] text-gray-400 truncate">{layout.organiser_name || ''}{layout.event_name ? ` | ${layout.event_name}` : ''}</p>
                    </div>
                    <div className="flex-1 h-8 bg-gray-50 rounded-lg overflow-hidden relative border border-gray-100">
                      <div className="h-full rounded-lg transition-all duration-700 ease-out" style={{ width: `${barWidth}%`, background: layout.is_published ? 'linear-gradient(90deg, #8b5cf6, #6366f1)' : '#e5e7eb' }} />
                      <div className="absolute inset-0 flex items-center justify-end pr-2">
                        <span className="text-xs font-semibold text-gray-700">{seats.toLocaleString()}</span>
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 text-[10px] rounded font-medium shrink-0 ${layout.is_published ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-50 text-gray-500 border border-gray-200'}`}>{layout.is_published ? 'live' : 'draft'}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-10 text-gray-400"><BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-40" /><p className="text-sm">No layouts in this period</p></div>
          )}
        </Card>

        {/* Organisers - 2 cols */}
        <Card className="lg:col-span-2 p-6" data-testid="organiser-breakdown">
          <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-500" /> Organisers
          </h3>
          {organisers.length > 0 ? (
            <div className="space-y-3">
              {organisers.map((org, idx) => (
                <div key={org.organiser_id || idx} className="p-3 rounded-lg border border-gray-100 hover:border-violet-200 hover:bg-violet-50/30 transition-colors cursor-pointer" onClick={() => setSelectedOrg(org)} data-testid={`organiser-${idx}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center text-xs font-bold text-white shrink-0">
                      {(org.organiser_name || '?')[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{org.organiser_name}</p>
                      {org.organiser_email && <p className="text-[10px] text-gray-400 truncate">{org.organiser_email}</p>}
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-300" />
                  </div>
                  <div className="flex items-center gap-4 mt-2 ml-12 text-xs text-gray-500">
                    <span>{org.layouts_count} layouts</span>
                    <span>{fmtNum(org.total_seats || 0)} seats</span>
                    <span>{org.events_count || 0} events</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400"><Users className="w-8 h-8 mx-auto mb-2 opacity-40" /><p className="text-xs">No organisers yet</p></div>
          )}
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="p-6" data-testid="recent-activity">
        <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-400" /> Recent Activity
        </h3>
        <div className="grid md:grid-cols-2 gap-3">
          {filtered.slice(0, 6).map((layout, idx) => (
            <div key={layout.id || idx} className="flex items-start gap-3 p-3 rounded-xl bg-gradient-to-r from-gray-50 to-white border border-gray-100 hover:border-violet-200 transition-colors cursor-pointer group" onClick={() => navigate(`/builder/${layout.id}`)}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${layout.is_published ? 'bg-green-100' : 'bg-gray-100'}`}>
                <Layout className={`w-5 h-5 ${layout.is_published ? 'text-green-600' : 'text-gray-400'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 group-hover:text-violet-700 truncate">{layout.name}</p>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {layout.organiser_name && <span className="text-[10px] text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded">{layout.organiser_name}</span>}
                  {layout.event_name && <span className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">{layout.event_name}</span>}
                  {!layout.event_id && <span className="text-[10px] text-gray-400">No event</span>}
                  <span
                    data-testid={`layout-source-badge-${layout.id}`}
                    className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                      (layout.created_via || 'dashboard') === 'api'
                        ? 'text-emerald-700 bg-emerald-50 border border-emerald-200'
                        : 'text-slate-600 bg-slate-100 border border-slate-200'
                    }`}
                    title={(layout.created_via || 'dashboard') === 'api' ? 'Created via external API / SDK' : 'Created via SeatPilot dashboard'}
                  >
                    {(layout.created_via || 'dashboard') === 'api' ? 'API' : 'Dashboard'}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-400">
                  <span>{fmtDate(layout.created_at)}</span>
                  <span>{(layout.total_seats || layout.seat_count || 0).toLocaleString()} seats</span>
                  <span className={layout.is_published ? 'text-green-600' : 'text-gray-400'}>{layout.is_published ? 'Published' : 'Draft'}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        {filtered.length === 0 && <p className="text-center text-gray-400 py-6 text-sm">No activity in this period</p>}
      </Card>

      {/* API Access */}
      <Card className="p-5 bg-slate-50 border border-slate-200" data-testid="api-access-info">
        <div className="flex items-start gap-3">
          <Terminal className="w-5 h-5 text-violet-500 mt-0.5 shrink-0" />
          <div>
            <h4 className="text-sm font-semibold text-gray-900">Analytics API</h4>
            <p className="text-xs text-gray-500 mt-1">Pull analytics into your own website via API with <code className="bg-white px-1 rounded border">X-API-Key</code>.</p>
            <div className="flex flex-wrap gap-2 mt-2">
              {["/external/analytics/dashboard","/external/analytics/layouts","/external/analytics/revenue","/external/analytics/trends"].map(ep => (
                <code key={ep} className="px-2 py-0.5 bg-white border border-slate-200 rounded text-[10px] text-gray-600 font-mono">{ep}</code>
              ))}
            </div>
            <a href="/developer-docs?section=analytics" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-violet-600 hover:text-violet-700 mt-2 font-medium">View Analytics Docs <ExternalLink className="w-3 h-3" /></a>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default function UserDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, company, subscription, logout, fetchCompanyData, isSuperAdmin, hasAdminAccess, apiKeyWarnings } = useAuth();
  
  // Initialize activeTab from URL params if present
  const initialTab = searchParams.get('tab') || "overview";
  const [activeTab, setActiveTab] = useState(initialTab);
  // Local state for refer-and-earn banner dismissal so the X click forces a
  // re-render even if the dismiss key is already persisted to localStorage.
  const [referBannerDismissed, setReferBannerDismissed] = useState(() => {
    try { return localStorage.getItem("sp_refer_banner_dismissed") === "1"; } catch { return false; }
  });

  // Dashboard walkthrough (separate localStorage key from the builder one).
  // Auto-opens on first dashboard visit; replayable via "Help" button.
  const dashboardWalkthrough = useCanvasWalkthrough({
    autoOpenOnFirstVisit: true,
    storageKey: 'sp_dashboard_walkthrough_seen_v1',
  });
  const [setSearchParams] = [useSearchParams()[1]];
  
  // Handle tab change with URL update
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    // Close sidebar on mobile after selecting menu item
    if (window.innerWidth < 768) {
      setSidebarCollapsed(true);
    }
    // Update URL without full page reload
    const newUrl = tabId === 'overview' 
      ? '/dashboard' 
      : `/dashboard?tab=${tabId}`;
    window.history.pushState({}, '', newUrl);
  };
  const [layouts, setLayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthlyUsage, setMonthlyUsage] = useState({ seats_booked: 0, bookings_count: 0 });
  const [loginHistory, setLoginHistory] = useState([]);
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [companyForm, setCompanyForm] = useState({ 
    company_name: "", website: "", phone: "", address: "", city: "", postcode: "", country: "", vat_number: "", vat_registered: false,
    plan_id: null  // null = free trial, or selected plan id
  });
  const [creatingCompany, setCreatingCompany] = useState(false);
  const [trialEligibility, setTrialEligibility] = useState(null);
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  
  // Layout management states
  const [layoutViewMode, setLayoutViewMode] = useState(() => localStorage.getItem('layoutViewMode') || 'grid');
  const [layoutSortBy, setLayoutSortBy] = useState(() => localStorage.getItem('layoutSortBy') || 'created');
  const [layoutSortOrder, setLayoutSortOrder] = useState(() => localStorage.getItem('layoutSortOrder') || 'desc');
  const [layoutSearchQuery, setLayoutSearchQuery] = useState('');
  const [layoutFilter, setLayoutFilter] = useState(() => localStorage.getItem('layoutFilter') || 'all');
  const [renameDialog, setRenameDialog] = useState({ open: false, layout: null, name: '' });
  const [duplicating, setDuplicating] = useState(null);
  const [publishing, setPublishing] = useState(null); // Track which layout is being published
  const [unpublishDialog, setUnpublishDialog] = useState({ open: false, layout: null, checking: false, canUnpublish: true, reason: '' });
  
  // Persist layout preferences to localStorage
  useEffect(() => {
    localStorage.setItem('layoutViewMode', layoutViewMode);
  }, [layoutViewMode]);
  
  useEffect(() => {
    localStorage.setItem('layoutSortBy', layoutSortBy);
  }, [layoutSortBy]);
  
  useEffect(() => {
    localStorage.setItem('layoutSortOrder', layoutSortOrder);
  }, [layoutSortOrder]);
  
  useEffect(() => {
    localStorage.setItem('layoutFilter', layoutFilter);
  }, [layoutFilter]);
  
  // Pagination states
  const [layoutCurrentPage, setLayoutCurrentPage] = useState(1);
  const [layoutPageSize, setLayoutPageSize] = useState(12); // 12 for grid (4x3), adjustable
  
  // Start collapsed on mobile, expanded on desktop
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => window.innerWidth < 1024);
  const [showApiKey, setShowApiKey] = useState(false);
  
  // Handle responsive sidebar on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarCollapsed(true);
      }
    };
    window.addEventListener('resize', handleResize);
    // Initial check
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Company Settings
  const [editingCompany, setEditingCompany] = useState(false);
  const [companySettingsForm, setCompanySettingsForm] = useState({});
  const [savingCompany, setSavingCompany] = useState(false);
  
  // Payment Method States
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [showAddCard, setShowAddCard] = useState(false);
  const [pendingPlanAfterCard, setPendingPlanAfterCard] = useState(null);
  // Auto-renew T&C confirmation dialog: the user must explicitly accept
  // the recurring-charge terms before we flip auto-renew ON. This gives us
  // a timestamped + IP-stamped consent record for chargeback defence.
  const [showAutoRenewTerms, setShowAutoRenewTerms] = useState(false);
  const [autoRenewTermsAccepted, setAutoRenewTermsAccepted] = useState(false);
  const [autoRenewSaving, setAutoRenewSaving] = useState(false);
  // Auto-renew OFF re-confirmation dialog so the customer doesn't
  // accidentally cancel their Stripe recurring charge.
  const [showAutoRenewOffConfirm, setShowAutoRenewOffConfirm] = useState(false);
  
  // Invoice History States
  const [invoiceHistory, setInvoiceHistory] = useState([]);
  const [invoiceSummary, setInvoiceSummary] = useState({ total_paid: 0, total_pending: 0, invoice_count: 0 });
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [upcomingCharges, setUpcomingCharges] = useState(null);
  
  // Profile Edit States
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: "", phone: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Email Change States
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [emailChangeStep, setEmailChangeStep] = useState(1); // 1: request, 2: verify, 3: new email
  const [emailChangeData, setEmailChangeData] = useState({ otp: "", new_email: "" });
  const [emailChangeLoading, setEmailChangeLoading] = useState(false);
  
  // Email Verification States (for users who haven't verified email)
  const [showVerifyEmail, setShowVerifyEmail] = useState(false);
  const [verificationOTP, setVerificationOTP] = useState("");
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [resendingOTP, setResendingOTP] = useState(false);
  
  // Password Change States
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // Team Invite States
  const [showInviteTeam, setShowInviteTeam] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("user");
  const [inviting, setInviting] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);

  // Security & 2FA States
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [toggling2FA, setToggling2FA] = useState(false);
  const [activeSessions, setActiveSessions] = useState([]);
  const [trustedDevices, setTrustedDevices] = useState([]);
  const [showLogoutAllConfirm, setShowLogoutAllConfirm] = useState(false);
  const [loggingOutAll, setLoggingOutAll] = useState(false);
  const [clearingHistory, setClearingHistory] = useState(false);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  
  // 2FA Enable Verification States
  const [show2FAVerify, setShow2FAVerify] = useState(false);
  const [twoFAVerifyCode, setTwoFAVerifyCode] = useState("");
  const [verifying2FAEnable, setVerifying2FAEnable] = useState(false);
  
  // Customer Support Ticket States
  const [myTickets, setMyTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [newTicketForm, setNewTicketForm] = useState({ subject: "", description: "", category: "general", priority: "medium" });
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [selectedMyTicket, setSelectedMyTicket] = useState(null);
  const [myTicketReply, setMyTicketReply] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Company Edit States
  const [showEditCompany, setShowEditCompany] = useState(false);
  const [companyEditForm, setCompanyEditForm] = useState({});
  const [savingCompanyEdit, setSavingCompanyEdit] = useState(false);
  
  // Organiser Support View States
  const [organiserSearchTerm, setOrganiserSearchTerm] = useState("");
  const [expandedOrganisers, setExpandedOrganisers] = useState({});
  const [showLayoutDetails, setShowLayoutDetails] = useState(null);
  
  // Notification Preferences States
  const [notificationPrefs, setNotificationPrefs] = useState(null);
  const [notificationCategories, setNotificationCategories] = useState({});
  const [mandatoryNotifications, setMandatoryNotifications] = useState([]);
  const [savingNotificationPrefs, setSavingNotificationPrefs] = useState(false);

  // Plan Change States
  const [showChangePlan, setShowChangePlan] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [selectedNewPlan, setSelectedNewPlan] = useState(null);
  const [changingPlan, setChangingPlan] = useState(false);
  const [planChangePreview, setPlanChangePreview] = useState(null);
  const [showApiKeySelection, setShowApiKeySelection] = useState(false);
  const [apiKeySelectionData, setApiKeySelectionData] = useState(null);
  const [selectedKeysToKeep, setSelectedKeysToKeep] = useState([]);
  const [processingKeySelection, setProcessingKeySelection] = useState(false);
  
  // Cancel Plan States
  const [showCancelPlan, setShowCancelPlan] = useState(false);
  const [cancelImmediately, setCancelImmediately] = useState(false);
  const [cancellingPlan, setCancellingPlan] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelStep, setCancelStep] = useState(1);
  const [applyingRetention, setApplyingRetention] = useState(false);
  const [retentionConfig, setRetentionConfig] = useState(null);

  // White Label Cancel States
  const [showCancelWhiteLabel, setShowCancelWhiteLabel] = useState(false);
  const [cancelWhiteLabelImmediately, setCancelWhiteLabelImmediately] = useState(false);
  const [cancellingWhiteLabel, setCancellingWhiteLabel] = useState(false);

  // White Label Subscribe Confirmation States
  const [showWhiteLabelConfirm, setShowWhiteLabelConfirm] = useState(false);
  const [subscribingWhiteLabel, setSubscribingWhiteLabel] = useState(false);

  // White Label Reactivation Confirmation States
  const [showReactivateConfirm, setShowReactivateConfirm] = useState(false);
  const [reactivatingWhiteLabel, setReactivatingWhiteLabel] = useState(false);

  // User Role Management States
  const [showRoleManager, setShowRoleManager] = useState(false);
  const [customRoles, setCustomRoles] = useState([]);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: "", permissions: [] });
  const [savingRole, setSavingRole] = useState(false);

  // Handle tab changes from URL params
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
    // Auto-open specific ticket from URL
    const ticketParam = searchParams.get('ticket');
    if (ticketParam && tabParam === 'support') {
      setTimeout(() => fetchTicketDetails(ticketParam), 300);
    }
  }, [searchParams]);
// Auto-fetch tickets when Support tab is selected
  useEffect(() => {
    if (activeTab === "support") {
      fetchMyTickets();
      fetchNotificationCount();
    }
  }, [activeTab]);

  // Fetch notification preferences when Settings tab is active
  useEffect(() => {
    if (activeTab === "settings") {
      fetchNotificationPreferences();
    }
  }, [activeTab]);

  // Reset pagination when search/sort/filter changes or view mode changes
  useEffect(() => {
    setLayoutCurrentPage(1);
  }, [layoutSearchQuery, layoutSortBy, layoutSortOrder, layoutViewMode, layoutFilter]);

  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'success') {
      const sessionId = searchParams.get('session_id');
      if (sessionId) {
        handlePaymentSuccess(sessionId);
      }
    } else if (paymentStatus === 'cancelled') {
      toast.error("Payment was cancelled");
    }

    fetchData();
  }, []);
const handlePaymentSuccess = async (sessionId) => {
    try {
      const response = await axios.get(`${getAPI()}/subscriptions/checkout/status/${sessionId}`, {
        withCredentials: true
      });
      if (response.data.payment_status === 'paid') {
        toast.success("Payment successful! Your subscription is now active.");
        fetchCompanyData();
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
    }
    window.history.replaceState({}, '', '/dashboard');
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const companyRes = await axios.get(`${getAPI()}/subscriptions/company`, { withCredentials: true });
      setMonthlyUsage(companyRes.data.monthly_usage || { seats_booked: 0, bookings_count: 0 });

      if (companyRes.data.company) {
        try {
          const layoutsRes = await axios.get(`${getAPI()}/admin/layouts`, { withCredentials: true });
          setLayouts(layoutsRes.data || []);
        } catch (e) {
          setLayouts([]);
        }
        
        // Fetch team members
        try {
          const teamRes = await axios.get(`${getAPI()}/team/members`, { withCredentials: true });
          setTeamMembers(teamRes.data.members || []);
        } catch (e) {
          setTeamMembers([]);
        }
        
        // Fetch custom roles
        try {
          const rolesRes = await axios.get(`${getAPI()}/team/roles`, { withCredentials: true });
          setCustomRoles(rolesRes.data.roles || []);
        } catch (e) {
          setCustomRoles([]);
        }
        
        // Fetch payment method
        try {
          const pmRes = await axios.get(`${getAPI()}/subscriptions/payment-method`, { withCredentials: true });
          setPaymentMethod(pmRes.data.has_payment_method ? pmRes.data.payment_method : null);
        } catch (e) {
          setPaymentMethod(null);
        }
      }

      const historyRes = await axios.get(`${getAPI()}/auth/login-history`, { withCredentials: true });
      setLoginHistory(historyRes.data.login_history || []);
      
      // Set profile form
      if (user) {
        setProfileForm({ name: user.name || "", phone: user.phone || "" });
      }
      
      // Check if we need to show add card dialog from URL params
      if (searchParams.get('add_card') === 'true') {
        setShowAddCard(true);
        setPendingPlanAfterCard(searchParams.get('plan') || null);
        handleTabChange('billing');
      }
      
      // Fetch 2FA settings
      try {
        const twoFARes = await axios.get(`${getAPI()}/auth/2fa/settings`, { withCredentials: true });
        setTwoFactorEnabled(twoFARes.data.two_factor_enabled || false);
      } catch (e) {
        console.error("Failed to fetch 2FA settings");
      }
      
      // Fetch active sessions
      try {
        const sessionsRes = await axios.get(`${getAPI()}/auth/sessions`, { withCredentials: true });
        setActiveSessions(sessionsRes.data.sessions || []);
      } catch (e) {
        console.error("Failed to fetch sessions");
      }
      
      // Fetch trusted devices
      try {
        const devicesRes = await axios.get(`${getAPI()}/auth/trusted-devices`, { withCredentials: true });
        setTrustedDevices(devicesRes.data.devices || []);
      } catch (e) {
        console.error("Failed to fetch trusted devices");
      }
      
      // Fetch notification count
      try {
        const notifRes = await axios.get(`${getAPI()}/support/notifications/count`, { withCredentials: true });
        setUnreadCount(notifRes.data.unread_count || 0);
      } catch (e) {
        console.error("Failed to fetch notification count");
      }
      
      // Fetch recent activity
      try {
        const activityRes = await axios.get(`${getAPI()}/auth/recent-activity`, { withCredentials: true });
        setRecentActivity(activityRes.data.activities || []);
      } catch (e) {
        console.error("Failed to fetch recent activity");
      }
      
      // Fetch invoice history and upcoming charges
      try {
        const [invoicesRes, chargesRes] = await Promise.all([
          axios.get(`${getAPI()}/invoices/user/history`, { withCredentials: true }),
          axios.get(`${getAPI()}/invoices/user/upcoming-charges`, { withCredentials: true })
        ]);
        setInvoiceHistory(invoicesRes.data.invoices || []);
        setInvoiceSummary(invoicesRes.data.summary || { total_paid: 0, total_pending: 0, invoice_count: 0 });
        setUpcomingCharges(chargesRes.data);
      } catch (e) {
        console.error("Failed to fetch invoice data");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle 2FA toggle - unified flow for enable/disable
  const handleToggle2FA = async () => {
    setToggling2FA(true);
    try {
      // Request change (will send verification code)
      const res = await axios.post(`${getAPI()}/auth/2fa/request-change`, {}, { withCredentials: true });
      toast.success(res.data.message || "Verification code sent to your email");
      setShow2FAVerify(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send verification code");
    } finally {
      setToggling2FA(false);
    }
  };

  // Confirm 2FA change with verification code
  const handleConfirm2FAChange = async (e) => {
    e.preventDefault();
    if (twoFAVerifyCode.length !== 6) {
      toast.error("Please enter a 6-digit code");
      return;
    }
    
    setVerifying2FAEnable(true);
    try {
      const res = await axios.post(`${getAPI()}/auth/2fa/confirm-change`, null, {
        params: { code: twoFAVerifyCode },
        withCredentials: true
      });
      setTwoFactorEnabled(res.data.two_factor_enabled);
      setShow2FAVerify(false);
      setTwoFAVerifyCode("");
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid verification code");
    } finally {
      setVerifying2FAEnable(false);
    }
  };

  // Resend 2FA change verification code
  const handleResend2FACode = async () => {
    try {
      await axios.post(`${getAPI()}/auth/2fa/resend-code`, {}, { withCredentials: true });
      toast.success("New verification code sent to your email");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend code");
    }
  };

  // Cancel 2FA change
  const handleCancel2FAChange = async () => {
    try {
      await axios.post(`${getAPI()}/auth/2fa/cancel-change`, {}, { withCredentials: true });
      setShow2FAVerify(false);
      setTwoFAVerifyCode("");
    } catch (error) {
      console.error("Cancel error:", error);
    }
  };

  // Handle layout publish/unpublish with confirmation
  const handlePublishToggle = async (layout) => {
    if (layout.is_published) {
      // Unpublishing - check if it's safe first
      setUnpublishDialog({ open: true, layout, checking: true, canUnpublish: true, reason: '' });
      
      try {
        // Check if layout has active events with bookings
        const checkRes = await axios.get(`${getAPI()}/admin/layouts/${layout.id}/unpublish-check`, { withCredentials: true });
        
        if (!checkRes.data.can_unpublish) {
          setUnpublishDialog({
            open: true,
            layout,
            checking: false,
            canUnpublish: false,
            reason: checkRes.data.reason,
            active_events: checkRes.data.active_events || [],
            total_bookings: checkRes.data.total_bookings || 0
          });
        } else {
          setUnpublishDialog({
            open: true,
            layout,
            checking: false,
            canUnpublish: true,
            reason: 'This layout will no longer be available for booking. Customers won\'t be able to select seats until you publish it again.'
          });
        }
      } catch (error) {
        // If check endpoint doesn't exist, show generic warning
        setUnpublishDialog({
          open: true,
          layout,
          checking: false,
          canUnpublish: true,
          reason: 'This layout will no longer be available for booking. Are you sure you want to unpublish?'
        });
      }
    } else {
      // Publishing - do it directly
      await executePublishToggle(layout);
    }
  };

  const executePublishToggle = async (layout) => {
    setPublishing(layout.id);
    try {
      await axios.patch(`${getAPI()}/admin/layouts/${layout.id}`, { is_published: !layout.is_published }, { withCredentials: true });
      toast.success(layout.is_published ? "Layout unpublished" : "Layout published!");
      setLayouts(layouts.map(l => l.id === layout.id ? { ...l, is_published: !l.is_published } : l));
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to update layout status";
      // If the publish was rejected because the plan expired/cancelled, show a
      // dedicated "Renew" toast that deep-links the user to billing instead of
      // the generic error so they have a one-click recovery path.
      const looksExpired = /subscription|expired|cancelled|renew|plan/i.test(String(detail));
      if (looksExpired) {
        toast.error(detail, {
          action: {
            label: "Renew plan",
            onClick: () => navigate("/dashboard?tab=billing"),
          },
          duration: 8000,
        });
      } else {
        toast.error(detail);
      }
    } finally {
      setPublishing(null);
      setUnpublishDialog({ open: false, layout: null, checking: false, canUnpublish: true, reason: '' });
    }
  };

  // Handle logout all devices
  const handleLogoutAllDevices = async (includesCurrent = false) => {
    setLoggingOutAll(true);
    try {
      const endpoint = includesCurrent ? `${getAPI()}/auth/logout-all-including-current` : `${getAPI()}/auth/logout-all`;
      await axios.post(endpoint, {}, { withCredentials: true });
      toast.success(includesCurrent ? "Logged out from all devices" : "Logged out from all other devices");
      setShowLogoutAllConfirm(false);
      
      if (includesCurrent) {
        logout();
      } else {
        // Refresh sessions list
        const sessionsRes = await axios.get(`${getAPI()}/auth/sessions`, { withCredentials: true });
        setActiveSessions(sessionsRes.data.sessions || []);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to logout from devices");
    } finally {
      setLoggingOutAll(false);
    }
  };

  // Handle logout specific session
  const handleLogoutSession = async (sessionId) => {
    try {
      await axios.delete(`${getAPI()}/auth/sessions/${sessionId}`, { withCredentials: true });
      toast.success("Session terminated");
      setActiveSessions(activeSessions.filter(s => s.session_id !== sessionId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to terminate session");
    }
  };

  // Handle clear login history
  const handleClearLoginHistory = async () => {
    setClearingHistory(true);
    try {
      await axios.delete(`${getAPI()}/auth/login-history`, { withCredentials: true });
      setLoginHistory([]);
      toast.success("Login history cleared");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clear history");
    } finally {
      setClearingHistory(false);
    }
  };

  // Handle remove trusted device
  const handleRemoveTrustedDevice = async (fingerprint) => {
    try {
      await axios.delete(`${getAPI()}/auth/trusted-devices/${fingerprint}`, { withCredentials: true });
      setTrustedDevices(trustedDevices.filter(d => d.fingerprint !== fingerprint));
      toast.success("Device removed from trusted list");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove device");
    }
  };

  // Handle company profile update
  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    setSavingCompanyEdit(true);
    try {
      const res = await axios.put(`${getAPI()}/auth/company/profile`, companyEditForm, { withCredentials: true });
      toast.success("Company information updated");
      setEditingCompany(false);
      fetchCompanyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update company");
    } finally {
      setSavingCompanyEdit(false);
    }
  };

  // Handle successful card addition
  const handleCardAdded = async (pm) => {
    setPaymentMethod(pm);
    setShowAddCard(false);
    
    // If there's a pending plan, activate it
    if (pendingPlanAfterCard === 'pay_as_you_go') {
      try {
        const response = await axios.post(
          `${getAPI()}/subscriptions/subscribe`,
          { plan_id: 'pay_as_you_go', origin_url: window.location.origin },
          { withCredentials: true }
        );
        if (response.data.success) {
          toast.success("Pay As You Go plan activated!");
          fetchCompanyData();
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to activate plan");
      }
      setPendingPlanAfterCard(null);
    }
  };

  // Check trial eligibility when opening create company dialog
  const openCreateCompanyDialog = async () => {
    setShowCreateCompany(true);
    setCheckingEligibility(true);
    try {
      const res = await axios.get(`${getAPI()}/subscriptions/check-trial-eligibility`, { withCredentials: true });
      setTrialEligibility(res.data);
      // If not eligible for free trial, they need to select a plan
      if (!res.data.eligible_for_free_trial && res.data.available_plans?.length > 0) {
        // Default to PAYG plan
        setCompanyForm(prev => ({ ...prev, plan_id: "payg" }));
      }
    } catch (error) {
      // Assume eligible if check fails
      setTrialEligibility({ eligible_for_free_trial: true });
    } finally {
      setCheckingEligibility(false);
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    if (!companyForm.company_name.trim()) {
      toast.error("Company name is required");
      return;
    }
    
    if (!companyForm.phone || !companyForm.phone.trim()) {
      toast.error("Contact number is required");
      return;
    }
    
    // If not eligible for trial and no plan selected, require plan selection
    if (trialEligibility && !trialEligibility.eligible_for_free_trial && !companyForm.plan_id) {
      toast.error("Please select a subscription plan");
      return;
    }
    
    setCreatingCompany(true);
    try {
      const response = await axios.post(`${getAPI()}/subscriptions/company`, companyForm, { withCredentials: true });
      
      // Check if payment is required
      if (response.data.subscription?.requires_payment) {
        toast.success("Company created! Please add a payment method to activate your subscription.");
        setShowCreateCompany(false);
        await fetchCompanyData();
        await fetchData();
        // Navigate to billing to add payment method
        setActiveTab('billing');
      } else {
        toast.success("Company created! You're on a 30-day free trial.");
        setShowCreateCompany(false);
        await fetchCompanyData();
        await fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create company");
    } finally {
      setCreatingCompany(false);
    }
  };

  const copyApiKey = () => {
    if (company?.api_key) {
      navigator.clipboard.writeText(company.api_key);
      toast.success("API key copied to clipboard");
    }
  };

  // Email Verification Handlers (for users who haven't verified email)
  const handleResendVerificationEmail = async () => {
    setResendingOTP(true);
    try {
      const response = await axios.post(`${getAPI()}/auth/resend-otp`, { email: user.email }, { withCredentials: true });
      
      // If auto-verified (internal user), refresh and show success
      if (response.data.auto_verified) {
        toast.success("Email verified successfully!");
        await fetchCompanyData();
      } else {
        toast.success("Verification email sent! Please check your inbox.");
        setShowVerifyEmail(true);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "";
      // If email is already verified, refresh user data and show success
      if (errorMsg.toLowerCase().includes("already verified")) {
        toast.success("Your email is already verified! Refreshing...");
        await fetchCompanyData();
      } else {
        toast.error(errorMsg || "Failed to send verification email");
      }
    } finally {
      setResendingOTP(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!verificationOTP || verificationOTP.length !== 6) {
      toast.error("Please enter a valid 6-digit code");
      return;
    }
    setVerifyingEmail(true);
    try {
      await axios.post(`${getAPI()}/auth/verify-email`, { email: user.email, otp: verificationOTP }, { withCredentials: true });
      toast.success("Email verified successfully!");
      setShowVerifyEmail(false);
      setVerificationOTP("");
      // Refresh user data to update email_verified status
      await fetchCompanyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Verification failed. Please check the code.");
    } finally {
      setVerifyingEmail(false);
    }
  };

  // Profile Update Handler
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await axios.put(`${getAPI()}/auth/profile`, profileForm, { withCredentials: true });
      toast.success("Profile updated successfully");
      setEditingProfile(false);
      fetchCompanyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };
  
  // Email Change Handlers
  const handleRequestEmailChange = async () => {
    setEmailChangeLoading(true);
    try {
      await axios.post(`${getAPI()}/auth/request-email-change`, {}, { withCredentials: true });
      toast.success("Verification code sent to your current email");
      setEmailChangeStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send verification");
    } finally {
      setEmailChangeLoading(false);
    }
  };
  
  const handleVerifyEmailChange = async () => {
    if (!emailChangeData.otp || emailChangeData.otp.length !== 6) {
      toast.error("Please enter a valid 6-digit code");
      return;
    }
    setEmailChangeLoading(true);
    try {
      await axios.post(`${getAPI()}/auth/verify-email-change-otp`, { otp: emailChangeData.otp }, { withCredentials: true });
      toast.success("Code verified! Now enter your new email");
      setEmailChangeStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid verification code");
    } finally {
      setEmailChangeLoading(false);
    }
  };
  
  const handleCompleteEmailChange = async () => {
    if (!emailChangeData.new_email) {
      toast.error("Please enter your new email");
      return;
    }
    setEmailChangeLoading(true);
    try {
      await axios.post(`${getAPI()}/auth/complete-email-change`, { 
        otp: emailChangeData.otp, 
        new_email: emailChangeData.new_email 
      }, { withCredentials: true });
      toast.success("Email changed successfully!");
      setShowEmailChange(false);
      setEmailChangeStep(1);
      setEmailChangeData({ otp: "", new_email: "" });
      fetchCompanyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change email");
    } finally {
      setEmailChangeLoading(false);
    }
  };
  
  // Password Change Handler
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }
    if (passwordForm.new_password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setPasswordLoading(true);
    try {
      await axios.post(`${getAPI()}/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, { withCredentials: true });
      toast.success("Password changed successfully!");
      setShowPasswordChange(false);
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setPasswordLoading(false);
    }
  };
  
  // Team Member Revoke Handler
  const handleRevokeMember = async (memberId, isInvite = false) => {
    const confirmMsg = isInvite 
      ? "Are you sure you want to revoke this invitation?"
      : "Are you sure you want to remove this team member?";
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await axios.delete(`${getAPI()}/team/member/${memberId}`, { withCredentials: true });
      toast.success(isInvite ? "Invitation revoked" : "Team member removed");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove member");
    }
  };

  // Team Invite Handler
  const handleInviteTeam = async (e) => {
    e.preventDefault();
    if (!inviteEmail) {
      toast.error("Please enter an email");
      return;
    }
    setInviting(true);
    try {
      await axios.post(`${getAPI()}/team/invite`, { email: inviteEmail, role: inviteRole }, { withCredentials: true });
      toast.success("Invitation sent!");
      setShowInviteTeam(false);
      setInviteEmail("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    } finally {
      setInviting(false);
    }
  };

  // Fetch My Support Tickets
  const fetchMyTickets = async () => {
    setLoadingTickets(true);
    try {
      const res = await axios.get(`${getAPI()}/support/tickets`, { withCredentials: true });
      setMyTickets(res.data.tickets || []);
    } catch (error) {
      console.error("Failed to fetch tickets:", error);
    } finally {
      setLoadingTickets(false);
    }
  };

  // Fetch single ticket with fresh data
  const fetchTicketDetails = async (ticketId) => {
    try {
      const res = await axios.get(`${getAPI()}/support/tickets/${ticketId}`, { withCredentials: true });
      setSelectedMyTicket(res.data.ticket);
      // Also update in list
      setMyTickets(prev => prev.map(t => t.ticket_id === ticketId ? res.data.ticket : t));
    } catch (error) {
      console.error("Failed to fetch ticket details:", error);
      toast.error("Failed to load ticket details");
    }
  };

  // Fetch notification count
  const fetchNotificationCount = async () => {
    try {
      const res = await axios.get(`${getAPI()}/support/notifications/count`, { withCredentials: true });
      setUnreadCount(res.data.unread_count || 0);
    } catch (error) {
      console.error("Failed to fetch notification count:", error);
    }
  };

  // Fetch notification preferences
  const fetchNotificationPreferences = async () => {
    try {
      const res = await axios.get(`${getAPI()}/notifications/preferences`, { withCredentials: true });
      setNotificationPrefs(res.data.preferences || {});
      setNotificationCategories(res.data.categories || {});
      setMandatoryNotifications(res.data.mandatory_email || []);
    } catch (error) {
      console.error("Failed to fetch notification preferences:", error);
    }
  };

  // Save notification preferences
  const saveNotificationPreferences = async (updates) => {
    setSavingNotificationPrefs(true);
    try {
      const res = await axios.put(`${getAPI()}/notifications/preferences`, updates, { withCredentials: true });
      setNotificationPrefs(res.data.preferences);
      toast.success("Notification preferences saved");
    } catch (error) {
      toast.error("Failed to save preferences");
    } finally {
      setSavingNotificationPrefs(false);
    }
  };

  // Toggle notification preference
  const toggleNotificationPref = async (channel, category) => {
    const currentValue = notificationPrefs?.[channel]?.[category] ?? true;
    const newPrefs = {
      ...notificationPrefs,
      [channel]: {
        ...notificationPrefs?.[channel],
        [category]: !currentValue
      }
    };
    setNotificationPrefs(newPrefs);
    await saveNotificationPreferences({ [channel]: { [category]: !currentValue } });
  };

  // Toggle sound
  const toggleNotificationSound = async () => {
    const newMuted = !notificationPrefs?.sound_muted;
    setNotificationPrefs(prev => ({ ...prev, sound_muted: newMuted }));
    await saveNotificationPreferences({ sound_muted: newMuted });
  };

  // Create Support Ticket
  const createSupportTicket = async (e) => {
    e.preventDefault();
    if (!newTicketForm.subject || !newTicketForm.description) {
      toast.error("Please fill in all fields");
      return;
    }
    setCreatingTicket(true);
    try {
      const res = await axios.post(`${getAPI()}/support/tickets`, newTicketForm, { withCredentials: true });
      toast.success("Ticket created! Our team will respond soon.");
      setShowCreateTicket(false);
      setNewTicketForm({ subject: "", description: "", category: "general", priority: "medium" });
      fetchMyTickets();
      setSelectedMyTicket(res.data.ticket);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create ticket");
    } finally {
      setCreatingTicket(false);
    }
  };

  // Reply to My Ticket
  const sendMyTicketReply = async () => {
    if (!selectedMyTicket || !myTicketReply.trim()) return;
    setSendingReply(true);
    try {
      await axios.post(`${getAPI()}/support/tickets/${selectedMyTicket.ticket_id}/reply`, {
        message: myTicketReply,
        is_internal: false
      }, { withCredentials: true });
      toast.success("Reply sent!");
      setMyTicketReply("");
      // Refresh ticket
      const res = await axios.get(`${getAPI()}/support/tickets/${selectedMyTicket.ticket_id}`, { withCredentials: true });
      setSelectedMyTicket(res.data.ticket);
      fetchMyTickets();
      fetchNotificationCount();
    } catch (error) {
      toast.error("Failed to send reply");
    } finally {
      setSendingReply(false);
    }
  };

  // Plan upgrade/downgrade handler
  const handleChangePlan = (planId) => {
    navigate(`/pricing?upgrade=${planId}`);
  };

  // Fetch available plans for plan change
  const fetchAvailablePlans = async () => {
    try {
      const res = await axios.get(`${getAPI()}/subscriptions/plans/public`, { withCredentials: true });
      // Get monthly plans from the response
      const monthlyPlans = res.data.monthly || [];
      const yearlyPlans = res.data.yearly || [];
      
      // Combine and format plans for display
      const formattedPlans = [];
      
      // Add monthly plans (except free trial which is separate)
      monthlyPlans.forEach(plan => {
        if (plan.id === 'free_trial') return; // Skip free trial in plan change
        formattedPlans.push({
          id: plan.id,
          name: plan.name,
          price: plan.price + (plan.period ? ` ${plan.period}` : ''),
          description: plan.description || plan.features?.slice(0, 2).join('. ') || '',
          needsCard: plan.id !== 'enterprise',
          isEnterprise: plan.isEnterprise || plan.id === 'enterprise'
        });
      });
      
      // Add yearly plan option for monthly
      const yearlyMonthly = yearlyPlans.find(p => p.id === 'monthly_yearly' || p.id?.includes('yearly'));
      if (yearlyMonthly && !formattedPlans.find(p => p.id === yearlyMonthly.id)) {
        formattedPlans.splice(2, 0, {
          id: yearlyMonthly.id || 'monthly_yearly',
          name: yearlyMonthly.name || 'Yearly Plan',
          price: yearlyMonthly.price + (yearlyMonthly.period ? ` ${yearlyMonthly.period}` : ''),
          description: yearlyMonthly.discount ? `Save ${yearlyMonthly.discount}! ` : '' + (yearlyMonthly.description || ''),
          needsCard: true,
          isEnterprise: false
        });
      }
      
      setAvailablePlans(formattedPlans);
    } catch (error) {
      console.error("Failed to fetch plans:", error);
      // Fallback to default plans if API fails
      setAvailablePlans([
        { id: 'pay_as_you_go', name: 'Pay As You Go', price: '£0.50/seat', description: 'No monthly fee. Pay per booking.', needsCard: true },
        { id: 'monthly', name: 'Monthly Plan', price: '£50/month', description: '10,000 seats included, then £0.30/seat.', needsCard: true },
        { id: 'monthly_yearly', name: 'Yearly Plan', price: '£480/year', description: 'Save 20%! 10,000 seats/month.', needsCard: true },
        { id: 'enterprise', name: 'Enterprise', price: 'Custom', description: 'Unlimited everything. Contact us.', needsCard: false, isEnterprise: true },
      ]);
    }
  };

  // State for plan change timing
  const [changeImmediately, setChangeImmediately] = useState(true);
  const [showEnterpriseTicket, setShowEnterpriseTicket] = useState(false);
  const [enterpriseRequirements, setEnterpriseRequirements] = useState("");

  // Preview plan change (calculate prorated amount)
  const previewPlanChange = async (newPlanId) => {
    // If Enterprise, show ticket form instead
    if (newPlanId === 'enterprise') {
      setShowEnterpriseTicket(true);
      setSelectedNewPlan(newPlanId);
      return;
    }
    
    try {
      const res = await axios.post(`${getAPI()}/subscriptions/preview-change`, {
        new_plan_id: newPlanId
      }, { withCredentials: true });
      setPlanChangePreview(res.data);
      setSelectedNewPlan(newPlanId);
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMsg = typeof errorDetail === 'string' 
        ? errorDetail 
        : (Array.isArray(errorDetail) ? errorDetail.map(e => e.msg || e).join(', ') : "Failed to preview plan change");
      toast.error(errorMsg);
    }
  };

  // Submit Enterprise enquiry as support ticket
  const submitEnterpriseEnquiry = async () => {
    if (!enterpriseRequirements.trim()) {
      toast.error("Please describe your requirements");
      return;
    }
    
    setChangingPlan(true);
    try {
      await axios.post(`${getAPI()}/support/tickets`, {
        subject: "Enterprise Plan Enquiry",
        category: "billing",
        priority: "high",
        description: `Enterprise Plan Request\n\n--- Requirements ---\n${enterpriseRequirements}\n\n--- Current Plan ---\n${subscription?.plan_id || 'None'}\n\n--- Company ---\n${company?.name || 'N/A'}`
      }, { withCredentials: true });
      
      toast.success("Your Enterprise plan enquiry has been submitted! Our team will contact you within 24 hours.");
      setShowChangePlan(false);
      setShowEnterpriseTicket(false);
      setEnterpriseRequirements("");
      setSelectedNewPlan(null);
    } catch (error) {
      const errorMsg = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : "Failed to submit enquiry. Please try again.";
      toast.error(errorMsg);
    } finally {
      setChangingPlan(false);
    }
  };

  // Confirm plan change
  const confirmPlanChange = async () => {
    if (!selectedNewPlan) return;
    
    // Check if API key selection is needed first
    if (planChangePreview?.api_key_impact?.action_required && !showApiKeySelection) {
      setApiKeySelectionData(planChangePreview.api_key_impact);
      setSelectedKeysToKeep([]);
      setShowApiKeySelection(true);
      return;
    }
    
    setChangingPlan(true);
    try {
      // For monthly/yearly plans, use direct subscription (like white label - instant activation!)
      if (selectedNewPlan === 'monthly' || selectedNewPlan === 'monthly_yearly') {
        const res = await axios.post(`${getAPI()}/subscriptions/subscribe-direct`, {
          plan_id: selectedNewPlan
        }, { withCredentials: true });
        
        if (res.data.success) {
          toast.success(res.data.message || "Subscription activated!");
          setShowChangePlan(false);
          setSelectedNewPlan(null);
          setPlanChangePreview(null);
          fetchCompanyData();  // Refresh to show new plan
          return;
        } else {
          toast.error(res.data.message || "Failed to subscribe");
        }
      } 
      // For PAYG, use direct payment flow (like monthly/yearly - instant activation!)
      else if (selectedNewPlan === 'pay_as_you_go') {
        const res = await axios.post(`${getAPI()}/subscriptions/payg-direct`, {}, { withCredentials: true });
        
        if (res.data.success) {
          toast.success(res.data.message || "Switched to Pay As You Go!");
          setShowChangePlan(false);
          setSelectedNewPlan(null);
          setPlanChangePreview(null);
          fetchCompanyData();  // Refresh to show new plan
          return;
        } else {
          toast.error(res.data.message || "Failed to switch to PAYG");
        }
      }
      // Fallback to old flow for other plans or free transitions
      else {
        const res = await axios.post(`${getAPI()}/subscriptions/change-plan`, {
          new_plan_id: selectedNewPlan,
          immediate: changeImmediately
        }, { withCredentials: true });
        
        if (res.data.requires_payment && res.data.checkout_url) {
          // Redirect to Stripe for payment
          window.location.href = res.data.checkout_url;
        } else {
          toast.success(res.data.message || "Plan changed successfully!");
          setShowChangePlan(false);
          setSelectedNewPlan(null);
          setPlanChangePreview(null);
          setChangeImmediately(true);
          fetchCompanyData();
        }
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMsg = typeof errorDetail === 'string' 
        ? errorDetail 
        : (Array.isArray(errorDetail) ? errorDetail.map(e => e.msg || e).join(', ') : "Failed to change plan");
      toast.error(errorMsg);
    } finally {
      setChangingPlan(false);
    }
  };

  // Handle API key selection and then proceed with plan change
  const confirmKeySelectionAndChangePlan = async () => {
    if (!apiKeySelectionData) return;
    
    const newLimit = apiKeySelectionData.new_plan_limit;
    if (selectedKeysToKeep.length !== newLimit) {
      toast.error(`Please select exactly ${newLimit} keys to keep`);
      return;
    }
    
    setProcessingKeySelection(true);
    try {
      // First, revoke unselected keys
      await axios.post(`${getAPI()}/subscriptions/api-keys/select-for-downgrade`, {
        keys_to_keep: selectedKeysToKeep,
        new_plan_id: selectedNewPlan
      }, { withCredentials: true });
      
      // Now proceed with the actual plan change
      setShowApiKeySelection(false);
      setApiKeySelectionData(null);
      // Call confirmPlanChange again - this time without the api_key check
      const preview = { ...planChangePreview };
      preview.api_key_impact = null; // Clear so it doesn't loop
      setPlanChangePreview(preview);
      await confirmPlanChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process key selection");
    } finally {
      setProcessingKeySelection(false);
    }
  };

  const toggleKeySelection = (keyId) => {
    const limit = apiKeySelectionData?.new_plan_limit || 1;
    setSelectedKeysToKeep(prev => {
      if (prev.includes(keyId)) {
        return prev.filter(k => k !== keyId);
      }
      if (prev.length >= limit) {
        toast.error(`You can only keep ${limit} keys`);
        return prev;
      }
      return [...prev, keyId];
    });
  };

  // Cancel plan
  const cancelPlan = async () => {
    setCancellingPlan(true);
    try {
      const res = await axios.post(`${getAPI()}/subscriptions/cancel`, {
        immediate: cancelImmediately,
        reason: cancelReason
      }, { withCredentials: true });
      
      toast.success(res.data.message || "Subscription cancelled");
      setShowCancelPlan(false);
      setCancelReason("");
      setCancelImmediately(false);
      fetchCompanyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel subscription");
    } finally {
      setCancellingPlan(false);
    }
  };

  // Fetch custom roles
  const fetchCustomRoles = async () => {
    try {
      const res = await axios.get(`${getAPI()}/team/roles`, { withCredentials: true });
      setCustomRoles(res.data.roles || []);
    } catch (error) {
      console.error("Failed to fetch roles:", error);
    }
  };

  // Fetch invoice history
  const fetchInvoiceHistory = async () => {
    setLoadingInvoices(true);
    try {
      const res = await axios.get(`${getAPI()}/invoices/user/history`, { withCredentials: true });
      setInvoiceHistory(res.data.invoices || []);
      setInvoiceSummary(res.data.summary || { total_paid: 0, total_pending: 0, invoice_count: 0 });
    } catch (error) {
      console.error("Failed to fetch invoices:", error);
    } finally {
      setLoadingInvoices(false);
    }
  };

  // Fetch upcoming charges
  const fetchUpcomingCharges = async () => {
    try {
      const res = await axios.get(`${getAPI()}/invoices/user/upcoming-charges`, { withCredentials: true });
      setUpcomingCharges(res.data);
    } catch (error) {
      console.error("Failed to fetch upcoming charges:", error);
    }
  };

  // Save custom role
  const saveCustomRole = async () => {
    if (!roleForm.name) {
      toast.error("Role name is required");
      return;
    }
    setSavingRole(true);
    try {
      if (editingRole) {
        await axios.put(`${getAPI()}/team/roles/${editingRole.role_id}`, roleForm, { withCredentials: true });
        toast.success("Role updated");
      } else {
        await axios.post(`${getAPI()}/team/roles`, roleForm, { withCredentials: true });
        toast.success("Role created");
      }
      setShowRoleManager(false);
      setEditingRole(null);
      setRoleForm({ name: "", permissions: [] });
      fetchCustomRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save role");
    } finally {
      setSavingRole(false);
    }
  };

  // Delete custom role
  const deleteCustomRole = async (roleId) => {
    if (!window.confirm("Are you sure you want to delete this role?")) return;
    try {
      await axios.delete(`${getAPI()}/team/roles/${roleId}`, { withCredentials: true });
      toast.success("Role deleted");
      fetchCustomRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete role");
    }
  };

  const getSubscriptionBadge = () => {
    if (!subscription) return null;
    const status = subscription.status;
    const planName = subscription.plan_name || subscription.plan_id;
    const isCustom = subscription.is_custom;
    
    const colors = {
      trial: "bg-blue-100 text-blue-800",
      active: "bg-green-100 text-green-800",
      cancelling: "bg-amber-100 text-amber-800",
      expired: "bg-red-100 text-red-800",
      cancelled: "bg-gray-100 text-gray-800"
    };
    
    const statusLabels = {
      trial: "Trial",
      active: "Active",
      cancelling: "Cancelling",
      expired: "Expired",
      cancelled: "Cancelled"
    };
    
    return (
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${colors[status] || colors.active}`}>
          {planName} - {statusLabels[status] || status}
        </span>
        {isCustom && (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            Custom
          </span>
        )}
        {status === 'cancelling' && subscription.scheduled_cancel_at && (
          <span className="text-xs text-amber-600">
            Ends: {formatDate(subscription.scheduled_cancel_at)}
          </span>
        )}
      </div>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
  };

  // Inline Referral Dashboard Component
  const ReferralDashboardInline = () => {
    const [referralData, setReferralData] = useState(null);
    const [referralsList, setReferralsList] = useState([]);
    const [creditData, setCreditData] = useState(null);
    const [referralLoading, setReferralLoading] = useState(true);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
      const fetchReferralData = async () => {
        try {
          setReferralLoading(true);
          const apiUrl = getAPI();
          const [codeRes, referralsRes, creditRes] = await Promise.all([
            axios.get(`${apiUrl}/referrals/my-code`, { withCredentials: true }),
            axios.get(`${apiUrl}/referrals/my-referrals`, { withCredentials: true }),
            axios.get(`${apiUrl}/referrals/my-credit`, { withCredentials: true })
          ]);
          setReferralData(codeRes.data);
          setReferralsList(referralsRes.data.referrals || []);
          setCreditData(creditRes.data);
        } catch (error) {
          console.error('Failed to fetch referral data:', error);
          // Only show error toast if it's not a network/auth issue
          if (error.response?.status !== 401) {
            toast.error('Failed to load referral data');
          }
        } finally {
          setReferralLoading(false);
        }
      };
      fetchReferralData();
    }, []);

    const copyToClipboard = async () => {
      if (referralData?.referral_url) {
        await navigator.clipboard.writeText(referralData.referral_url);
        setCopied(true);
        toast.success('Referral link copied!');
        setTimeout(() => setCopied(false), 2000);
      }
    };

    const shareOnSocial = (platform) => {
      const url = encodeURIComponent(referralData?.referral_url || '');
      const referrerReward = referralData?.referrer_reward || 50;
      const referredReward = referralData?.referred_reward || 50;
      const text = encodeURIComponent(`I'm using SeatPilot for my event ticketing! Sign up with my link - you'll get £${referredReward} and I'll get £${referrerReward} credit!`);
      const urls = {
        twitter: `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
        email: `mailto:?subject=Try SeatPilot&body=${text}%20${url}`
      };
      window.open(urls[platform], '_blank', 'width=600,height=400');
    };

    if (referralLoading) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <RefreshCw className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      );
    }

    // Get rewards from API response
    const referrerReward = referralData?.referrer_reward || 50;
    const referredReward = referralData?.referred_reward || 50;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Referral Program</h1>
            <p className="text-gray-500 mt-1">
              You get <span className="font-semibold text-violet-600">£{referrerReward}</span>, your friend gets <span className="font-semibold text-green-600">£{referredReward}</span> credit for White-Label
            </p>
          </div>
          <div className="flex gap-2">
            <span className="bg-violet-100 text-violet-700 px-3 py-1 rounded-full font-medium text-sm">
              You: £{referrerReward}
            </span>
            <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full font-medium text-sm">
              Friend: £{referredReward}
            </span>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg"><Users className="h-5 w-5 text-blue-600" /></div>
              <div>
                <p className="text-sm text-gray-500">Total</p>
                <p className="text-xl font-bold">{referralData?.stats?.total_referrals || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg"><Clock className="h-5 w-5 text-amber-600" /></div>
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-xl font-bold">{referralData?.stats?.pending_referrals || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg"><CheckCircle className="h-5 w-5 text-green-600" /></div>
              <div>
                <p className="text-sm text-gray-500">Confirmed</p>
                <p className="text-xl font-bold">{referralData?.stats?.confirmed_referrals || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-100 rounded-lg"><DollarSign className="h-5 w-5 text-violet-600" /></div>
              <div>
                <p className="text-sm text-gray-500">Credit</p>
                <p className="text-xl font-bold">£{(creditData?.available_credit || 0).toFixed(0)}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Credit Banner */}
        {creditData?.available_credit > 0 && (
          <Card className="bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200 p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-violet-900 flex items-center gap-2">
                  <Gift className="h-5 w-5" /> Your Referral Credit
                </h3>
                <p className="text-3xl font-bold text-violet-600 mt-2">£{creditData.available_credit.toFixed(2)}</p>
                <p className="text-sm text-violet-700 mt-1">= {creditData.months_covered} months of White-Label free!</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-violet-600">Expires:</p>
                <p className="font-medium text-violet-800">{creditData.credit_expiry_date ? new Date(creditData.credit_expiry_date).toLocaleDateString('en-GB') : '-'}</p>
                <p className="text-xs text-violet-500 mt-1">White-Label only</p>
              </div>
            </div>
            <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200 flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <span className="text-sm text-amber-700">Credit expires in 6 months if unused</span>
            </div>
          </Card>
        )}

        {/* Referral Link */}
        <Card className="p-6">
          <h3 className="font-semibold mb-4">Your Referral Link</h3>
          <div className="flex gap-2 mb-4">
            <Input value={referralData?.referral_url || ''} readOnly className="flex-1 font-mono text-sm bg-gray-50" />
            <Button onClick={copyToClipboard} variant={copied ? "default" : "outline"}>
              {copied ? <><CheckCircle className="h-4 w-4 mr-2" />Copied!</> : <><Copy className="h-4 w-4 mr-2" />Copy</>}
            </Button>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => shareOnSocial('twitter')} className="text-blue-500">
              <ExternalLink className="h-4 w-4 mr-1" /> Twitter
            </Button>
            <Button variant="outline" size="sm" onClick={() => shareOnSocial('linkedin')} className="text-blue-700">
              <ExternalLink className="h-4 w-4 mr-1" /> LinkedIn
            </Button>
            <Button variant="outline" size="sm" onClick={() => shareOnSocial('email')} className="text-gray-600">
              <Mail className="h-4 w-4 mr-1" /> Email
            </Button>
          </div>
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-600">Your Code: </span>
            <code className="font-mono font-bold text-violet-600">{referralData?.referral_code || '---'}</code>
          </div>
        </Card>

        {/* How it Works */}
        <Card className="p-6">
          <h3 className="font-semibold mb-4">How It Works</h3>
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div>
              <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="font-bold text-violet-600">1</span>
              </div>
              <p className="font-medium">Share Your Link</p>
              <p className="text-sm text-gray-500">Send to friends</p>
            </div>
            <div>
              <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="font-bold text-violet-600">2</span>
              </div>
              <p className="font-medium">They Subscribe</p>
              <p className="text-sm text-gray-500">To any paid plan</p>
            </div>
            <div>
              <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="font-bold text-violet-600">3</span>
              </div>
              <p className="font-medium">You Both Get Rewarded!</p>
              <p className="text-sm text-gray-500">You: £{referrerReward}, Friend: £{referredReward}</p>
            </div>
          </div>
        </Card>

        {/* Referrals List */}
        <Card className="p-6">
          <h3 className="font-semibold mb-4">Your Referrals</h3>
          {referralsList.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No referrals yet. Share your link to start earning!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 text-sm text-gray-500">Email</th>
                    <th className="text-left py-2 text-sm text-gray-500">Date</th>
                    <th className="text-left py-2 text-sm text-gray-500">Status</th>
                    <th className="text-right py-2 text-sm text-gray-500">Your Reward</th>
                  </tr>
                </thead>
                <tbody>
                  {referralsList.map((ref) => (
                    <tr key={ref.referral_id} className="border-b last:border-0">
                      <td className="py-3">{ref.referred_email_masked || ref.referred_email}</td>
                      <td className="py-3 text-gray-500">{new Date(ref.created_at).toLocaleDateString('en-GB')}</td>
                      <td className="py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          ref.status === 'confirmed' ? 'bg-green-100 text-green-700' : 
                          ref.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {ref.status}
                        </span>
                      </td>
                      <td className="py-3 text-right">
                        {ref.status === 'confirmed' ? (
                          <span className="text-green-600 font-medium">+£{ref.referrer_reward || ref.reward_amount || referrerReward}</span>
                        ) : (
                          <span className="text-gray-400">£{ref.referrer_reward || ref.reward_amount || referrerReward}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    );
  };

  const menuItems = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "layouts", label: "Layouts", icon: Layout },
    { id: "organisers", label: "Organisers", icon: Users },
    { id: "analytics", label: "Analytics", icon: BarChart3 },
    { id: "api-keys", label: "API Keys", icon: Key },
    { id: "integration", label: "Integration", icon: Code },
    { id: "billing", label: "Billing", icon: CreditCard },
    { id: "referrals", label: "Referrals", icon: Gift },
    { id: "team", label: "Team", icon: UserPlus },
    { id: "support", label: "Support", icon: Headphones },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  if (hasAdminAccess) {
    menuItems.push({ id: "admin", label: "Admin Panel", icon: Shield });
  }

  // If no company, show create company prompt
  if (!company && !loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <Building2 className="w-16 h-16 text-violet-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to SeatPilot!</h2>
          <p className="text-gray-600 mb-6">
            Create your company to get started with a 1-month free trial.
          </p>
          
          {/* Show email verification warning if not verified */}
          {user && !user.email_verified && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-left">
                  <p className="text-sm font-medium text-amber-800">Email Verification Required</p>
                  <p className="text-sm text-amber-700 mt-1">Please verify your email address before creating a company profile in order to use SeatPilot.</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 text-amber-700 border-amber-300 hover:bg-amber-100"
                    onClick={handleResendVerificationEmail}
                    disabled={resendingOTP}
                  >
                    {resendingOTP ? (
                      <>
                        <div className="w-4 h-4 border-2 border-amber-700/30 border-t-amber-700 rounded-full animate-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Resend Verification Email
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          <Button 
            onClick={() => openCreateCompanyDialog()}
            className="bg-gradient-to-r from-violet-600 to-indigo-600"
            disabled={user && !user.email_verified}
          >
            Create Company
          </Button>
          <button 
            onClick={logout}
            className="block w-full text-gray-500 hover:text-gray-700 mt-4 text-sm"
          >
            Sign out
          </button>
        </Card>

        {/* Email Verification Dialog */}
        <Dialog open={showVerifyEmail} onOpenChange={setShowVerifyEmail}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-violet-600" />
                Verify Your Email
              </DialogTitle>
              <DialogDescription>
                We sent a 6-digit verification code to <strong>{user?.email}</strong>. Enter it below to verify your email.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-sm font-medium">Verification Code</Label>
                <Input
                  type="text"
                  maxLength={6}
                  value={verificationOTP}
                  onChange={(e) => setVerificationOTP(e.target.value.replace(/\D/g, ''))}
                  placeholder="Enter 6-digit code"
                  className="text-center text-lg tracking-widest mt-2"
                />
              </div>
              <Button 
                onClick={handleVerifyEmail} 
                className="w-full bg-gradient-to-r from-violet-600 to-indigo-600"
                disabled={verifyingEmail || verificationOTP.length !== 6}
              >
                {verifyingEmail ? "Verifying..." : "Verify Email"}
              </Button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleResendVerificationEmail}
                  disabled={resendingOTP}
                  className="text-sm text-violet-600 hover:text-violet-800 disabled:opacity-50"
                >
                  {resendingOTP ? "Sending..." : "Didn't receive the code? Resend"}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={showCreateCompany} onOpenChange={setShowCreateCompany}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create Your Company</DialogTitle>
            </DialogHeader>
            
            {checkingEligibility ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-violet-500" />
                <span className="ml-2 text-gray-600">Checking eligibility...</span>
              </div>
            ) : (
              <form onSubmit={handleCreateCompany} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                  <Input
                    value={companyForm.company_name}
                    onChange={(e) => setCompanyForm({...companyForm, company_name: e.target.value})}
                    placeholder="Your Company Ltd"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                  <Input
                    value={companyForm.website}
                    onChange={(e) => setCompanyForm({...companyForm, website: e.target.value})}
                    placeholder="https://yourcompany.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                  <Input
                    value={companyForm.phone}
                    onChange={(e) => setCompanyForm({...companyForm, phone: e.target.value})}
                    placeholder="+44 123 456 7890"
                    required
                  />
                </div>
                
                {/* Plan Selection - Only show if not eligible for free trial */}
                {trialEligibility && !trialEligibility.eligible_for_free_trial && (
                  <div className="border-t pt-4 mt-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                      <p className="text-sm text-amber-800">
                        <strong>Note:</strong> {trialEligibility.message}
                      </p>
                    </div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select a Plan *</label>
                    <div className="space-y-2">
                      {trialEligibility.available_plans?.map((plan) => (
                        <label
                          key={plan.id}
                          className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                            companyForm.plan_id === plan.id 
                              ? 'border-violet-500 bg-violet-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="plan"
                            value={plan.id}
                            checked={companyForm.plan_id === plan.id}
                            onChange={() => setCompanyForm({...companyForm, plan_id: plan.id})}
                            className="mr-3"
                          />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-900">{plan.name}</span>
                              <span className="text-violet-600 font-semibold">{plan.price}</span>
                            </div>
                            {plan.description && (
                              <p className="text-xs text-gray-500 mt-0.5">{plan.description}</p>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Free Trial Info */}
                {trialEligibility?.eligible_for_free_trial && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-800">
                      🎉 <strong>30-Day Free Trial!</strong> No credit card required.
                    </p>
                  </div>
                )}
                
                <Button type="submit" className="w-full" disabled={creatingCompany}>
                  {creatingCompany ? "Creating..." : 
                    trialEligibility?.eligible_for_free_trial ? "Create & Start Free Trial" : "Create Company"
                  }
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4 z-50">
        <div className="flex items-center gap-2" onClick={() => navigate('/')}>
          <div className="w-8 h-8 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">SP</span>
          </div>
          <span className="text-lg font-bold text-gray-900">SeatPilot</span>
        </div>
        <button 
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          {sidebarCollapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile Overlay */}
      {!sidebarCollapsed && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarCollapsed(true)}
        />
      )}

      {/* Sidebar - Hidden on mobile by default, slide in when hamburger clicked */}
      <aside className={`
        ${sidebarCollapsed ? 'w-16 -translate-x-full md:translate-x-0' : 'w-64 translate-x-0'} 
        bg-white border-r border-gray-200 fixed h-full transition-all duration-300 z-50
        md:translate-x-0 flex flex-col
      `}>
        {/* Mobile Header with branding - visible on mobile when sidebar open */}
        <div className={`md:hidden h-14 flex items-center justify-between px-4 border-b border-gray-200 ${sidebarCollapsed ? 'hidden' : ''}`}>
          <div 
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <div className="w-8 h-8 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">SP</span>
            </div>
            <span className="text-lg font-bold text-gray-900">SeatPilot</span>
          </div>
          <button
            onClick={() => setSidebarCollapsed(true)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto">
          <div className={`hidden md:flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-between'} mb-8`}>
            <div 
              className={`flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity`}
              onClick={() => navigate('/')}
              title="Go to Homepage"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-sm">SP</span>
              </div>
              {!sidebarCollapsed && <span className="text-xl font-bold text-gray-900">SeatPilot</span>}
            </div>
            <button 
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-1 hover:bg-gray-100 rounded-lg"
              title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
          </div>

          <nav className="space-y-1" data-walkthrough="walkthrough-dashboard-sidebar">
            {menuItems.map((item) => (
              <button
                key={item.id}
                data-walkthrough={`walkthrough-tab-${item.id}`}
                onClick={() => {
                  if (item.id === "admin") {
                    // Single-tab navigation (was window.open spawning a new
                    // tab every click). Cmd-click still opens new tab.
                    navigate('/backoffice/dashboard');
                  } else {
                    handleTabChange(item.id);
                  }
                }}
                className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : ''} gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === item.id
                    ? "bg-violet-50 text-violet-700"
                    : item.id === "admin" 
                      ? "text-violet-600 hover:bg-violet-50 bg-violet-50/50"
                      : "text-gray-600 hover:bg-gray-50"
                }`}
                title={sidebarCollapsed ? item.label : ""}
              >
                <div className="relative">
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {item.id === "support" && unreadCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </div>
                {!sidebarCollapsed && (
                  <span className="flex items-center gap-2">
                    {item.label}
                    {item.id === "support" && unreadCount > 0 && (
                      <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full">
                        {unreadCount}
                      </span>
                    )}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        <div className={`p-4 border-t border-gray-200 ${sidebarCollapsed ? 'px-2' : ''}`}>
          {!sidebarCollapsed && (
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                <span className="text-violet-600 font-medium">
                  {user?.name?.charAt(0) || user?.email?.charAt(0) || "?"}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.name || "User"}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
          )}
          <button
            onClick={logout}
            className={`flex items-center ${sidebarCollapsed ? 'justify-center' : ''} gap-2 text-sm text-gray-600 hover:text-gray-900 w-full px-3 py-2 rounded-lg hover:bg-gray-50`}
            title={sidebarCollapsed ? "Sign out" : ""}
          >
            <LogOut className="w-4 h-4 flex-shrink-0" />
            {!sidebarCollapsed && "Sign out"}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 transition-all duration-300 pt-14 md:pt-0 min-w-0 overflow-x-hidden ${sidebarCollapsed ? 'md:ml-16' : 'md:ml-64'}`}>
        {/* Top Header Bar */}
        {/* On mobile: static (no sticky) — the fixed brand bar above already
            handles "always-visible" needs. On desktop: sticky to top.
            Note: must NOT use `sticky top-14` here because parent already has
            `pt-14`; combining both produces a doubled 112px gap below the
            fixed mobile bar (top offset is applied even at natural position). */}
        <div className="md:sticky md:top-0 z-30 bg-white border-b border-gray-200 px-4 md:px-8 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-base md:text-lg font-semibold text-gray-900">
                {activeTab === "overview" && "Dashboard"}
                {activeTab === "layouts" && "Layouts"}
                {activeTab === "events" && "Events"}
                {activeTab === "organisers" && "Organisers"}
                {activeTab === "analytics" && "Analytics"}
                {activeTab === "billing" && "Billing"}
                {activeTab === "api" && "API Keys"}
                {activeTab === "settings" && "Settings"}
                {activeTab === "support" && "Support"}
                {activeTab === "referrals" && "Referral Program"}
              </h1>
              {company?.customer_id && (
                <p className="text-xs text-gray-500 font-mono">Customer ID: {company.customer_id}</p>
              )}
            </div>
            <div className="flex items-center gap-2 md:gap-4">
              {/* Referral Promo Badge */}
              <button
                onClick={() => handleTabChange("referrals")}
                className="hidden sm:flex items-center gap-1.5 bg-gradient-to-r from-violet-500 to-purple-600 text-white px-3 py-1.5 rounded-full text-xs font-medium hover:from-violet-600 hover:to-purple-700 transition-all shadow-sm"
              >
                <Gift className="w-3.5 h-3.5" />
                <span>Refer & Earn</span>
              </button>
              
              {/* Notification Bell - User context only */}
              <NotificationBell 
                isAdmin={false} 
                context="user"
                onOpenTicket={(ticketId) => {
                  handleTabChange("support");
                  setTimeout(() => fetchTicketDetails(ticketId), 100);
                }}
                onGoToSupport={() => handleTabChange("support")}
              />
              
              {/* User Dropdown */}
              <UserDropdown onNavigate={(tab) => handleTabChange(tab)} />
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div className="p-4 md:p-8">
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div>
            <div className="flex justify-between items-center mb-8">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
                <p className="text-gray-600">Welcome back, {user?.name?.split(' ')[0] || 'there'}!</p>
              </div>
              <div className="flex items-center gap-4">
                {company?.customer_id && (
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Customer No.</p>
                    <p className="text-lg font-bold text-violet-600 font-mono">{company.customer_id}</p>
                  </div>
                )}
                {getSubscriptionBadge()}
              </div>
            </div>

            {/* Customer Info Banner */}
            {company?.customer_id && (
              <Card className="p-4 mb-6 bg-gradient-to-r from-violet-50 to-indigo-50 border-violet-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-violet-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{company.name}</p>
                      <p className="text-sm text-gray-600">Customer No: <span className="font-mono font-bold text-violet-600">{company.customer_id}</span></p>
                    </div>
                  </div>
                  <div className="text-right text-sm">
                    <p className="text-gray-500">Need support?</p>
                    <p className="text-gray-600">Quote your customer number</p>
                  </div>
                </div>
              </Card>
            )}

            {/* Referral Program Promotion Banner — shown only ~1 day in 5 and
                in a compact single-line form so it doesn't eat dashboard
                real estate. Customers can permanently dismiss it via the X. */}
            {(() => {
              if (referBannerDismissed) return null;
              const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
              const key = "sp_refer_banner_v2";
              let stored = null;
              try { stored = JSON.parse(localStorage.getItem(key) || "null"); } catch { /* ignore */ }
              // Pick a stable shown/hidden state for today (idx 0..4; show only when 0 = ~1 day in 5)
              if (!stored || stored.date !== today) {
                const idx = Math.floor(Math.random() * 5);
                stored = { date: today, idx };
                try { localStorage.setItem(key, JSON.stringify(stored)); } catch { /* ignore */ }
              }
              if (stored.idx !== 0) return null;
              return (
                <Card
                  className="px-3 py-2 mb-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow flex items-center justify-between gap-3"
                  onClick={() => handleTabChange('referrals')}
                  data-testid="refer-earn-banner"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Gift className="w-4 h-4 shrink-0 text-white" />
                    <p className="text-xs sm:text-sm font-medium truncate">
                      <span className="font-bold">Refer &amp; Earn:</span> share SeatPilot &amp; both get White-Label credit.
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className="hidden sm:inline text-xs font-semibold underline underline-offset-2">Start →</span>
                    <button
                      type="button"
                      onMouseDown={(e) => { e.stopPropagation(); }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        try { localStorage.setItem("sp_refer_banner_dismissed", "1"); } catch { /* ignore */ }
                        setReferBannerDismissed(true);
                      }}
                      aria-label="Dismiss refer-and-earn banner"
                      data-testid="refer-earn-dismiss"
                      className="ml-1 w-6 h-6 rounded hover:bg-white/20 flex items-center justify-center text-white/80 hover:text-white text-sm leading-none"
                    >
                      ×
                    </button>
                  </div>
                </Card>
              );
            })()}

            {/* No Active Subscription / Expired plan banner — shows on Overview
                tab when the customer's plan is expired/cancelled OR backend
                flagged is_expired=true. Renew button now goes to /pricing
                so the customer lands on a live plan-selection screen. */}
            {(!subscription || subscription.is_expired || subscription.status === 'cancelled' || subscription.status === 'expired') && (
              <Card className="p-4 mb-6 bg-gradient-to-r from-red-500 to-orange-500 text-white border-0 shadow-lg" data-testid="overview-expired-banner">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-bold text-lg">
                        {subscription?.is_free_trial && (subscription?.is_expired || subscription?.status === 'expired')
                          ? 'Your free trial has ended — upgrade to keep your data'
                          : (subscription?.is_expired || subscription?.status === 'expired'
                              ? 'Your plan has expired'
                              : (subscription?.status === 'cancelled' ? 'Your plan was cancelled' : 'No Active Subscription'))}
                      </p>
                      <p className="text-sm text-white/90">
                        {subscription?.is_free_trial
                          ? 'Pick a plan now to keep your layouts, API keys, and bookings live.'
                          : 'Renew now to continue creating layouts and accepting bookings'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => navigate('/pricing')}
                      className="bg-white text-red-600 hover:bg-white/90 font-semibold px-6"
                      data-testid="overview-renew-cta"
                    >
                      {subscription?.is_free_trial ? 'Upgrade now' : 'Renew now'}
                    </Button>
                    <Button
                      onClick={() => setActiveTab('billing')}
                      variant="outline"
                      className="border-white text-white hover:bg-white/10 px-4"
                    >
                      View billing
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Expiring-soon (≤15 days) + missing payment method / auto-renew banner.
                Shows for ACTIVE plans about to expire when the customer hasn't
                set up a payment method or auto-renewal yet — primary upsell
                lever to convert lapsing customers before they churn. */}
            {subscription && !subscription.is_expired && subscription.expiring_soon && (!subscription.has_payment_method || !subscription.auto_renew_enabled) && (
              <Card className="p-4 mb-6 bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 shadow-lg" data-testid="overview-expiring-soon-banner">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-lg">
                        Your plan expires in {subscription.days_until_expiry} day{subscription.days_until_expiry === 1 ? '' : 's'}
                      </p>
                      <p className="text-sm text-white/90">
                        {!subscription.has_payment_method
                          ? 'Add a payment method now and turn on auto-renew so you don\'t lose access.'
                          : 'Auto-renew is OFF. Switch it on to avoid disruption.'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setActiveTab('billing')}
                      className="bg-white text-amber-700 hover:bg-white/90 font-semibold px-6"
                      data-testid="overview-add-payment-cta"
                    >
                      {!subscription.has_payment_method ? 'Add payment method' : 'Enable auto-renew'}
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Day-7+ free trial upgrade nudge — soft amber banner that fires
                only after the user has been on the trial for 7+ days and the
                trial is still active. Encourages conversion before the
                expiry red-banner kicks in. */}
            {subscription && subscription.is_free_trial && !subscription.is_expired && (subscription.age_days || 0) >= 7 && (
              <Card className="px-4 py-3 mb-6 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white border-0 shadow-md" data-testid="overview-trial-upgrade-nudge">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center shrink-0">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-sm sm:text-base">Enjoying SeatPilot? Lock it in before your trial ends.</p>
                      <p className="text-xs sm:text-sm text-white/90 truncate">
                        Upgrade to a paid plan today and keep every layout, booking, and API key working without interruption.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      onClick={() => navigate('/pricing')}
                      size="sm"
                      className="bg-white text-violet-700 hover:bg-white/90 font-semibold"
                      data-testid="overview-trial-upgrade-cta"
                    >
                      See plans
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* White Label Promotion Banner — only shown to PAID subscribers
                (not free-trial / expired) so we don't dilute the upgrade
                push. White-label is a Tier-2 upsell once they're on a
                revenue plan. */}
            {!company?.white_label && subscription?.is_paid_plan && !subscription?.is_expired && (
              <Card className="p-4 mb-6 bg-gradient-to-r from-purple-600 to-violet-600 text-white border-0">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-semibold text-lg">White Label Available • £20/month</p>
                        <p className="text-sm text-white/80">Give your customers a fully branded experience</p>
                      </div>
                    </div>
                    <Button
                      onClick={() => setShowWhiteLabelConfirm(true)}
                      className="bg-white text-violet-600 hover:bg-white/90 font-semibold whitespace-nowrap"
                    >
                      Subscribe Now
                    </Button>
                  </div>
                  
                  {/* White Label Benefits */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-2 border-t border-white/20">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-300" />
                      <span className="text-sm text-white/90">Remove &quot;Powered by SeatPilot&quot;</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-300" />
                      <span className="text-sm text-white/90">Custom logo on checkout</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-300" />
                      <span className="text-sm text-white/90">Emails from your domain</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-300" />
                      <span className="text-sm text-white/90">Your brand colors</span>
                    </div>
                  </div>
                </div>
              </Card>
            )}
            
            {/* White Label Confirmation Dialog */}
            {showWhiteLabelConfirm && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="max-w-lg w-full p-6 bg-white">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    🏷️ Subscribe to White Label
                  </h3>
                  
                  <p className="text-gray-600 mb-4">
                    Transform your customer experience with a fully branded seating solution.
                  </p>
                  
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <p className="font-semibold text-gray-900 mb-3">What&apos;s Included:</p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-medium">Remove SeatPilot Branding</span>
                          <p className="text-sm text-gray-500">&quot;Powered by SeatPilot&quot; won&apos;t appear on your checkout widgets</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-medium">Custom Logo</span>
                          <p className="text-sm text-gray-500">Your logo appears on booking pages and tickets</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-medium">📧 Custom Email Domain</span>
                          <p className="text-sm text-gray-500">Send layout notifications from your own email (e.g., tickets@yourcompany.com)</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-medium">Brand Colors</span>
                          <p className="text-sm text-gray-500">Customize the checkout experience with your brand colors</p>
                        </div>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-violet-50 rounded-lg mb-4">
                    <span className="font-medium text-gray-700">Monthly Price</span>
                    <span className="text-2xl font-bold text-violet-600">£20<span className="text-sm font-normal text-gray-500">/month</span></span>
                  </div>
                  
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setShowWhiteLabelConfirm(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={async () => {
                        setSubscribingWhiteLabel(true);
                        try {
                          const res = await axios.post(`${getAPI()}/subscriptions/white-label/subscribe`, {
                            return_url: window.location.href
                          }, { withCredentials: true });
                          if (res.data.checkout_url) {
                            window.location.href = res.data.checkout_url;
                          } else if (res.data.success) {
                            toast.success(res.data.message || 'White label activated!');
                            await fetchCompanyData();
                            setShowWhiteLabelConfirm(false);
                          }
                        } catch (error) {
                          await fetchCompanyData();
                          if (company?.white_label) {
                            toast.success('White label activated!');
                            setShowWhiteLabelConfirm(false);
                          } else {
                            toast.error(error.response?.data?.detail || 'Failed to subscribe');
                          }
                        } finally {
                          setSubscribingWhiteLabel(false);
                        }
                      }}
                      disabled={subscribingWhiteLabel}
                      className="flex-1 bg-violet-600 hover:bg-violet-700"
                    >
                      {subscribingWhiteLabel ? 'Processing...' : 'Subscribe to White Label'}
                    </Button>
                  </div>
                </Card>
              </div>
            )}

            {/* White Label Active Badge - Only show if CANCELLING to prompt reactivation */}
            {company?.white_label && company?.white_label_cancelling && (
              <Card className="p-4 mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-amber-800">White Label Cancelling</p>
                      <p className="text-sm text-amber-600">
                        Ends {company?.white_label_cancel_at ? new Date(company.white_label_cancel_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : 'soon'} • Reactivate to keep branding hidden
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={() => setShowReactivateConfirm(true)}
                  >
                    Reactivate Now
                  </Button>
                </div>
              </Card>
            )}

            {/* Stats Grid - Clickable Widgets */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card 
                className="p-6 cursor-pointer hover:shadow-lg hover:border-violet-300 transition-all"
                onClick={() => handleTabChange('layouts')}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center">
                    <Layout className="w-6 h-6 text-violet-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{layouts.length}</p>
                    <p className="text-sm text-gray-500">Total Layouts</p>
                  </div>
                </div>
              </Card>

              <Card 
                className="p-6 cursor-pointer hover:shadow-lg hover:border-green-300 transition-all"
                onClick={() => handleTabChange('analytics')}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{layouts.reduce((a, l) => a + (l.total_seats || l.seat_count || 0), 0).toLocaleString()}</p>
                    <p className="text-sm text-gray-500">Total Seats</p>
                  </div>
                </div>
              </Card>

              <Card 
                className="p-6 cursor-pointer hover:shadow-lg hover:border-blue-300 transition-all"
                onClick={() => handleTabChange('analytics')}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{layouts.filter(l => l.event_id).length}</p>
                    <p className="text-sm text-gray-500">Linked to Events</p>
                  </div>
                </div>
              </Card>

              <Card 
                className="p-6 cursor-pointer hover:shadow-lg hover:border-amber-300 transition-all"
                onClick={() => handleTabChange('billing')}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {subscription?.expires_at ? 
                        Math.max(0, Math.ceil((new Date(subscription.expires_at) - new Date()) / (1000 * 60 * 60 * 24)))
                        : "∞"
                      }
                    </p>
                    <p className="text-sm text-gray-500">Days Remaining</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Quick Actions */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <Button 
                    className="w-full justify-start gap-2" 
                    variant="outline"
                    onClick={() => navigate('/builder')}
                  >
                    <Plus className="w-4 h-4" /> Create New Layout
                  </Button>
                  <Button 
                    className="w-full justify-start gap-2" 
                    variant="outline"
                    onClick={() => handleTabChange('api-keys')}
                  >
                    <Key className="w-4 h-4" /> View API Key
                  </Button>
                  <Button 
                    className="w-full justify-start gap-2" 
                    variant="outline"
                    onClick={() => handleTabChange('integration')}
                  >
                    <Code className="w-4 h-4" /> Integration Guide
                  </Button>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Login Activity</h3>
                <div className="space-y-3">
                  {loginHistory.slice(0, 3).map((login) => (
                    <div key={`${login.timestamp}-${login.ip}`} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">{formatDate(login.timestamp)}</span>
                      <span className="text-gray-400 text-xs truncate max-w-[150px]">{login.ip}</span>
                    </div>
                  ))}
                  {loginHistory.length === 0 && (
                    <p className="text-gray-500 text-sm">No login history yet</p>
                  )}
                </div>
              </Card>
            </div>

            {/* Recent Layouts */}
            <Card className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Recent Layouts</h3>
                <Button variant="ghost" size="sm" onClick={() => handleTabChange('layouts')}>
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              {layouts.length === 0 ? (
                <div className="text-center py-8">
                  <Layout className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No layouts yet</p>
                  <Button 
                    className="mt-4" 
                    variant="outline"
                    onClick={() => navigate('/builder')}
                  >
                    Create Your First Layout
                  </Button>
                </div>
              ) : (
                <div className="divide-y">
                  {layouts.slice(0, 5).map((layout) => (
                    <div key={layout.id} className="py-3 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{layout.name}</p>
                        <p className="text-sm text-gray-500">{layout.venue_name || 'No venue'}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/builder/${layout.id}`)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/widget/${layout.id}`)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Layouts Tab */}
        {activeTab === "layouts" && (
          <div>
            {/* Header with controls */}
            <div className="flex flex-col gap-4 mb-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h1 className="text-2xl font-bold text-gray-900">Layouts</h1>
                <Button onClick={() => navigate('/builder')} className="gap-2">
                  <Plus className="w-4 h-4" /> New Layout
                </Button>
              </div>
              
              {/* Search and View Controls */}
              {layouts.length > 0 && (
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  {/* Search */}
                  <div className="relative flex-1 min-w-0 w-full sm:max-w-xs">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search layouts..."
                      value={layoutSearchQuery}
                      onChange={(e) => setLayoutSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  
                  {/* Sort */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="gap-2">
                        <ArrowUpDown className="w-4 h-4" />
                        Sort
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => { setLayoutSortBy('created'); setLayoutSortOrder('desc'); }}>
                        <Clock className="w-4 h-4 mr-2" /> Newest First
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { setLayoutSortBy('created'); setLayoutSortOrder('asc'); }}>
                        <Clock className="w-4 h-4 mr-2" /> Oldest First
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => { setLayoutSortBy('name'); setLayoutSortOrder('asc'); }}>
                        <FileText className="w-4 h-4 mr-2" /> Name (A-Z)
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { setLayoutSortBy('name'); setLayoutSortOrder('desc'); }}>
                        <FileText className="w-4 h-4 mr-2" /> Name (Z-A)
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => { setLayoutSortBy('status'); setLayoutSortOrder('desc'); }}>
                        <CheckCircle2 className="w-4 h-4 mr-2" /> Published First
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { setLayoutSortBy('status'); setLayoutSortOrder('asc'); }}>
                        <Circle className="w-4 h-4 mr-2" /> Drafts First
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  
                  {/* View Toggle */}
                  <div className="flex items-center bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setLayoutViewMode('grid')}
                      className={`p-1.5 rounded ${layoutViewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
                    >
                      <Grid3X3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setLayoutViewMode('list')}
                      className={`p-1.5 rounded ${layoutViewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
                    >
                      <List className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
              
              {/* Filter Tabs - Show counts for each filter */}
              {layouts.length > 0 && (
                <div className="flex flex-wrap gap-2 border-b pb-3">
                  <button
                    onClick={() => setLayoutFilter('all')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      layoutFilter === 'all' 
                        ? 'bg-violet-100 text-violet-700 border border-violet-200' 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    All ({layouts.length})
                  </button>
                  <button
                    onClick={() => setLayoutFilter('published')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-1 ${
                      layoutFilter === 'published' 
                        ? 'bg-green-100 text-green-700 border border-green-200' 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    Published ({layouts.filter(l => l.is_published).length})
                  </button>
                  <button
                    onClick={() => setLayoutFilter('draft')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-1 ${
                      layoutFilter === 'draft' 
                        ? 'bg-amber-100 text-amber-700 border border-amber-200' 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Circle className="w-4 h-4" />
                    Draft ({layouts.filter(l => !l.is_published).length})
                  </button>
                  <button
                    onClick={() => setLayoutFilter('linked')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-1 ${
                      layoutFilter === 'linked' 
                        ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Link className="w-4 h-4" />
                    Linked to Event ({layouts.filter(l => l.event_id).length})
                  </button>
                </div>
              )}
            </div>
            
            {layouts.length === 0 ? (
              <Card className="p-12 text-center">
                <Layout className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No layouts yet</h3>
                <p className="text-gray-500 mb-4">Create your first seating layout to get started.</p>
                <Button onClick={() => navigate('/builder')}>Create Layout</Button>
              </Card>
            ) : (
              <>
                {/* Filter and sort layouts */}
                {(() => {
                  // Apply status filter first
                  let filteredLayouts = layouts.filter(l => {
                    if (layoutFilter === 'published') return l.is_published;
                    if (layoutFilter === 'draft') return !l.is_published;
                    if (layoutFilter === 'linked') return !!l.event_id;
                    return true; // 'all'
                  });
                  
                  // Then apply search filter
                  filteredLayouts = filteredLayouts.filter(l => 
                    l.name?.toLowerCase().includes(layoutSearchQuery.toLowerCase()) ||
                    l.venue_name?.toLowerCase().includes(layoutSearchQuery.toLowerCase())
                  );
                  
                  // Sort layouts
                  filteredLayouts.sort((a, b) => {
                    let comparison = 0;
                    if (layoutSortBy === 'created') {
                      comparison = new Date(b.created_at) - new Date(a.created_at);
                    } else if (layoutSortBy === 'name') {
                      comparison = (a.name || '').localeCompare(b.name || '');
                    } else if (layoutSortBy === 'status') {
                      comparison = (b.is_published ? 1 : 0) - (a.is_published ? 1 : 0);
                    }
                    return layoutSortOrder === 'desc' ? comparison : -comparison;
                  });
                  
                  if (filteredLayouts.length === 0) {
                    return (
                      <Card className="p-8 text-center">
                        <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No layouts found</h3>
                        <p className="text-gray-500">Try a different search term</p>
                      </Card>
                    );
                  }
                  
                  // Pagination calculations
                  const totalLayouts = filteredLayouts.length;
                  const totalPages = Math.ceil(totalLayouts / layoutPageSize);
                  const startIndex = (layoutCurrentPage - 1) * layoutPageSize;
                  const endIndex = startIndex + layoutPageSize;
                  const paginatedLayouts = filteredLayouts.slice(startIndex, endIndex);
                  
                  // Pagination Component
                  const PaginationControls = () => {
                    if (totalPages <= 1) return null;
                    
                    // Generate page numbers to display
                    const getPageNumbers = () => {
                      const pages = [];
                      const maxVisiblePages = 5;
                      
                      if (totalPages <= maxVisiblePages) {
                        for (let i = 1; i <= totalPages; i++) pages.push(i);
                      } else {
                        // Always show first page
                        pages.push(1);
                        
                        // Calculate start and end of middle pages
                        let start = Math.max(2, layoutCurrentPage - 1);
                        let end = Math.min(totalPages - 1, layoutCurrentPage + 1);
                        
                        // Adjust if at start or end
                        if (layoutCurrentPage <= 2) {
                          end = 4;
                        } else if (layoutCurrentPage >= totalPages - 1) {
                          start = totalPages - 3;
                        }
                        
                        // Add ellipsis if needed before middle pages
                        if (start > 2) pages.push('...');
                        
                        // Add middle pages
                        for (let i = start; i <= end; i++) pages.push(i);
                        
                        // Add ellipsis if needed after middle pages
                        if (end < totalPages - 1) pages.push('...');
                        
                        // Always show last page
                        pages.push(totalPages);
                      }
                      
                      return pages;
                    };
                    
                    return (
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-200">
                        {/* Results info */}
                        <div className="text-sm text-gray-600">
                          Showing {startIndex + 1}-{Math.min(endIndex, totalLayouts)} of {totalLayouts} layouts
                        </div>
                        
                        {/* Pagination buttons */}
                        <div className="flex items-center gap-1">
                          {/* Previous button */}
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={layoutCurrentPage === 1}
                            onClick={() => setLayoutCurrentPage(p => Math.max(1, p - 1))}
                            className="h-8 px-2"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </Button>
                          
                          {/* Page numbers */}
                          <div className="flex items-center gap-1">
                            {getPageNumbers().map((page, idx) => (
                              page === '...' ? (
                                <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">...</span>
                              ) : (
                                <Button
                                  key={page}
                                  variant={layoutCurrentPage === page ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => setLayoutCurrentPage(page)}
                                  className={`h-8 w-8 p-0 ${layoutCurrentPage === page ? 'bg-violet-600 hover:bg-violet-700' : ''}`}
                                >
                                  {page}
                                </Button>
                              )
                            ))}
                          </div>
                          
                          {/* Next button */}
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={layoutCurrentPage === totalPages}
                            onClick={() => setLayoutCurrentPage(p => Math.min(totalPages, p + 1))}
                            className="h-8 px-2"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        {/* Page size selector */}
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">Per page:</span>
                          <select
                            value={layoutPageSize}
                            onChange={(e) => {
                              setLayoutPageSize(Number(e.target.value));
                              setLayoutCurrentPage(1);
                            }}
                            className="h-8 px-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                          >
                            <option value={6}>6</option>
                            <option value={12}>12</option>
                            <option value={24}>24</option>
                            <option value={48}>48</option>
                          </select>
                        </div>
                      </div>
                    );
                  };
                  
                  // Grid View
                  if (layoutViewMode === 'grid') {
                    return (
                      <div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {paginatedLayouts.map((layout) => {
                          // Always trust the backend's authoritative total_seats from the seats collection.
                          // The canvas_objects-derived count returns grid capacity (e.g. 10x6=60) not the
                          // actual bookable seat count (e.g. 36 after some seats marked unbookable).
                          const seatCount = layout.total_seats ?? layout.seat_count ?? 0;

                          return (
                            <Card key={layout.id} className="p-4 hover:shadow-md transition-shadow">
                              <div className="flex justify-between items-start mb-2">
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-semibold text-gray-900 truncate">{layout.name}</h3>
                                  <p className="text-sm text-gray-500">{layout.venue_name || 'No venue'}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  {/* Source Badge (API vs Dashboard) */}
                                  <span
                                    data-testid={`layout-source-${layout.id}`}
                                    className={`px-2 py-1 text-xs rounded-full border font-semibold ${
                                      (layout.created_via || 'dashboard') === 'api'
                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                        : 'bg-slate-50 text-slate-600 border-slate-200'
                                    }`}
                                    title={(layout.created_via || 'dashboard') === 'api' ? 'Created via external API / SDK' : 'Created via SeatPilot dashboard'}
                                  >
                                    {(layout.created_via || 'dashboard') === 'api' ? 'API' : 'Dashboard'}
                                  </span>
                                  {/* Status Badge */}
                                  {layout.is_published ? (
                                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
                                      <CheckCircle2 className="w-3 h-3" /> Published
                                    </span>
                                  ) : (
                                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full flex items-center gap-1">
                                      <Circle className="w-3 h-3" /> Draft
                                    </span>
                                  )}
                                  
                                  {/* Actions Dropdown */}
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                        <MoreVertical className="w-4 h-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem onClick={() => setRenameDialog({ open: true, layout, name: layout.name })}>
                                        <FileEdit className="w-4 h-4 mr-2" /> Rename
                                      </DropdownMenuItem>
                                      <DropdownMenuItem 
                                        disabled={duplicating === layout.id}
                                        onClick={async () => {
                                          setDuplicating(layout.id);
                                          try {
                                            const res = await axios.post(`${getAPI()}/admin/layouts/${layout.id}/duplicate`, {}, { withCredentials: true });
                                            toast.success("Layout duplicated successfully");
                                            setLayouts(prev => [res.data, ...prev]);
                                          } catch (error) {
                                            toast.error("Failed to duplicate layout");
                                          } finally {
                                            setDuplicating(null);
                                          }
                                        }}
                                      >
                                        <Copy className="w-4 h-4 mr-2" /> {duplicating === layout.id ? 'Duplicating...' : 'Duplicate'}
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem 
                                        onClick={async () => {
                                          try {
                                            await axios.patch(`${getAPI()}/admin/layouts/${layout.id}`, { is_published: !layout.is_published }, { withCredentials: true });
                                            toast.success(layout.is_published ? "Layout unpublished" : "Layout published");
                                            setLayouts(layouts.map(l => l.id === layout.id ? { ...l, is_published: !l.is_published } : l));
                                          } catch (error) {
                                            toast.error("Failed to update layout status");
                                          }
                                        }}
                                      >
                                        {layout.is_published ? (
                                          <><EyeOff className="w-4 h-4 mr-2" /> Unpublish</>
                                        ) : (
                                          <><CheckCircle2 className="w-4 h-4 mr-2" /> Publish</>
                                        )}
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem 
                                        onClick={() => navigate(`/layout-info/${layout.id}`)}
                                        data-testid={`layout-info-${layout.id}`}
                                      >
                                        <Info className="w-4 h-4 mr-2" /> Info
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem 
                                        className="text-red-600"
                                        onClick={async () => {
                                          if (window.confirm(`Are you sure you want to delete "${layout.name}"?`)) {
                                            try {
                                              await axios.delete(`${getAPI()}/admin/layouts/${layout.id}`, { withCredentials: true });
                                              toast.success("Layout deleted");
                                              setLayouts(layouts.filter(l => l.id !== layout.id));
                                            } catch (error) {
                                              toast.error("Failed to delete layout");
                                            }
                                          }
                                        }}
                                      >
                                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </div>
                              
                              {/* Layout Stats */}
                              <div className="grid grid-cols-2 gap-2 mb-3 p-2 bg-gray-50 rounded-lg">
                                <div className="text-center">
                                  <p className="text-lg font-bold text-violet-600">{seatCount}</p>
                                  <p className="text-xs text-gray-500">Total Seats</p>
                                </div>
                                <div className="text-center">
                                  <p className="text-lg font-bold text-blue-600">{layout.booked_seats_count || 0}</p>
                                  <p className="text-xs text-gray-500">Booked</p>
                                </div>
                              </div>
                              
                              {/* Event Link Status */}
                              {layout.event_id ? (
                                <div className="mb-3 p-2 bg-green-50 rounded-lg border border-green-100">
                                  <p className="text-xs text-green-700 font-medium flex items-center gap-1">
                                    <Link className="w-3 h-3" /> Linked to Event
                                  </p>
                                  <p className="text-sm text-green-800 font-semibold truncate">{layout.event_name}</p>
                                </div>
                              ) : !layout.is_published ? (
                                <div className="mb-3 p-2 bg-amber-50 rounded-lg border border-amber-100">
                                  <p className="text-xs text-amber-700">Publish to attach to events</p>
                                </div>
                              ) : null}
                              
                              {/* Tags */}
                              {layout.tags && layout.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-3">
                                  {layout.tags.slice(0, 3).map((tag, idx) => (
                                    <span key={idx} className="px-2 py-0.5 bg-violet-50 text-violet-700 text-xs rounded">
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              )}
                              
                              {/* Action Buttons - Edit, Preview, and Publish */}
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" className="flex-1" onClick={() => navigate(`/builder/${layout.id}`)}>
                                  <Edit className="w-4 h-4 mr-1" /> Edit
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant={layout.is_published ? "outline" : "default"}
                                  className={`flex-1 ${layout.is_published ? '' : 'bg-green-600 hover:bg-green-700 text-white'}`}
                                  disabled={publishing === layout.id}
                                  onClick={() => handlePublishToggle(layout)}
                                >
                                  {publishing === layout.id ? (
                                    <span className="flex items-center gap-1">
                                      <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                    </span>
                                  ) : layout.is_published ? (
                                    <><EyeOff className="w-4 h-4 mr-1" /> Unpublish</>
                                  ) : (
                                    <><CheckCircle2 className="w-4 h-4 mr-1" /> Publish</>
                                  )}
                                </Button>
                                {/* Preview button - works for both published and unpublished */}
                                <Button size="sm" variant="outline" onClick={() => window.open(`/widget/${layout.id}`, '_blank')} title="Preview Layout">
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </div>
                              
                              {/* Created date */}
                              <p className="text-xs text-gray-400 mt-2 text-right">
                                Created: {new Date(layout.created_at).toLocaleDateString()}
                              </p>
                            </Card>
                          );
                        })}
                      </div>
                      <PaginationControls />
                      </div>
                    );
                  }
                  
                  // List View
                  return (
                    <div>
                    <Card className="overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Source</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Seats</th>
                              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Booked</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Event</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Created</th>
                              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {paginatedLayouts.map((layout) => {
                              // Always trust backend total_seats (actual bookable seat count).
                              const seatCount = layout.total_seats ?? layout.seat_count ?? 0;

                              return (
                                <tr key={layout.id} className="hover:bg-gray-50">
                                  <td className="px-4 py-3">
                                    <div>
                                      <p className="font-medium text-gray-900">{layout.name}</p>
                                      <p className="text-xs text-gray-500">{layout.venue_name || 'No venue'}</p>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3">
                                    <span
                                      data-testid={`layout-source-row-${layout.id}`}
                                      className={`px-2 py-1 text-xs rounded-full border font-semibold ${
                                        (layout.created_via || 'dashboard') === 'api'
                                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                          : 'bg-slate-50 text-slate-600 border-slate-200'
                                      }`}
                                      title={(layout.created_via || 'dashboard') === 'api' ? 'Created via external API / SDK' : 'Created via SeatPilot dashboard'}
                                    >
                                      {(layout.created_via || 'dashboard') === 'api' ? 'API' : 'Dashboard'}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3">
                                    {layout.is_published ? (
                                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Published</span>
                                    ) : (
                                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">Draft</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-center font-semibold text-violet-600">{seatCount}</td>
                                  <td className="px-4 py-3 text-center font-semibold text-blue-600">{layout.booked_seats_count || 0}</td>
                                  <td className="px-4 py-3">
                                    {layout.event_name ? (
                                      <span className="text-sm text-green-700 font-medium">{layout.event_name}</span>
                                    ) : (
                                      <span className="text-xs text-gray-400">Not linked</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-500">
                                    {new Date(layout.created_at).toLocaleDateString()}
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex items-center justify-end gap-1">
                                      <Button size="sm" variant="ghost" onClick={() => navigate(`/builder/${layout.id}`)}>
                                        <Edit className="w-4 h-4" />
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant={layout.is_published ? "ghost" : "default"}
                                        className={layout.is_published ? '' : 'bg-green-600 hover:bg-green-700 text-white h-8'}
                                        disabled={publishing === layout.id}
                                        onClick={() => handlePublishToggle(layout)}
                                      >
                                        {publishing === layout.id ? (
                                          <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                        ) : layout.is_published ? (
                                          <EyeOff className="w-4 h-4" />
                                        ) : (
                                          <CheckCircle2 className="w-4 h-4" />
                                        )}
                                      </Button>
                                      <Button size="sm" variant="ghost" onClick={() => navigate(`/widget/${layout.id}`)} title="Preview">
                                        <Eye className="w-4 h-4" />
                                      </Button>
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                            <MoreVertical className="w-4 h-4" />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                          <DropdownMenuItem onClick={() => setRenameDialog({ open: true, layout, name: layout.name })}>
                                            <FileEdit className="w-4 h-4 mr-2" /> Rename
                                          </DropdownMenuItem>
                                          <DropdownMenuItem 
                                            disabled={duplicating === layout.id}
                                            onClick={async () => {
                                              setDuplicating(layout.id);
                                              try {
                                                const res = await axios.post(`${getAPI()}/admin/layouts/${layout.id}/duplicate`, {}, { withCredentials: true });
                                                toast.success("Layout duplicated");
                                                setLayouts(prev => [res.data, ...prev]);
                                              } catch (error) {
                                                toast.error("Failed to duplicate");
                                              } finally {
                                                setDuplicating(null);
                                              }
                                            }}
                                          >
                                            <Copy className="w-4 h-4 mr-2" /> Duplicate
                                          </DropdownMenuItem>
                                          <DropdownMenuSeparator />
                                          <DropdownMenuItem 
                                            onClick={() => navigate(`/layout-info/${layout.id}`)}
                                          >
                                            <Info className="w-4 h-4 mr-2" /> Info
                                          </DropdownMenuItem>
                                          <DropdownMenuSeparator />
                                          <DropdownMenuItem 
                                            className="text-red-600"
                                            onClick={async () => {
                                              if (window.confirm(`Delete "${layout.name}"?`)) {
                                                try {
                                                  await axios.delete(`${getAPI()}/admin/layouts/${layout.id}`, { withCredentials: true });
                                                  toast.success("Deleted");
                                                  setLayouts(layouts.filter(l => l.id !== layout.id));
                                                } catch { toast.error("Failed"); }
                                              }
                                            }}
                                          >
                                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                    <PaginationControls />
                    </div>
                  );
                })()}
              </>
            )}
            
            {/* Rename Dialog */}
            <Dialog open={renameDialog.open} onOpenChange={(open) => setRenameDialog({ ...renameDialog, open })}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Rename Layout</DialogTitle>
                  <DialogDescription>Enter a new name for this layout</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <Input
                    value={renameDialog.name}
                    onChange={(e) => setRenameDialog({ ...renameDialog, name: e.target.value })}
                    placeholder="Layout name"
                  />
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setRenameDialog({ open: false, layout: null, name: '' })}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={async () => {
                        try {
                          await axios.patch(`${getAPI()}/admin/layouts/${renameDialog.layout.id}`, { name: renameDialog.name }, { withCredentials: true });
                          toast.success("Layout renamed");
                          setLayouts(layouts.map(l => l.id === renameDialog.layout.id ? { ...l, name: renameDialog.name } : l));
                          setRenameDialog({ open: false, layout: null, name: '' });
                        } catch (error) {
                          toast.error("Failed to rename layout");
                        }
                      }}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Layout Info - now uses dedicated page at /layout-info/:layoutId */}
          </div>
        )}

        {/* Organisers Tab - Support View for Ticketing Companies */}
        {activeTab === "organisers" && (
          <div className="space-y-6">
            {/* Header with Search */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Headphones className="w-6 h-6 text-violet-600" />
                  Organiser Support
                </h1>
                <p className="text-gray-600">View and manage your organisers&apos; layouts for support purposes</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right hidden sm:block">
                  <p className="text-xs text-gray-500">Your Customer ID</p>
                  <code className="text-sm font-mono bg-violet-100 text-violet-700 px-2 py-1 rounded font-bold">{company?.customer_id || 'N/A'}</code>
                </div>
                <div className="relative w-full sm:w-64">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Search organisers..."
                    value={organiserSearchTerm}
                    onChange={(e) => setOrganiserSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-violet-100 rounded-lg">
                    <Users className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {[...new Set(layouts.filter(l => l.organiser_id).map(l => l.organiser_id))].length}
                    </p>
                    <p className="text-sm text-gray-500">Organisers</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Layout className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{layouts.length}</p>
                    <p className="text-sm text-gray-500">Total Layouts</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Calendar className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {[...new Set(layouts.filter(l => l.event_id).map(l => l.event_id))].length}
                    </p>
                    <p className="text-sm text-gray-500">Events</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <BarChart3 className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{monthlyUsage.seats_booked}</p>
                    <p className="text-sm text-gray-500">Seats/Month</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Info Banner */}
            <Card className="p-4 bg-gradient-to-r from-violet-50 to-indigo-50 border-violet-200">
              <div className="flex gap-3">
                <Headphones className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-violet-800">Support Your Organisers</p>
                  <p className="text-sm text-violet-700 mt-1">
                    Click on any organiser to expand and view their layouts. You can view layout details, 
                    edit configurations, or help troubleshoot issues for your platform users.
                  </p>
                </div>
              </div>
            </Card>

            {/* Organisers List with Expandable Layouts */}
            <Card className="p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-violet-600" />
                Your Organisers
              </h3>
              
              {(() => {
                // Group layouts by organiser_id
                const organiserMap = {};
                layouts.forEach(layout => {
                  if (layout.organiser_id) {
                    if (!organiserMap[layout.organiser_id]) {
                      organiserMap[layout.organiser_id] = {
                        organiser_id: layout.organiser_id,
                        organiser_name: layout.organiser_name || 'Unknown',
                        layouts: [],
                        events: new Set()
                      };
                    }
                    organiserMap[layout.organiser_id].layouts.push(layout);
                    if (layout.event_id) {
                      organiserMap[layout.organiser_id].events.add(layout.event_id);
                    }
                  }
                });
                
                // Filter by search term
                let filteredOrganisers = Object.values(organiserMap);
                if (organiserSearchTerm) {
                  const term = organiserSearchTerm.toLowerCase();
                  filteredOrganisers = filteredOrganisers.filter(org =>
                    org.organiser_name.toLowerCase().includes(term) ||
                    org.organiser_id.toLowerCase().includes(term) ||
                    org.layouts.some(l => l.name?.toLowerCase().includes(term) || l.event_name?.toLowerCase().includes(term))
                  );
                }

                if (filteredOrganisers.length === 0) {
                  return (
                    <div className="text-center py-12 border-2 border-dashed rounded-lg">
                      <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500 mb-2">
                        {organiserSearchTerm ? "No organisers match your search" : "No organisers yet"}
                      </p>
                      <p className="text-sm text-gray-400 mb-4">
                        {organiserSearchTerm 
                          ? "Try a different search term" 
                          : "Organisers will appear here when they create layouts via your API"}
                      </p>
                      {!organiserSearchTerm && (
                        <Button variant="outline" onClick={() => handleTabChange('integration')}>
                          <Code className="w-4 h-4 mr-2" /> View API Documentation
                        </Button>
                      )}
                    </div>
                  );
                }

                return (
                  <div className="space-y-3">
                    {filteredOrganisers.map((org) => {
                      const isExpanded = expandedOrganisers[org.organiser_id];
                      const firstLayout = org.layouts.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))[0];
                      
                      // Calculate total seats for this organiser
                      const totalSeats = org.layouts.reduce((total, layout) => {
                        const seatCount = layout.canvas_objects?.reduce((count, obj) => {
                          const type = obj.type;
                          if (type === 'single_row' || type === 'row') return count + (obj.seats?.length || obj.properties?.seat_count || 0);
                          if (type === 'multiple_rows') return count + ((obj.properties?.row_count || 0) * (obj.properties?.seat_count || 0));
                          if (type === 'round_table' || type === 'rect_table') return count + (obj.seats?.length || obj.properties?.seats || 0);
                          if (type === 'ga_zone' || type === 'vip_pod') return count + (obj.properties?.capacity || 0);
                          if (type === 'seat') return count + 1;
                          if (type === 'bus_layout') return count + (obj.seats?.length || 0);
                          return count;
                        }, 0) || 0;
                        return total + seatCount;
                      }, 0);
                      
                      return (
                        <Card key={org.organiser_id} className={`border transition-all ${isExpanded ? 'border-violet-300 shadow-md' : 'hover:border-gray-300'}`}>
                          {/* Organiser Header - Clickable */}
                          <div 
                            className="p-4 cursor-pointer flex items-center justify-between"
                            onClick={() => setExpandedOrganisers(prev => ({
                              ...prev,
                              [org.organiser_id]: !prev[org.organiser_id]
                            }))}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center text-white font-bold text-lg">
                                {org.organiser_name?.charAt(0) || 'O'}
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900">{org.organiser_name}</p>
                                <p className="text-xs text-gray-500 font-mono">{org.organiser_id}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-6">
                              <div className="hidden sm:flex items-center gap-4 text-sm">
                                <div className="text-center">
                                  <p className="font-bold text-blue-600">{org.layouts.length}</p>
                                  <p className="text-xs text-gray-500">Layouts</p>
                                </div>
                                <div className="text-center">
                                  <p className="font-bold text-green-600">{org.events.size}</p>
                                  <p className="text-xs text-gray-500">Events</p>
                                </div>
                                <div className="text-center">
                                  <p className="font-bold text-violet-600">{totalSeats}</p>
                                  <p className="text-xs text-gray-500">Seats</p>
                                </div>
                              </div>
                              <div className="text-xs text-gray-400 hidden md:block">
                                Since {firstLayout?.created_at ? new Date(firstLayout.created_at).toLocaleDateString() : 'N/A'}
                              </div>
                              {isExpanded ? (
                                <ChevronDown className="w-5 h-5 text-gray-400" />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                          </div>
                          
                          {/* Expanded Layouts View */}
                          {isExpanded && (
                            <div className="border-t bg-gray-50 p-4">
                              <div className="flex items-center justify-between mb-3">
                                <p className="text-sm font-medium text-gray-700">
                                  Layouts by {org.organiser_name}
                                </p>
                                <span className="text-xs text-gray-500">
                                  {org.layouts.length} layout{org.layouts.length !== 1 ? 's' : ''}
                                </span>
                              </div>
                              
                              <div className="space-y-2">
                                {org.layouts.map(layout => {
                                  // Calculate seats for this layout
                                  const seatCount = layout.canvas_objects?.reduce((count, obj) => {
                                    const type = obj.type;
                                    if (type === 'single_row' || type === 'row') return count + (obj.seats?.length || obj.properties?.seat_count || 0);
                                    if (type === 'multiple_rows') return count + ((obj.properties?.row_count || 0) * (obj.properties?.seat_count || 0));
                                    if (type === 'round_table' || type === 'rect_table') return count + (obj.seats?.length || obj.properties?.seats || 0);
                                    if (type === 'ga_zone' || type === 'vip_pod') return count + (obj.properties?.capacity || 0);
                                    if (type === 'seat') return count + 1;
                                    if (type === 'bus_layout') return count + (obj.seats?.length || 0);
                                    return count;
                                  }, 0) || 0;
                                  
                                  return (
                                    <div key={layout.id} className="bg-white p-3 rounded-lg border flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                      <div className="flex items-center gap-3">
                                        <Layout className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                        <div>
                                          <p className="font-medium text-gray-900">{layout.name}</p>
                                          <div className="flex flex-wrap items-center gap-2 mt-1">
                                            {layout.event_name && (
                                              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded flex items-center gap-1">
                                                <Calendar className="w-3 h-3" />
                                                {layout.event_name}
                                              </span>
                                            )}
                                            {layout.venue_name && (
                                              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded flex items-center gap-1">
                                                <MapPin className="w-3 h-3" />
                                                {layout.venue_name}
                                              </span>
                                            )}
                                            <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-xs rounded">
                                              {seatCount} seats
                                            </span>
                                            <span className="text-xs text-gray-400">
                                              Created: {layout.created_at ? new Date(layout.created_at).toLocaleDateString() : 'N/A'}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2 ml-7 sm:ml-0">
                                        <Button 
                                          size="sm" 
                                          variant="outline"
                                          onClick={() => navigate(`/builder/${layout.id}`)}
                                          title="Edit Layout"
                                        >
                                          <Edit className="w-3 h-3 mr-1" /> Edit
                                        </Button>
                                        <Button 
                                          size="sm" 
                                          variant="outline"
                                          onClick={() => navigate(`/widget/${layout.id}`)}
                                          title="Preview Widget"
                                        >
                                          <Eye className="w-3 h-3 mr-1" /> View
                                        </Button>
                                        <Button 
                                          size="sm" 
                                          variant="outline"
                                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                          onClick={async () => {
                                            if (window.confirm(`Delete layout "${layout.name}"? This cannot be undone.`)) {
                                              try {
                                                await axios.delete(`${getAPI()}/admin/layouts/${layout.id}`, { withCredentials: true });
                                                toast.success("Layout deleted");
                                                fetchData();
                                              } catch (error) {
                                                toast.error(error.response?.data?.detail || "Failed to delete layout");
                                              }
                                            }
                                          }}
                                          title="Delete Layout"
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                );
              })()}
            </Card>

            {/* White Label Subscription - Only show if NOT active OR if cancelling */}
            {(!company?.white_label || company?.white_label_cancelling) && (
              <Card className={`p-6 ${company?.white_label_cancelling ? 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300' : 'bg-gradient-to-r from-violet-50 to-indigo-50 border-violet-200'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-violet-600" />
                      {company?.white_label_cancelling ? 'White Label Cancelling' : 'Upgrade to White Label'}
                    </h3>
                    
                    {company?.white_label_cancelling ? (
                      <>
                        {/* Cancelling State - Show timer and reactivate */}
                        <p className="text-sm text-gray-600 mt-1">
                          Your white-label subscription is scheduled to cancel.
                        </p>
                        <div className="mt-3 p-4 bg-amber-100 border border-amber-300 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0">
                              <Clock className="w-8 h-8 text-amber-600" />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-amber-800">
                                Cancels on {company?.white_label_cancel_at ? new Date(company.white_label_cancel_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'end of billing period'}
                              </p>
                              <p className="text-xs text-amber-700 mt-1">
                                After this date, &quot;Powered by SeatPilot&quot; branding will be visible again
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="mt-4">
                          <Button 
                            className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                            onClick={() => setShowReactivateConfirm(true)}
                          >
                            <RefreshCw className="w-4 h-4 mr-2" /> Reactivate White Label
                          </Button>
                          <p className="text-xs text-gray-500 text-center mt-2">Continue at £20/month</p>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Not subscribed - Sell the feature */}
                        <p className="text-sm text-gray-600 mt-1">
                          Remove &quot;Powered by SeatPilot&quot; branding for a professional, branded experience.
                        </p>
                        
                        <div className="mt-4 space-y-2">
                          <div className="flex items-center gap-2 text-sm text-gray-700">
                            <Check className="w-4 h-4 text-green-600" />
                            <span>Hide &quot;Powered by SeatPilot&quot; badge on all widgets</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-700">
                            <Check className="w-4 h-4 text-green-600" />
                            <span>Complete brand consistency for your customers</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-700">
                            <Check className="w-4 h-4 text-green-600" />
                            <span>Cancel anytime - no long-term commitment</span>
                          </div>
                        </div>
                        
                        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-sm text-amber-700 font-medium flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" /> Currently Showing
                          </p>
                          <p className="text-xs text-amber-600 mt-1">
                            &quot;Powered by SeatPilot&quot; badge is visible on your checkout widgets
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                  
                  {!company?.white_label_cancelling && (
                    <div className="text-right ml-4">
                      <p className="text-2xl font-bold text-gray-900">£20</p>
                      <p className="text-sm text-gray-500">/month</p>
                    </div>
                  )}
                </div>
                
                {!company?.white_label_cancelling && (
                  <div className="mt-4">
                    <Button 
                      className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700"
                      onClick={() => {
                        if (!paymentMethod) {
                          // Redirect to billing tab to add payment method
                          handleTabChange("billing");
                          setShowAddCard(true);
                          toast.info("Please add a payment method to subscribe to White Label");
                          return;
                        }
                        // Show confirmation dialog
                        setShowWhiteLabelConfirm(true);
                      }}
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      {paymentMethod ? "Subscribe to White Label" : "Add Payment Method & Subscribe"}
                    </Button>
                    
                    {!paymentMethod && (
                      <p className="text-xs text-gray-500 text-center mt-2">
                        You'll be redirected to add a payment method first
                      </p>
                    )}
                  </div>
                )}
              </Card>
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === "analytics" && (
          <AnalyticsTab />
        )}

        {/* API Keys Tab */}
        {activeTab === "api-keys" && (
          <div className="overflow-x-hidden">
            <h1 className="text-2xl font-bold text-gray-900 mb-6">API Keys</h1>
            
            {/* API Key Warnings */}
            {apiKeyWarnings && apiKeyWarnings.map((warning, idx) => (
              <Card key={idx} className={`p-4 mb-6 ${
                warning.type === 'keys_revoked' ? 'border-red-200 bg-red-50' :
                warning.type === 'needs_regeneration' ? 'border-amber-200 bg-amber-50' :
                warning.type === 'will_be_revoked' ? 'border-orange-200 bg-orange-50' :
                'border-blue-200 bg-blue-50'
              }`}>
                <div className="flex gap-3">
                  <AlertCircle className={`w-5 h-5 flex-shrink-0 ${
                    warning.type === 'keys_revoked' ? 'text-red-600' :
                    warning.type === 'needs_regeneration' ? 'text-amber-600' :
                    warning.type === 'will_be_revoked' ? 'text-orange-600' :
                    'text-blue-600'
                  }`} />
                  <div>
                    <p className={`font-medium ${
                      warning.type === 'keys_revoked' ? 'text-red-800' :
                      warning.type === 'needs_regeneration' ? 'text-amber-800' :
                      warning.type === 'will_be_revoked' ? 'text-orange-800' :
                      'text-blue-800'
                    }`}>
                      {warning.type === 'keys_revoked' && '⚠️ API Keys Revoked'}
                      {warning.type === 'needs_regeneration' && '🔄 New API Keys Required'}
                      {warning.type === 'will_be_revoked' && '⏳ API Keys Will Be Revoked'}
                    </p>
                    <p className={`text-sm ${
                      warning.type === 'keys_revoked' ? 'text-red-700' :
                      warning.type === 'needs_regeneration' ? 'text-amber-700' :
                      warning.type === 'will_be_revoked' ? 'text-orange-700' :
                      'text-blue-700'
                    }`}>
                      {warning.message}
                    </p>
                    {warning.type === 'needs_regeneration' && (
                      <p className="text-sm text-amber-700 mt-2 font-medium">
                        👉 Click &quot;Generate New Key&quot; below and update your website/integration with the new key.
                      </p>
                    )}
                  </div>
                </div>
              </Card>
            ))}
            
            {/* No active subscription warning */}
            {(!subscription || subscription.status === 'cancelled') && (
              <Card className="p-4 mb-6 border-red-200 bg-red-50">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-red-800">No Active Subscription</p>
                    <p className="text-sm text-red-700">
                      You need an active subscription to generate and use API keys. 
                      <Button variant="link" className="text-red-800 underline p-0 h-auto ml-1" onClick={() => handleTabChange('billing')}>
                        Activate a plan
                      </Button>
                    </p>
                  </div>
                </div>
              </Card>
            )}
            
            {user && !user.email_verified && (
              <Card className="p-4 mb-6 border-amber-200 bg-amber-50">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-amber-800">Email verification required</p>
                    <p className="text-sm text-amber-700">Please verify your email address before generating an API key.</p>
                  </div>
                </div>
              </Card>
            )}

            <Card className="p-6 mb-6">
              <MultiAPIKeys
                company={company}
                subscription={subscription}
                user={user}
                getAPI={getAPI}
                fetchCompanyData={fetchCompanyData}
                fetchData={fetchData}
                handleTabChange={handleTabChange}
              />
            </Card>

            {/* Developer Documentation Section */}
            <Card className="p-6 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-violet-600" />
                    Developer Documentation
                  </h3>
                  <p className="text-gray-600">
                    Complete integration guide - learn how to embed widgets, set pricing, use APIs, and handle webhooks.
                  </p>
                </div>
                <a
                  href="/developer-docs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-lg font-medium hover:from-violet-700 hover:to-indigo-700 transition-all flex-shrink-0"
                >
                  <Book className="w-5 h-5" />
                  View Full Docs
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
              
              {/* Quick Links */}
              <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <a href="/developer-docs?section=quickstart" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-gray-600 hover:text-violet-600">
                  <Zap className="w-4 h-4" /> Quick Start
                </a>
                <a href="/developer-docs?section=pricing" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-gray-600 hover:text-violet-600">
                  <DollarSign className="w-4 h-4" /> Pricing Setup
                </a>
                <a href="/developer-docs?section=widget" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-gray-600 hover:text-violet-600">
                  <Layout className="w-4 h-4" /> Widget Embed
                </a>
                <a href="/developer-docs?section=api" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-gray-600 hover:text-violet-600">
                  <Terminal className="w-4 h-4" /> API Reference
                </a>
              </div>
            </Card>

            {/* Key API Endpoints */}
            <Card className="p-6 mb-6" data-testid="api-keys-endpoints-card">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Terminal className="w-5 h-5 text-violet-600" />
                    Key API Endpoints
                  </h3>
                  <p className="text-gray-600">
                    Core endpoints for layouts, bookings, and seat management. All require <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono">x-api-key</code> header.
                  </p>
                </div>
                <button
                  onClick={() => window.open(`${getBaseUrl()}/api/docs`, '_blank')}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-lg font-medium hover:from-violet-700 hover:to-indigo-700 transition-all flex-shrink-0"
                >
                  <BookOpen className="w-5 h-5" />
                  Full API Docs
                  <ExternalLink className="w-4 h-4" />
                </button>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { method: "POST", path: "/api/external/layouts", label: "Create Layout" },
                  { method: "POST", path: "/api/external/layouts/{id}/link-event", label: "Link Event" },
                  { method: "POST", path: "/api/external/layouts/{id}/ticket-types", label: "Ticket Types" },
                  { method: "GET", path: "/api/external/layouts/{id}/availability", label: "Availability" },
                ].map(({ method, path, label }) => (
                  <button key={path} onClick={() => { navigator.clipboard.writeText(path); toast.success('Copied'); }} className="flex items-center gap-2 text-sm text-gray-600 hover:text-violet-600 group text-left">
                    <span className={`px-1.5 py-0.5 text-xs font-semibold rounded ${method === 'GET' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{method}</span>
                    <span className="truncate">{label}</span>
                    <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                  </button>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Integration Tab */}
        {activeTab === "integration" && (
          <div className="overflow-x-hidden">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Integration Guide</h1>
            <p className="text-xs sm:text-sm md:text-base text-gray-600 mb-4 sm:mb-6">Integrate SeatPilot into your event ticketing platform - like Stripe Connect for seating</p>
            
            {/* How It Works */}
            <Card className="p-3 sm:p-6 mb-4 sm:mb-8 bg-gradient-to-r from-violet-50 to-indigo-50 border-violet-200">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-violet-600 shrink-0" />
                <span>How SeatPilot Works</span>
              </h3>
              <p className="text-xs sm:text-sm md:text-base text-gray-700 mb-3 sm:mb-4 leading-relaxed">
                SeatPilot is designed to integrate seamlessly into your existing event ticketing platform. 
                Your platform&apos;s organizers can create and manage seating layouts directly from your website 
                using our embeddable tools - they never need to visit SeatPilot directly.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-3 sm:mt-4">
                <div className="bg-white rounded-lg p-3 sm:p-4">
                  <h4 className="font-medium text-gray-900 mb-2 text-sm sm:text-base">For Your Platform</h4>
                  <ul className="text-xs sm:text-sm text-gray-600 space-y-1">
                    <li>• Embed our layout builder in your dashboard</li>
                    <li>• Embed seat selection widget on event pages</li>
                    <li>• Use your own payment gateway</li>
                    <li>• Full white-label support</li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-3 sm:p-4">
                  <h4 className="font-medium text-gray-900 mb-2 text-sm sm:text-base">For Your Organizers</h4>
                  <ul className="text-xs sm:text-sm text-gray-600 space-y-1">
                    <li>• Create layouts for their events</li>
                    <li>• Each organizer sees only their layouts</li>
                    <li>• Manage seat pricing and categories</li>
                    <li>• Track bookings in real-time</li>
                  </ul>
                </div>
              </div>
            </Card>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 mb-4 sm:mb-8">
              <Card className="p-3 sm:p-6 border-2 border-violet-200 bg-violet-50">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-violet-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                  <span className="text-violet-600 font-bold">1</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Get API Key</h3>
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">Generate your API key to authenticate all API requests from your platform.</p>
              </Card>
              
              <Card className="p-3 sm:p-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                  <span className="text-gray-600 font-bold">2</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Embed Builder</h3>
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">Embed our layout builder so your organizers can create seating charts from your platform.</p>
              </Card>
              
              <Card className="p-3 sm:p-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                  <span className="text-gray-600 font-bold">3</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Embed Widget</h3>
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">Add the seat selection widget to your event checkout pages.</p>
              </Card>
            </div>
            
            {/* Embed Layout Builder */}
            <Card className="p-3 sm:p-6 mb-4 sm:mb-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Embed the Layout Builder</h3>
              <p className="text-xs sm:text-sm md:text-base text-gray-600 mb-3 sm:mb-4">Let your organizers create seating layouts directly from your platform:</p>
              
              <div className="bg-gray-900 rounded-lg p-2 sm:p-4 overflow-x-auto -mx-1 sm:mx-0 max-w-full">
                <pre className="text-[10px] sm:text-sm text-green-400 whitespace-pre" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
{`<!-- SeatPilot Layout Builder -->
<div id="seatpilot-builder"></div>
<script src="${PRODUCTION_URL}/builder.js"></script>
<script>
  SeatPilot.builder({
    apiKey: '${company?.api_key ? company.api_key.substring(0, 8) + '...' : 'YOUR_API_KEY'}',
    container: '#seatpilot-builder',
    userId: 'ORGANIZER_USER_ID',  // Your platform's user ID
    eventId: 'EVENT_ID',          // Optional: link to specific event
    brand: 'YourBrand',           // White-label walkthrough
    onSave: function(layout) {
      // Layout saved - store layout.id in your database
      console.log('Layout created:', layout.id);
    }
  });
</script>`}
                </pre>
              </div>
            </Card>
            
            <Card className="p-3 sm:p-6 mb-4 sm:mb-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Embed the Seat Selection Widget</h3>
              <p className="text-xs sm:text-sm md:text-base text-gray-600 mb-3 sm:mb-4">Add this to your event checkout page for customers to select seats:</p>
              
              <div className="bg-gray-900 rounded-lg p-2 sm:p-4 overflow-x-auto -mx-1 sm:mx-0 max-w-full">
                <pre className="text-[10px] sm:text-sm text-green-400 whitespace-pre" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
{`<!-- SeatPilot Widget -->
<div id="seatpilot-widget"></div>
<script src="${PRODUCTION_URL}/widget.js"></script>
<script>
  SeatPilot.init({
    apiKey: '${company?.api_key ? company.api_key.substring(0, 8) + '...' : 'YOUR_API_KEY'}',
    layoutId: 'LAYOUT_ID',
    eventId: 'EVENT_ID',
    container: '#seatpilot-widget',
    onSeatSelect: function(seats) {
      // Update your checkout with selected seats
      updateCart(seats);
    },
    onCheckout: function(booking) {
      // Process payment with YOUR payment gateway
      processPaymentWithYourGateway(booking);
    }
  });
</script>`}
                </pre>
              </div>
              
              <Button variant="outline" className="mt-3 sm:mt-4 w-full sm:w-auto" onClick={() => {
                navigator.clipboard.writeText(`<div id="seatpilot-widget"></div>
<script src="${PRODUCTION_URL}/widget.js"></script>
<script>
  SeatPilot.init({
    apiKey: '${company?.api_key || 'YOUR_API_KEY'}',
    layoutId: 'YOUR_LAYOUT_ID',
    eventId: 'YOUR_EVENT_ID',
    container: '#seatpilot-widget'
  });
</script>`);
                toast.success('Code copied to clipboard');
              }}>
                <Copy className="w-4 h-4 mr-2" /> Copy Code
              </Button>
            </Card>
            
            <Card className="p-3 sm:p-6 mb-4 sm:mb-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Key API Endpoints</h3>
              <div className="space-y-3 sm:space-y-4">
                <div className="border rounded-lg p-3 sm:p-4">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">POST</span>
                    <code className="text-xs sm:text-sm break-all">/api/layouts</code>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">Create a new layout (pass organizer_user_id to track ownership)</p>
                </div>
                
                <div className="border rounded-lg p-3 sm:p-4">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">GET</span>
                    <code className="text-xs sm:text-sm break-all">/api/layouts?user_id=ORGANIZER_ID</code>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">Get layouts for a specific organizer</p>
                </div>
                
                <div className="border rounded-lg p-3 sm:p-4">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">POST</span>
                    <code className="text-xs sm:text-sm break-all">/api/bookings</code>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">Create a booking after successful payment</p>
                </div>
                
                <div className="border rounded-lg p-3 sm:p-4">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="px-2 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded">POST</span>
                    <code className="text-xs sm:text-sm break-all">/api/seats/hold</code>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">Hold seats during checkout (auto-expires after 10 mins)</p>
                </div>
              </div>
              
              <Button variant="outline" className="mt-3 sm:mt-4 w-full sm:w-auto" onClick={() => window.open(`${getBaseUrl()}/api/docs`, '_blank')}>
                <BookOpen className="w-4 h-4 mr-2" /> Full API Documentation
              </Button>
            </Card>
            
            <Card className="p-6 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
              <div className="flex gap-4">
                <CreditCard className="w-8 h-8 text-amber-600 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">You Handle Payments</h3>
                  <p className="text-gray-600 mb-4">
                    SeatPilot manages seating - you keep 100% control of payments through your own gateway (Stripe, PayPal, etc.).
                  </p>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
                    <li>Customer selects seats via SeatPilot widget</li>
                    <li>Your platform receives selected seats via callback</li>
                    <li>You process payment with your payment gateway</li>
                    <li>On success, call our API to confirm the booking</li>
                    <li>Seats are marked as sold - done!</li>
                  </ol>
                </div>
              </div>
            </Card>
            
            {/* White Label Option */}
            <Card className="p-6 mt-6 bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200">
              <div className="flex flex-col md:flex-row md:items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-r from-violet-600 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">White Label Integration</h3>
                  <p className="text-gray-600 mb-4">
                    Remove &quot;Powered by SeatPilot&quot; branding from all widgets and checkouts for a completely seamless integration with your platform.
                  </p>
                  
                  {company?.white_label ? (
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 px-3 py-2 bg-green-100 text-green-700 rounded-lg">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">White Label Active</span>
                      </div>
                      {company?.white_label_cancelling && (
                        <span className="text-sm text-amber-600">
                          Cancelling on {company?.white_label_cancel_at ? new Date(company.white_label_cancel_at).toLocaleDateString() : 'end of billing period'}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button
                        onClick={async () => {
                          if (window.confirm("Subscribe to White Label for £20/month?\n\n• Remove 'Powered by SeatPilot' branding\n• Cancel anytime\n• Billed monthly")) {
                            try {
                              const res = await axios.post(`${getAPI()}/subscriptions/white-label/subscribe`, {
                                return_url: window.location.href
                              }, { withCredentials: true });
                              if (res.data.checkout_url) {
                                window.location.href = res.data.checkout_url;
                              } else {
                                toast.success('White label activated!');
                                fetchCompanyData();
                              }
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Failed to subscribe');
                            }
                          }
                        }}
                        className="bg-gradient-to-r from-violet-600 to-purple-600 text-white"
                      >
                        Subscribe - £20/month
                      </Button>
                      <p className="text-sm text-gray-500 self-center">Cancel anytime</p>
                    </div>
                  )}
                </div>
              </div>
            </Card>
            
            {/* Developer Documentation Section */}
            <Card className="p-6 mb-6 mt-8 bg-gradient-to-r from-indigo-50 to-violet-50 border-indigo-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-indigo-600" />
                    Complete Developer Guide
                  </h3>
                  <p className="text-gray-600">
                    Everything you need to integrate SeatPilot - pricing setup, widget embed, APIs, webhooks, and code examples.
                  </p>
                </div>
                <a
                  href="/developer-docs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-lg font-medium hover:from-indigo-700 hover:to-violet-700 transition-all flex-shrink-0"
                >
                  <Book className="w-5 h-5" />
                  Open Documentation
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
              
              {/* Doc Sections Grid */}
              <div className="mt-4 pt-4 border-t border-indigo-200 grid grid-cols-2 md:grid-cols-5 gap-3">
                <a href="/developer-docs?section=quickstart" target="_blank" rel="noopener noreferrer" className="p-3 bg-white rounded-lg border border-gray-200 hover:border-indigo-300 hover:shadow-sm transition-all text-center">
                  <Zap className="w-5 h-5 text-indigo-600 mx-auto mb-1" />
                  <span className="text-xs font-medium text-gray-700">Quick Start</span>
                </a>
                <a href="/developer-docs?section=pricing" target="_blank" rel="noopener noreferrer" className="p-3 bg-white rounded-lg border border-gray-200 hover:border-indigo-300 hover:shadow-sm transition-all text-center">
                  <DollarSign className="w-5 h-5 text-indigo-600 mx-auto mb-1" />
                  <span className="text-xs font-medium text-gray-700">Pricing Setup</span>
                </a>
                <a href="/developer-docs?section=widget" target="_blank" rel="noopener noreferrer" className="p-3 bg-white rounded-lg border border-gray-200 hover:border-indigo-300 hover:shadow-sm transition-all text-center">
                  <Layout className="w-5 h-5 text-indigo-600 mx-auto mb-1" />
                  <span className="text-xs font-medium text-gray-700">Widget Embed</span>
                </a>
                <a href="/developer-docs?section=api" target="_blank" rel="noopener noreferrer" className="p-3 bg-white rounded-lg border border-gray-200 hover:border-indigo-300 hover:shadow-sm transition-all text-center">
                  <Terminal className="w-5 h-5 text-indigo-600 mx-auto mb-1" />
                  <span className="text-xs font-medium text-gray-700">API Reference</span>
                </a>
                <a href="/developer-docs?section=webhooks" target="_blank" rel="noopener noreferrer" className="p-3 bg-white rounded-lg border border-gray-200 hover:border-indigo-300 hover:shadow-sm transition-all text-center">
                  <Bell className="w-5 h-5 text-indigo-600 mx-auto mb-1" />
                  <span className="text-xs font-medium text-gray-700">Webhooks</span>
                </a>
              </div>
            </Card>
          </div>
        )}

        {/* Billing Tab */}
        {activeTab === "billing" && (
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-6">Billing & Subscription</h1>
            
            {/* No Active Subscription - Prominent Call to Action */}
            {(!subscription || subscription.status === 'cancelled' || subscription.status === 'expired') && (
              <Card className="p-6 mb-6 bg-gradient-to-r from-red-500 to-orange-500 text-white border-0 shadow-lg">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-bold text-xl">Activate Your Plan</p>
                      <p className="text-white/90 mt-1">Choose a plan to unlock all features:</p>
                      <ul className="text-sm text-white/80 mt-2 space-y-1">
                        <li>✓ Create unlimited seating layouts</li>
                        <li>✓ Accept bookings from customers</li>
                        <li>✓ Generate API keys for integration</li>
                        <li>✓ Access analytics and reports</li>
                      </ul>
                    </div>
                  </div>
                  <Button
                    onClick={() => {
                      fetchAvailablePlans();
                      setShowChangePlan(true);
                    }}
                    className="bg-white text-red-600 hover:bg-white/90 font-bold px-8 py-3 text-lg h-auto"
                  >
                    Choose a Plan
                  </Button>
                </div>
              </Card>
            )}
            
            {/* Scheduled Cancellation Warning */}
            {subscription?.status === 'cancelling' && subscription?.scheduled_cancel_at && (
              <Card className="p-4 mb-6 border-amber-300 bg-amber-50">
                <div className="flex gap-3">
                  <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-amber-800 text-lg">Subscription Ending Soon</p>
                    <p className="text-amber-700 mt-1">
                      Your subscription is scheduled to end on <strong>{new Date(subscription.scheduled_cancel_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>.
                    </p>
                    <ul className="text-sm text-amber-700 mt-2 list-disc list-inside">
                      <li>All API keys will be revoked on this date</li>
                      <li>Any active integrations will stop working</li>
                      <li>Existing layouts will become read-only</li>
                    </ul>
                    <div className="mt-3 flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="border-amber-500 text-amber-700 hover:bg-amber-100"
                        onClick={async () => {
                          try {
                            await axios.post(`${getAPI()}/subscriptions/reverse-cancellation`, {}, { withCredentials: true });
                            toast.success("Cancellation reversed! Your subscription will continue.");
                            fetchCompanyData();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || "Failed to reverse cancellation");
                          }
                        }}
                      >
                        Keep My Subscription
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            )}
            
            {/* Current Plan Card */}
            <Card className="p-6 mb-6">
              <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Current Plan</p>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {subscription?.plan_name || subscription?.plan_id?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'No Plan'}
                  </h2>
                  {getSubscriptionBadge()}
                </div>
                <div className="flex gap-2">
                  {/* Change/Activate Plan Button - always show for all plans */}
                  {(
                    <Button 
                      variant={(!subscription || subscription.status === 'cancelled') ? "default" : "outline"}
                      className={(!subscription || subscription.status === 'cancelled') ? "bg-violet-600 hover:bg-violet-700" : ""}
                      onClick={() => {
                        // For cancelled/no subscription, allow activation without payment method check first
                        if (!subscription || subscription.status === 'cancelled') {
                          fetchAvailablePlans();
                          setShowChangePlan(true);
                          return;
                        }
                        // Check if they have a payment method before showing plan change
                        if (!company?.default_payment_method && subscription?.plan_id !== 'free_trial') {
                          toast.error("Please add a payment method first to change your plan");
                          return;
                        }
                        fetchAvailablePlans();
                        setShowChangePlan(true);
                      }}
                    >
                      <CreditCard className="w-4 h-4 mr-1" /> 
                      {(!subscription || subscription.status === 'cancelled') ? 'Activate Plan' : 'Change Plan'}
                    </Button>
                  )}
                  
                  {/* Cancel Plan Button */}
                  {subscription && subscription.status !== 'cancelled' && subscription.status !== 'cancelling' && subscription.plan_id !== 'free_trial' && (
                    <Button 
                      variant="outline"
                      size="sm"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={async () => {
                        // Fetch retention config to determine steps
                        try {
                          const res = await axios.get(`${getAPI()}/subscriptions/retention-config/public`, { withCredentials: true });
                          const cfg = res.data;
                          setRetentionConfig(cfg);
                          
                          const isPAYG = subscription.plan_id === 'pay_as_you_go';
                          const discountAvailable = !isPAYG && cfg.discount_enabled && !cfg.discount_already_used;
                          const freeMonthAvailable = !isPAYG && cfg.free_month_enabled && !cfg.free_month_already_used;
                          
                          if (discountAvailable) setCancelStep(1);
                          else if (freeMonthAvailable) setCancelStep(2);
                          else setCancelStep(3);
                        } catch {
                          setCancelStep(subscription.plan_id === 'pay_as_you_go' ? 3 : 1);
                          setRetentionConfig(null);
                        }
                        setShowCancelPlan(true);
                      }}
                    >
                      Cancel Plan
                    </Button>
                  )}
                </div>
              </div>
              
              {subscription && (
                <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Show different info based on plan type */}
                  {subscription.plan_id === 'free_trial' ? (
                    <>
                      {(() => {
                        const exp = subscription.expires_at;
                        const daysLeft = exp ? Math.ceil((new Date(exp) - new Date()) / 86400000) : null;
                        const isExpired = subscription.is_expired || (daysLeft !== null && daysLeft <= 0);
                        // Trial card flips from amber → red once expired so
                        // the warning is unmissable.
                        const cardCls = isExpired
                          ? "p-4 bg-red-50 rounded-lg border border-red-200"
                          : "p-4 bg-amber-50 rounded-lg border border-amber-200";
                        const labelCls = isExpired ? "text-sm text-red-600" : "text-sm text-amber-600";
                        const valueCls = isExpired ? "font-semibold text-lg text-red-700" : "font-semibold text-lg text-amber-700";
                        return (
                          <div className={cardCls} data-testid="trial-expiry-card">
                            <p className={labelCls}>{isExpired ? "Trial Expired" : "Trial Expires"}</p>
                            <p className={valueCls}>{exp ? formatDate(exp) : '30 days from start'}</p>
                            {isExpired && (
                              <Button
                                size="sm"
                                className="mt-2 bg-red-600 hover:bg-red-700 text-white"
                                onClick={() => navigate('/pricing')}
                                data-testid="renew-plan-cta-trial"
                              >
                                Renew now
                              </Button>
                            )}
                          </div>
                        );
                      })()}
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">Seat Allowance</p>
                        <p className="font-semibold text-lg">
                          {subscription.monthly_seat_allowance === -1 || subscription.monthly_seat_allowance === null || subscription.monthly_seat_allowance === undefined 
                            ? 'Unlimited' 
                            : `${(subscription.monthly_seat_allowance || 500).toLocaleString()} seats`}
                        </p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">Events Allowed</p>
                        <p className="font-semibold text-lg">
                          {subscription.max_events === -1 || subscription.max_events === null || subscription.max_events === undefined 
                            ? 'Unlimited' 
                            : subscription.max_events || 1}
                        </p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">Layouts Allowed</p>
                        <p className="font-semibold text-lg">
                          {subscription.max_layouts === -1 || subscription.max_layouts === null || subscription.max_layouts === undefined 
                            ? 'Unlimited' 
                            : subscription.max_layouts || 1}
                        </p>
                      </div>
                    </>
                  ) : subscription.plan_id === 'pay_as_you_go' ? (
                    <>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">Price Per Seat</p>
                        <p className="font-semibold text-lg">{formatCurrency(subscription.per_seat_price || 0.50)}</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">No Monthly Fee</p>
                        <p className="font-semibold text-lg text-green-600">Pay As You Go</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">Events</p>
                        <p className="font-semibold text-lg">Unlimited</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">Layouts</p>
                        <p className="font-semibold text-lg">Unlimited</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">Monthly Price</p>
                        <p className="font-semibold text-lg">{formatCurrency(subscription.monthly_price || 0)}</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">Seat Allowance</p>
                        <p className="font-semibold text-lg">
                          {subscription.monthly_seat_allowance === -1 || subscription.monthly_seat_allowance === null || subscription.monthly_seat_allowance === undefined 
                            ? 'Unlimited' 
                            : (subscription.monthly_seat_allowance?.toLocaleString() || 'N/A')}
                        </p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">Overage Rate</p>
                        <p className="font-semibold text-lg">{formatCurrency(subscription.overage_per_seat || 0)}/seat</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">{subscription.expires_at ? (subscription.is_expired ? 'Expired' : 'Renews') : 'Started'}</p>
                        {(() => {
                          // Highlight expiry date in red when the plan is past
                          // its expires_at OR within 30 days of expiry — gives
                          // the customer an immediate visual cue to renew.
                          const exp = subscription.expires_at;
                          if (!exp) return <p className="font-semibold text-lg">{formatDate(subscription.started_at)}</p>;
                          const daysLeft = Math.ceil((new Date(exp) - new Date()) / 86400000);
                          const cls = (daysLeft <= 0 || subscription.is_expired)
                            ? "font-semibold text-lg text-red-600"
                            : (daysLeft <= 30 ? "font-semibold text-lg text-amber-600" : "font-semibold text-lg");
                          return <p className={cls} data-testid="billing-expiry-date">{formatDate(exp)}</p>;
                        })()}
                      </div>
                    </>
                  )}
                </div>
              )}
              
              {subscription?.is_custom && (
                <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                  <div className="flex gap-2">
                    <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-purple-800">Custom Plan</p>
                      <p className="text-sm text-purple-700">You have a custom plan configured by our team. Contact support to make changes.</p>
                    </div>
                  </div>
                </div>
              )}
            </Card>

            {/* Auto-renew toggle — customers can enable/disable automatic
                renewal on their next expiry. Requires an active payment
                method to be enabled; otherwise the toggle deep-links to
                the Add-Card flow so we can collect the card BEFORE
                auto-renewal is activated. */}
            {subscription && subscription.plan_id !== 'free_trial' && subscription.status !== 'cancelled' && (
              <Card className="p-6 mb-6" data-testid="billing-auto-renew-card">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center shrink-0">
                      <CreditCard className="w-5 h-5 text-violet-600" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">Auto-renewal</h3>
                      <p className="text-sm text-gray-600">
                        {subscription.auto_renew_enabled
                          ? `Your plan will renew automatically on ${subscription.expires_at ? new Date(subscription.expires_at).toLocaleDateString('en-GB', {day:'numeric', month:'short', year:'numeric'}) : 'the next billing date'}.`
                          : 'When ON, we will charge your saved card on the expiry date so your service never pauses.'}
                      </p>
                      {!subscription.has_payment_method && (
                        <p className="text-xs text-amber-600 mt-1 font-medium" data-testid="billing-auto-renew-no-card">
                          No payment method on file — add a card first to enable auto-renew.
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`text-sm font-medium ${subscription.auto_renew_enabled ? 'text-emerald-600' : 'text-gray-400'}`}>
                      {subscription.auto_renew_enabled ? 'ON' : 'OFF'}
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={subscription.auto_renew_enabled ? 'true' : 'false'}
                      data-testid="billing-auto-renew-toggle"
                      onClick={async () => {
                        const turningOn = !subscription.auto_renew_enabled;
                        if (turningOn) {
                          if (!subscription.has_payment_method) {
                            toast.error("Please add a payment method before enabling auto-renew.");
                            setShowAddCard(true);
                            return;
                          }
                          // Require explicit T&C acceptance for auto-renew ON
                          setAutoRenewTermsAccepted(false);
                          setShowAutoRenewTerms(true);
                          return;
                        }
                        // Turning OFF — show a re-confirmation dialog
                        // because we'll cancel the Stripe recurring
                        // charge at period end (cancel_at_period_end=true).
                        setShowAutoRenewOffConfirm(true);
                      }}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 ${subscription.auto_renew_enabled ? 'bg-emerald-500' : 'bg-gray-300'}`}
                    >
                      <span
                        aria-hidden="true"
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${subscription.auto_renew_enabled ? 'translate-x-5' : 'translate-x-0'}`}
                      />
                    </button>
                  </div>
                </div>
              </Card>
            )}

            {/* Usage Card */}
            <Card className="p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage This Month</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 border rounded-lg">
                  <p className="text-3xl font-bold text-gray-900">{monthlyUsage.seats_booked.toLocaleString()}</p>
                  <p className="text-gray-500">Seats Booked</p>
                  {subscription?.monthly_seat_allowance && subscription.monthly_seat_allowance > 0 && (
                    <div className="mt-2">
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-violet-600 rounded-full"
                          style={{ width: `${Math.min(100, (monthlyUsage.seats_booked / subscription.monthly_seat_allowance) * 100)}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {((monthlyUsage.seats_booked / subscription.monthly_seat_allowance) * 100).toFixed(1)}% of {subscription.monthly_seat_allowance.toLocaleString()} allowance
                      </p>
                    </div>
                  )}
                </div>
                <div className="p-4 border rounded-lg">
                  <p className="text-3xl font-bold text-gray-900">{monthlyUsage.bookings_count}</p>
                  <p className="text-gray-500">Total Bookings</p>
                </div>
              </div>
            </Card>

            {/* White Label Subscription Card */}
            {(company?.white_label || !company?.white_label) && (
              <Card className={`p-6 mb-6 ${company?.white_label ? 'bg-gradient-to-r from-violet-50 to-indigo-50 border-violet-200' : ''}`}>
                <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-5 h-5 text-violet-600" />
                      <h3 className="text-lg font-semibold text-gray-900">White Label Subscription</h3>
                    </div>
                    <p className="text-sm text-gray-600">
                      Remove &quot;Powered by SeatPilot&quot; branding from checkout widgets.
                    </p>
                    
                    {company?.white_label ? (
                      <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-700 font-medium flex items-center gap-2">
                          <Check className="w-4 h-4" /> White Label Active - £20/month
                        </p>
                        {company?.white_label_cancelling && (
                          <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            Cancelling on {company?.white_label_cancel_at ? new Date(company.white_label_cancel_at).toLocaleDateString() : 'end of billing period'}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <p className="text-sm text-gray-600">Not subscribed - £20/month</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    {!company?.white_label ? (
                      <Button 
                        size="sm" 
                        className="bg-gradient-to-r from-violet-600 to-indigo-600"
                        disabled={!paymentMethod}
                        onClick={async () => {
                          if (window.confirm("Subscribe to White Label for £20/month?\n\n• Remove 'Powered by SeatPilot' branding\n• Cancel anytime\n• Billed monthly")) {
                            try {
                              const res = await axios.post(`${getAPI()}/subscriptions/white-label/subscribe`, {
                                origin_url: window.location.origin
                              }, { withCredentials: true });
                              
                              if (res.data.checkout_url) {
                                window.location.href = res.data.checkout_url;
                              } else {
                                toast.success("White Label subscription activated!");
                                await fetchData();
                                await fetchCompanyData();
                              }
                            } catch (e) {
                              toast.error(e.response?.data?.detail || "Failed to subscribe");
                            }
                          }
                        }}
                      >
                        {paymentMethod ? "Subscribe £20/mo" : "Add Card First"}
                      </Button>
                    ) : company?.white_label_cancelling ? (
                      <Button 
                        size="sm" 
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => setShowReactivateConfirm(true)}
                      >
                        Reactivate
                      </Button>
                    ) : (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                        onClick={() => setShowCancelWhiteLabel(true)}
                      >
                        Cancel White Label
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            )}
            
            {/* Payment Method Card */}
            <Card className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Payment Method</h3>
                {!showAddCard && (
                  <Button variant="outline" size="sm" onClick={() => setShowAddCard(true)}>
                    <Plus className="w-4 h-4 mr-1" /> {paymentMethod ? 'Update Card' : 'Add Card'}
                  </Button>
                )}
              </div>
              
              {showAddCard ? (
                <Elements stripe={stripePromise}>
                  <PaymentMethodForm 
                    onSuccess={handleCardAdded}
                    onCancel={() => {
                      setShowAddCard(false);
                      setPendingPlanAfterCard(null);
                    }}
                  />
                </Elements>
              ) : paymentMethod ? (
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="w-12 h-8 bg-gradient-to-r from-blue-600 to-blue-800 rounded flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {paymentMethod.brand?.toUpperCase()} •••• {paymentMethod.last4}
                    </p>
                    <p className="text-sm text-gray-500">
                      Expires {paymentMethod.exp}
                    </p>
                  </div>
                  <div className="ml-auto">
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                      Active
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 mb-4">No payment method on file</p>
                  {subscription?.plan_id === 'free_trial' && (
                    <p className="text-sm text-amber-600 mb-4">
                      Add a card to continue using SeatPilot after your trial ends
                    </p>
                  )}
                  <Button onClick={() => setShowAddCard(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Add Payment Method
                  </Button>
                </div>
              )}
              
              {paymentMethod && !showAddCard && (
                <div className="mt-4 pt-4 border-t">
                  <Button 
                    variant="outline"
                    className="w-full"
                    onClick={async () => {
                      try {
                        const res = await axios.post(`${getAPI()}/subscriptions/billing-portal`, {
                          return_url: window.location.href
                        }, { withCredentials: true });
                        if (res.data.url) {
                          window.location.href = res.data.url;
                        }
                      } catch (error) {
                        toast.error("Unable to open billing portal");
                      }
                    }}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" /> Manage Billing in Stripe
                  </Button>
                </div>
              )}
            </Card>

            {/* Scheduled Plan Change Notice */}
            {subscription?.pending_plan_change && (
              <Card className="p-6 mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-100 rounded-full">
                    <Calendar className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-blue-900 mb-1">📅 Plan Change Scheduled</h3>
                    <p className="text-blue-700 mb-3">
                      Your plan will change from <strong>{subscription.plan_name || subscription.plan_id}</strong> to{' '}
                      <strong>{subscription.pending_plan_change.new_plan_name || subscription.pending_plan_change.new_plan}</strong>
                    </p>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="p-3 bg-white rounded-lg border border-blue-200">
                        <p className="text-xs text-blue-600 font-medium">CURRENT PLAN</p>
                        <p className="text-lg font-bold text-gray-900">{subscription.plan_name || subscription.plan_id}</p>
                        <p className="text-sm text-gray-500">Active until change date</p>
                      </div>
                      <div className="p-3 bg-white rounded-lg border border-blue-200">
                        <p className="text-xs text-blue-600 font-medium">NEW PLAN (Starting {formatDate(subscription.pending_plan_change.effective_date)})</p>
                        <p className="text-lg font-bold text-gray-900">{subscription.pending_plan_change.new_plan_name || subscription.pending_plan_change.new_plan}</p>
                        <p className="text-sm text-gray-500">
                          {subscription.pending_plan_change.is_upgrade ? '⬆️ Upgrade' : '⬇️ Downgrade'}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={async () => {
                          if (!window.confirm("Are you sure you want to cancel the scheduled plan change?")) return;
                          try {
                            await axios.delete(`${getAPI()}/subscriptions/cancel-scheduled-change`, { withCredentials: true });
                            toast.success("Scheduled plan change cancelled");
                            fetchCompanyData();
                          } catch (error) {
                            toast.error("Failed to cancel scheduled change");
                          }
                        }}
                      >
                        Cancel Scheduled Change
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            )}
            {upcomingCharges && subscription?.plan_id === 'pay_as_you_go' && (
              <Card className="p-6 mb-6 bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Charges</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-4 bg-white rounded-lg">
                    <p className="text-sm text-gray-500">Estimated Amount</p>
                    <p className="text-2xl font-bold text-violet-600">{formatCurrency(upcomingCharges.estimated_charges)}</p>
                  </div>
                  <div className="p-4 bg-white rounded-lg">
                    <p className="text-sm text-gray-500">Seats This Period</p>
                    <p className="text-2xl font-bold">{upcomingCharges.usage?.total_seats || 0}</p>
                  </div>
                  <div className="p-4 bg-white rounded-lg">
                    <p className="text-sm text-gray-500">Days Until Billing</p>
                    <p className="text-2xl font-bold">{upcomingCharges.usage?.days_until_billing || 0}</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Billing period started: {upcomingCharges.usage?.billing_period_start ? new Date(upcomingCharges.usage.billing_period_start).toLocaleDateString() : 'N/A'}
                </p>
              </Card>
            )}

            {/* Invoice History Card */}
            <Card className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Invoice History</h3>
                <Button variant="outline" size="sm" onClick={fetchInvoiceHistory}>
                  <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                </Button>
              </div>
              
              {/* Invoice Summary */}
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-600">Total Paid</p>
                  <p className="text-xl font-bold text-green-700">{formatCurrency(invoiceSummary.total_paid)}</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-600">Pending</p>
                  <p className="text-xl font-bold text-amber-700">{formatCurrency(invoiceSummary.total_pending)}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Total Invoices</p>
                  <p className="text-xl font-bold">{invoiceSummary.invoice_count}</p>
                </div>
              </div>

              {/* Invoice List */}
              {loadingInvoices ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                  <p className="text-sm text-gray-500 mt-2">Loading invoices...</p>
                </div>
              ) : invoiceHistory.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  <p>No invoices yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {invoiceHistory.map((invoice) => (
                    <div key={invoice.invoice_id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${
                          invoice.status === 'paid' ? 'bg-green-100' : 
                          invoice.status === 'pending' ? 'bg-amber-100' : 'bg-gray-100'
                        }`}>
                          <FileText className={`w-5 h-5 ${
                            invoice.status === 'paid' ? 'text-green-600' : 
                            invoice.status === 'pending' ? 'text-amber-600' : 'text-gray-600'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{invoice.invoice_number}</p>
                          <p className="text-sm text-gray-500">{invoice.description}</p>
                          <p className="text-xs text-gray-400">{new Date(invoice.invoice_date || invoice.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-semibold text-gray-900">{formatCurrency(invoice.total)}</p>
                          <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                            invoice.status === 'paid' ? 'bg-green-100 text-green-700' : 
                            invoice.status === 'pending' ? 'bg-amber-100 text-amber-700' : 
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                          </span>
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            title="View Invoice (Print to PDF)"
                            onClick={() => {
                              // Open invoice in new tab - user can print to PDF from browser
                              window.open(`${getAPI()}/invoices/${invoice.invoice_id}/pdf`, '_blank');
                            }}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            title="Send to Email"
                            onClick={async () => {
                              try {
                                await axios.post(`${getAPI()}/invoices/${invoice.invoice_id}/send-email`, {}, { withCredentials: true });
                                toast.success("Invoice sent to your email!");
                              } catch (error) {
                                toast.error(error.response?.data?.detail || "Failed to send invoice");
                              }
                            }}
                          >
                            <Mail className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Referrals Tab */}
        {activeTab === "referrals" && (
          <ReferralDashboardInline />
        )}

        {/* Team Tab */}
        {activeTab === "team" && (
          <div>
            {/* Mobile-responsive header */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Team Management</h1>
                <p className="text-sm sm:text-base text-gray-600">Invite team members and manage roles</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => {
                  fetchCustomRoles();
                  setShowRoleManager(true);
                }} className="gap-1 sm:gap-2 text-xs sm:text-sm">
                  <Shield className="w-3 h-3 sm:w-4 sm:h-4" /> <span className="hidden xs:inline">Manage</span> Roles
                </Button>
                <Button size="sm" onClick={() => setShowInviteTeam(true)} className="gap-1 sm:gap-2 text-xs sm:text-sm">
                  <UserPlus className="w-3 h-3 sm:w-4 sm:h-4" /> Invite<span className="hidden sm:inline"> Member</span>
                </Button>
              </div>
            </div>

            {/* Custom Roles Section */}
            <Card className="p-4 sm:p-6 mb-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Custom Roles</h3>
                <Button variant="outline" size="sm" onClick={() => {
                  setEditingRole(null);
                  setRoleForm({ name: "", permissions: [] });
                  setShowRoleManager(true);
                }} className="text-xs sm:text-sm w-full sm:w-auto">
                  <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> Create Role
                </Button>
              </div>
              
              {customRoles.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <Shield className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No custom roles yet. Create roles to define team permissions.</p>
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {customRoles.map(role => (
                    <div key={role.role_id} className="p-4 border rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-medium text-gray-900">{role.name}</p>
                          <p className="text-xs text-gray-500">{role.permissions?.length || 0} permissions</p>
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setEditingRole(role);
                              setRoleForm({ name: role.name, permissions: role.permissions || [] });
                              setShowRoleManager(true);
                            }}
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          {!role.is_default && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => deleteCustomRole(role.role_id)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(role.permissions || []).slice(0, 3).map(perm => (
                          <span key={perm} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px]">
                            {perm.replace(/_/g, ' ')}
                          </span>
                        ))}
                        {(role.permissions?.length || 0) > 3 && (
                          <span className="px-1.5 py-0.5 bg-violet-100 text-violet-600 rounded text-[10px]">
                            +{role.permissions.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
            
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Members</h3>
              {/* Filter out the current owner from team members list to avoid duplicate */}
              {(() => {
                const otherMembers = teamMembers.filter(m => m.email?.toLowerCase() !== user?.email?.toLowerCase());
                return otherMembers.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 mb-4">No other team members yet</p>
                    <Button variant="outline" onClick={() => setShowInviteTeam(true)}>
                      <UserPlus className="w-4 h-4 mr-2" /> Invite Your First Team Member
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y">
                    {/* Current user (Owner) - always shown first */}
                    <div className="py-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
                          <span className="text-violet-600 font-medium">{user?.name?.charAt(0) || user?.email?.charAt(0)}</span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{user?.name || 'You'}</p>
                          <p className="text-sm text-gray-500">{user?.email}</p>
                        </div>
                      </div>
                      <span className="px-2 py-1 bg-violet-100 text-violet-800 text-xs font-medium rounded">Owner</span>
                    </div>
                    
                    {/* Other team members (excluding owner) */}
                    {otherMembers.map((member) => (
                      <div key={member.user_id || member.invite_id} className="py-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                            <span className="text-gray-600 font-medium">{member.name?.charAt(0) || member.email?.charAt(0)}</span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{member.name || 'Invited User'}</p>
                            <p className="text-sm text-gray-500">{member.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs font-medium rounded ${
                            member.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {member.status === 'pending' ? 'Pending' : member.role}
                          </span>
                          {(member.user_id !== user?.user_id || member.status === 'pending') && (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleRevokeMember(member.invite_id || member.user_id, member.status === 'pending')}
                              title={member.status === 'pending' ? 'Revoke Invitation' : 'Remove Member'}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </Card>
            
            {/* Invite Dialog */}
            <Dialog open={showInviteTeam} onOpenChange={setShowInviteTeam}>
              <DialogContent className="max-w-md max-h-[90vh] overflow-hidden flex flex-col">
                <DialogHeader className="flex-shrink-0">
                  <DialogTitle>Invite Team Member</DialogTitle>
                  <DialogDescription>Send an invitation to join your company</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleInviteTeam} className="flex flex-col flex-1 overflow-hidden">
                  <div className="flex-1 overflow-y-auto space-y-4 pr-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                      <Input
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="colleague@company.com"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                      
                      {/* Predefined Roles */}
                      <div className="space-y-2 mb-3">
                        <p className="text-xs text-gray-500 font-medium">Standard Roles</p>
                        {[
                          { id: 'admin', name: 'Admin', description: 'Full access including billing, team, and settings management' },
                          { id: 'manager', name: 'Manager', description: 'Can manage layouts, events, reports, and team members' },
                          { id: 'editor', name: 'Editor', description: 'Can create and edit layouts and events' },
                          { id: 'viewer', name: 'Viewer', description: 'Read-only access to layouts, events, and reports' },
                          { id: 'support', name: 'Support Agent', description: 'Can view tickets and customer support features' },
                          { id: 'billing', name: 'Billing', description: 'Access to billing, invoices, and subscription management' },
                          { id: 'developer', name: 'Developer', description: 'Access to API keys, integrations, and developer tools' },
                          { id: 'marketing', name: 'Marketing', description: 'Access to referrals, rewards, and promotional features' },
                        ].map(role => (
                          <label 
                            key={role.id}
                            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                              inviteRole === role.id ? 'border-violet-500 bg-violet-50' : 'border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="radio"
                              name="role"
                              value={role.id}
                              checked={inviteRole === role.id}
                              onChange={(e) => setInviteRole(e.target.value)}
                              className="mt-1"
                            />
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{role.name}</p>
                              <p className="text-xs text-gray-500">{role.description}</p>
                            </div>
                          </label>
                        ))}
                      </div>

                      {/* Custom Roles from Company */}
                      {customRoles.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs text-gray-500 font-medium">Custom Roles</p>
                          {customRoles.map(role => (
                            <label 
                              key={role.role_id}
                              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                inviteRole === role.role_id ? 'border-violet-500 bg-violet-50' : 'border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="radio"
                                name="role"
                                value={role.role_id}
                                checked={inviteRole === role.role_id}
                                onChange={(e) => setInviteRole(e.target.value)}
                                className="mt-1"
                              />
                              <div>
                                <p className="font-medium text-gray-900 text-sm">{role.name}</p>
                                <p className="text-xs text-gray-500">{role.permissions?.length || 0} permissions</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex-shrink-0 pt-4 border-t mt-4">
                    <Button type="submit" className="w-full" disabled={inviting}>
                      {inviting ? "Sending..." : "Send Invitation"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Support Tab - Customer Tickets */}
        {activeTab === "support" && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Headphones className="w-6 h-6 text-violet-600" />
                  Support Center
                </h1>
                <p className="text-gray-600">Get help from our support team</p>
                {company?.customer_id && (
                  <p className="text-sm text-gray-500 mt-1">
                    Your Customer ID: <code className="bg-gray-100 px-2 py-0.5 rounded font-mono text-violet-600">{company.customer_id}</code>
                    <span className="text-xs text-gray-400 ml-2">(Include this when contacting support)</span>
                  </p>
                )}
              </div>
              <Button onClick={() => { setShowCreateTicket(true); fetchMyTickets(); }} className="bg-violet-600 hover:bg-violet-700">
                <Plus className="w-4 h-4 mr-2" /> New Ticket
              </Button>
            </div>

            {/* Info Card */}
            <Card className="p-4 bg-gradient-to-r from-violet-50 to-indigo-50 border-violet-200">
              <div className="flex gap-3">
                <Headphones className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-violet-800">Need Help?</p>
                  <p className="text-sm text-violet-700 mt-1">
                    Create a support ticket and our team will respond within 24 hours. 
                    You can track all your tickets and conversations here.
                  </p>
                </div>
              </div>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Tickets List */}
              <Card className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold">Active Tickets</h3>
                  <Button variant="ghost" size="sm" onClick={fetchMyTickets} disabled={loadingTickets}>
                    <RefreshCw className={`w-4 h-4 ${loadingTickets ? 'animate-spin' : ''}`} />
                  </Button>
                </div>

                {loadingTickets ? (
                  <div className="text-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                    <p className="text-sm text-gray-500 mt-2">Loading tickets...</p>
                  </div>
                ) : myTickets.filter(t => !["resolved", "closed"].includes(t.status)).length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Headphones className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    <p className="mb-2">No active tickets</p>
                    <p className="text-sm text-gray-400">Create a ticket to get help from our team</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {myTickets.filter(t => !["resolved", "closed"].includes(t.status)).map(ticket => (
                      <div
                        key={ticket.ticket_id}
                        onClick={() => fetchTicketDetails(ticket.ticket_id)}
                        className={`p-3 rounded-lg border cursor-pointer transition ${
                          selectedMyTicket?.ticket_id === ticket.ticket_id ? "border-violet-500 bg-violet-50" : "hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <div>
                              <p className="font-medium text-sm">{ticket.subject}</p>
                              <p className="text-xs text-gray-500">{ticket.ticket_number}</p>
                            </div>
                            {ticket.unread_by_customer && (
                              <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0" title="New reply"></span>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              ticket.status === "open" ? "bg-blue-100 text-blue-700" :
                              ticket.status === "in_progress" ? "bg-yellow-100 text-yellow-700" :
                              ticket.status === "waiting_customer" ? "bg-orange-100 text-orange-700" :
                              ticket.status === "requested_info" ? "bg-amber-100 text-amber-700" :
                              "bg-gray-100 text-gray-700"
                            }`}>{ticket.status === "requested_info" ? "Info Needed" : ticket.status.replace("_", " ")}</span>
                            <span className="text-xs text-gray-400">
                              {new Date(ticket.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Closed/Resolved Tickets Section */}
                {myTickets.filter(t => ["resolved", "closed"].includes(t.status)).length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="font-medium text-sm text-gray-600 mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Closed Tickets ({myTickets.filter(t => ["resolved", "closed"].includes(t.status)).length})
                    </h4>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {myTickets.filter(t => ["resolved", "closed"].includes(t.status)).map(ticket => (
                        <div
                          key={ticket.ticket_id}
                          onClick={() => fetchTicketDetails(ticket.ticket_id)}
                          className={`p-2 rounded-lg border cursor-pointer transition opacity-70 hover:opacity-100 ${
                            selectedMyTicket?.ticket_id === ticket.ticket_id ? "border-violet-500 bg-violet-50" : "hover:bg-gray-50"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-xs">{ticket.subject}</p>
                              <p className="text-xs text-gray-400">{ticket.ticket_number}</p>
                            </div>
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              ticket.status === "resolved" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"
                            }`}>{ticket.status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>

              {/* Selected Ticket */}
              <Card className="p-4">
                {selectedMyTicket ? (
                  <div className="h-full flex flex-col">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold">{selectedMyTicket.subject}</h3>
                        <p className="text-sm text-gray-500">{selectedMyTicket.ticket_number}</p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${
                        selectedMyTicket.status === "open" ? "bg-blue-100 text-blue-700" :
                        selectedMyTicket.status === "in_progress" ? "bg-yellow-100 text-yellow-700" :
                        selectedMyTicket.status === "resolved" ? "bg-green-100 text-green-700" :
                        "bg-gray-100 text-gray-700"
                      }`}>{selectedMyTicket.status.replace("_", " ")}</span>
                    </div>

                    <div className="flex-1 space-y-2 max-h-64 overflow-y-auto mb-4">
                      {selectedMyTicket.messages?.filter(msg => !msg.is_internal).map(msg => (
                        <div key={msg.message_id} className={`p-3 rounded-lg text-sm ${
                          msg.sender_type === "staff" 
                            ? "bg-blue-50 border border-blue-200 ml-4" 
                            : "bg-violet-100 border border-violet-300 mr-4"
                        }`}>
                          <div className="flex justify-between mb-1">
                            <span className={`font-medium text-xs ${msg.sender_type === "staff" ? "text-blue-700" : "text-violet-700"}`}>
                              {msg.sender_type === "staff" ? "Support Team" : "You"}
                            </span>
                            <span className="text-xs text-gray-400">{new Date(msg.created_at).toLocaleString()}</span>
                          </div>
                          <p className="whitespace-pre-wrap">{msg.message}</p>
                          {msg.attachments?.length > 0 && (
                            <div className="mt-2 text-xs text-blue-600">
                              📎 {msg.attachments.length} attachment(s)
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Action buttons based on ticket status */}
                    {selectedMyTicket.status === "resolved" && (
                      <div className="border-t pt-3">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                          <p className="text-sm text-green-800 font-medium">This ticket has been marked as resolved.</p>
                          <p className="text-xs text-green-600 mt-1">
                            If your issue is resolved, please confirm closure. The ticket will auto-close after 7 days if no response.
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={async () => {
                              try {
                                await axios.post(`${getAPI()}/support/tickets/${selectedMyTicket.ticket_id}/confirm-close`, {}, { withCredentials: true });
                                toast.success("Ticket closed successfully");
                                fetchMyTickets();
                                setSelectedMyTicket(null);
                              } catch (error) {
                                toast.error("Failed to close ticket");
                              }
                            }}
                          >
                            <CheckCircle className="w-3 h-3 mr-1" /> Confirm Closed
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={async () => {
                              try {
                                await axios.post(`${getAPI()}/support/tickets/${selectedMyTicket.ticket_id}/reopen`, {}, { withCredentials: true });
                                toast.success("Ticket reopened - please add more details");
                                fetchTicketDetails(selectedMyTicket.ticket_id);
                                fetchMyTickets();
                              } catch (error) {
                                toast.error("Failed to reopen ticket");
                              }
                            }}
                          >
                            <RefreshCw className="w-3 h-3 mr-1" /> Still Need Help
                          </Button>
                        </div>
                      </div>
                    )}

                    {selectedMyTicket.status === "closed" && (
                      <div className="border-t pt-3">
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
                          <p className="text-sm text-gray-700 font-medium">This ticket is closed.</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {selectedMyTicket.auto_closed 
                              ? "This ticket was automatically closed due to no response within 7 days."
                              : "You can reopen this ticket if you need further assistance."}
                          </p>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={async () => {
                            try {
                              await axios.post(`${getAPI()}/support/tickets/${selectedMyTicket.ticket_id}/reopen`, {}, { withCredentials: true });
                              toast.success("Ticket reopened");
                              fetchTicketDetails(selectedMyTicket.ticket_id);
                              fetchMyTickets();
                            } catch (error) {
                              toast.error("Failed to reopen ticket");
                            }
                          }}
                        >
                          <RefreshCw className="w-3 h-3 mr-1" /> Reopen Ticket
                        </Button>
                      </div>
                    )}

                    {selectedMyTicket.status === "requested_info" && (
                      <div className="border-t pt-3">
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                          <p className="text-sm text-amber-800 font-medium">⚠️ Additional information required</p>
                          <p className="text-xs text-amber-600 mt-1">
                            Our support team needs more information from you to resolve this issue. Please provide the requested details below.
                          </p>
                        </div>
                        
                        {/* File Upload Section - only if attachments requested */}
                        {selectedMyTicket.attachments_requested && (
                          <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                            <div className="flex items-center gap-2 mb-2">
                              <Paperclip className="w-4 h-4 text-gray-500" />
                              <p className="text-sm font-medium">Attach Files</p>
                            </div>
                            <input
                              type="file"
                              accept={selectedMyTicket.attachment_types_allowed?.includes("image") ? "image/*," : ""}
                              multiple
                              onChange={async (e) => {
                                const files = Array.from(e.target.files);
                                for (const file of files) {
                                  const formData = new FormData();
                                  formData.append("file", file);
                                  try {
                                    const res = await axios.post(
                                      `${getAPI()}/support/tickets/${selectedMyTicket.ticket_id}/upload`,
                                      formData,
                                      { withCredentials: true, headers: { "Content-Type": "multipart/form-data" }}
                                    );
                                    toast.success(`${file.name} uploaded!`);
                                    // Add to reply
                                    setMyTicketReply(prev => prev + `\n[Attached: ${file.name}]`);
                                  } catch (error) {
                                    toast.error(`Failed to upload ${file.name}`);
                                  }
                                }
                              }}
                              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"
                            />
                            <p className="text-xs text-gray-400 mt-1">
                              Allowed: {selectedMyTicket.attachment_types_allowed?.join(", ") || "images, documents"} • Max 10MB
                            </p>
                          </div>
                        )}
                        
                        <textarea
                          className="w-full p-2 border border-amber-300 rounded text-sm focus:ring-amber-500 focus:border-amber-500"
                          rows={3}
                          placeholder="Please provide the requested information..."
                          value={myTicketReply}
                          onChange={(e) => setMyTicketReply(e.target.value)}
                        />
                        <div className="flex justify-end mt-2">
                          <Button size="sm" onClick={sendMyTicketReply} disabled={!myTicketReply.trim() || sendingReply} className="bg-amber-600 hover:bg-amber-700">
                            {sendingReply ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                            Send Information
                          </Button>
                        </div>
                      </div>
                    )}

                    {!["resolved", "closed", "requested_info"].includes(selectedMyTicket.status) && (
                      <div className="border-t pt-3">
                        <textarea
                          className="w-full p-2 border rounded text-sm"
                          rows={2}
                          placeholder="Type your reply..."
                          value={myTicketReply}
                          onChange={(e) => setMyTicketReply(e.target.value)}
                        />
                        <div className="flex justify-end mt-2">
                          <Button size="sm" onClick={sendMyTicketReply} disabled={!myTicketReply.trim() || sendingReply}>
                            {sendingReply ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                            Send
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400 py-12">
                    <div className="text-center">
                      <Headphones className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                      <p>Select a ticket to view conversation</p>
                    </div>
                  </div>
                )}
              </Card>
            </div>

            {/* Create Ticket Dialog */}
            <Dialog open={showCreateTicket} onOpenChange={setShowCreateTicket}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Support Ticket</DialogTitle>
                </DialogHeader>
                <form onSubmit={createSupportTicket} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                    <Input
                      value={newTicketForm.subject}
                      onChange={(e) => setNewTicketForm({ ...newTicketForm, subject: e.target.value })}
                      placeholder="Brief description of your issue"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      className="w-full p-2 border rounded-md"
                      value={newTicketForm.category}
                      onChange={(e) => setNewTicketForm({ ...newTicketForm, category: e.target.value })}
                    >
                      <option value="general">General Inquiry</option>
                      <option value="technical">Technical Issue</option>
                      <option value="billing">Billing Question</option>
                      <option value="feature_request">Feature Request</option>
                      <option value="bug">Bug Report</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                    <select
                      className="w-full p-2 border rounded-md"
                      value={newTicketForm.priority}
                      onChange={(e) => setNewTicketForm({ ...newTicketForm, priority: e.target.value })}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      className="w-full p-2 border rounded-md"
                      rows={4}
                      value={newTicketForm.description}
                      onChange={(e) => setNewTicketForm({ ...newTicketForm, description: e.target.value })}
                      placeholder="Please describe your issue in detail..."
                      required
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setShowCreateTicket(false)}>Cancel</Button>
                    <Button type="submit" disabled={creatingTicket}>
                      {creatingTicket ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Create Ticket
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-6">Settings</h1>
            
            {/* Customer ID Card - Prominent Display */}
            {company?.customer_id && (
              <Card className="p-4 mb-6 bg-gradient-to-r from-violet-500 to-indigo-600 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-violet-100">Your Customer ID</p>
                      <p className="text-2xl font-bold font-mono tracking-wider">{company.customer_id}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-violet-100">Need Support?</p>
                    <p className="text-xs text-violet-200">Always quote this ID</p>
                  </div>
                </div>
              </Card>
            )}
            
            {/* Profile Section */}
            <Card className="p-6 mb-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Profile Information</h3>
                {!editingProfile && (
                  <Button variant="outline" size="sm" onClick={() => setEditingProfile(true)}>
                    <Edit className="w-4 h-4 mr-1" /> Edit
                  </Button>
                )}
              </div>
              
              {editingProfile ? (
                <form onSubmit={handleUpdateProfile} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <Input
                      value={profileForm.name}
                      onChange={(e) => setProfileForm({...profileForm, name: e.target.value})}
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <PhoneInput
                      value={profileForm.phone}
                      onChange={(phone) => setProfileForm({...profileForm, phone})}
                      autoDetectCountry={true}
                    />
                    <p className="text-xs text-gray-400 mt-1">We&apos;ll auto-detect your country or you can select it</p>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={savingProfile}>
                      {savingProfile ? "Saving..." : "Save Changes"}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setEditingProfile(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Name</p>
                      <p className="font-medium">{user?.name || "Not set"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium">{user?.email}</p>
                      {user?.email_verified ? (
                        <span className="text-xs text-green-600 flex items-center gap-1"><Check className="w-3 h-3" /> Verified</span>
                      ) : (
                        <span className="text-xs text-red-600">Not verified</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      {user?.phone ? (
                        <a href={`tel:${user.phone}`} className="font-medium text-violet-600 hover:text-violet-700 hover:underline">
                          {user.phone}
                        </a>
                      ) : (
                        <p className="font-medium text-gray-400">Not set</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Card>

            {/* Company Information */}
            <Card className="p-6 mb-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Company Information</h3>
                {!editingCompany && (
                  <Button variant="outline" size="sm" onClick={() => {
                    setCompanyEditForm({
                      name: company?.name || "",
                      website: company?.website || "",
                      address: company?.address || "",
                      city: company?.city || "",
                      postcode: company?.postcode || "",
                      country: company?.country || "",
                      phone: company?.phone || "",
                      vat_number: company?.vat_number || "",
                      vat_registered: company?.vat_registered || false
                    });
                    setEditingCompany(true);
                  }}>
                    <Edit className="w-4 h-4 mr-1" /> Edit
                  </Button>
                )}
              </div>
              
              {/* Company Logo */}
              <div className="flex items-start gap-6 mb-6 pb-6 border-b border-gray-100">
                <AvatarUpload
                  currentUrl={company?.logo_url}
                  name={company?.name || "Company"}
                  onUploadSuccess={(url) => fetchCompanyData()}
                  uploadEndpoint="/subscriptions/company/logo"
                  deleteEndpoint="/subscriptions/company/logo"
                  size="lg"
                />
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-1">Company Logo</h4>
                  <p className="text-sm text-gray-500">
                    Upload your company logo. Recommended size: 200x200px. 
                    Supported formats: JPEG, PNG, GIF, WebP (max 5MB).
                  </p>
                </div>
              </div>
              
              {editingCompany ? (
                <form onSubmit={handleUpdateCompany} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                      <Input
                        value={companyEditForm.name}
                        onChange={(e) => setCompanyEditForm({...companyEditForm, name: e.target.value})}
                        placeholder="Your Company Ltd"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                      <Input
                        value={companyEditForm.website}
                        onChange={(e) => setCompanyEditForm({...companyEditForm, website: e.target.value})}
                        placeholder="https://yourcompany.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <PhoneInput
                        value={companyEditForm.phone}
                        onChange={(phone) => setCompanyEditForm({...companyEditForm, phone})}
                        autoDetectCountry={false}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                      <Input
                        value={companyEditForm.address}
                        onChange={(e) => setCompanyEditForm({...companyEditForm, address: e.target.value})}
                        placeholder="123 Business Street"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                      <Input
                        value={companyEditForm.city}
                        onChange={(e) => setCompanyEditForm({...companyEditForm, city: e.target.value})}
                        placeholder="London"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                      <Input
                        value={companyEditForm.postcode}
                        onChange={(e) => setCompanyEditForm({...companyEditForm, postcode: e.target.value})}
                        placeholder="SW1A 1AA"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                      <Input
                        value={companyEditForm.country}
                        onChange={(e) => setCompanyEditForm({...companyEditForm, country: e.target.value})}
                        placeholder="United Kingdom"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">VAT Number</label>
                      <Input
                        value={companyEditForm.vat_number}
                        onChange={(e) => setCompanyEditForm({...companyEditForm, vat_number: e.target.value})}
                        placeholder="GB123456789"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <input
                      type="checkbox"
                      id="vat_registered"
                      checked={companyEditForm.vat_registered}
                      onChange={(e) => setCompanyEditForm({...companyEditForm, vat_registered: e.target.checked})}
                      className="rounded border-gray-300 text-violet-600"
                    />
                    <label htmlFor="vat_registered" className="text-sm text-gray-700">VAT Registered</label>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button type="submit" disabled={savingCompanyEdit}>
                      {savingCompanyEdit ? "Saving..." : "Save Changes"}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setEditingCompany(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Company Name</p>
                    <p className="font-medium">{company?.name || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Website</p>
                    {company?.website ? (
                      <a href={company.website} target="_blank" rel="noopener noreferrer" className="font-medium text-violet-600 hover:underline">
                        {company.website}
                      </a>
                    ) : (
                      <p className="font-medium text-gray-400">Not set</p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    {company?.phone ? (
                      <a href={`tel:${company.phone}`} className="font-medium text-violet-600 hover:underline">
                        {company.phone}
                      </a>
                    ) : (
                      <p className="font-medium text-gray-400">Not set</p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Address</p>
                    <p className="font-medium">{company?.address || <span className="text-gray-400">Not set</span>}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">City</p>
                    <p className="font-medium">{company?.city || <span className="text-gray-400">Not set</span>}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Postcode</p>
                    <p className="font-medium">{company?.postcode || <span className="text-gray-400">Not set</span>}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Country</p>
                    <p className="font-medium">{company?.country || <span className="text-gray-400">Not set</span>}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">VAT Number</p>
                    <p className="font-medium">{company?.vat_number || <span className="text-gray-400">Not set</span>}</p>
                  </div>
                </div>
              )}
            </Card>

            {/* Security Section */}
            <Card className="p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Security</h3>
              <div className="space-y-4">
                {/* Change Password */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Key className="w-5 h-5 text-gray-600" />
                    <div>
                      <p className="font-medium">Change Password</p>
                      <p className="text-sm text-gray-500">Update your account password</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setShowPasswordChange(true)}>
                    Change
                  </Button>
                </div>

                {/* 2FA Toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-violet-600" />
                    <div>
                      <p className="font-medium">Two-Factor Authentication</p>
                      <p className="text-sm text-gray-500">Add an extra layer of security with email verification</p>
                    </div>
                  </div>
                  <button
                    onClick={handleToggle2FA}
                    disabled={toggling2FA}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      twoFactorEnabled ? 'bg-violet-600' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      twoFactorEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>
            </Card>

            {/* Trusted Devices */}
            <Card className="p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Trusted Devices</h3>
                  <p className="text-sm text-gray-500">{trustedDevices.length} device{trustedDevices.length !== 1 ? 's' : ''} trusted</p>
                </div>
                {trustedDevices.length > 0 && (
                  <Button variant="outline" size="sm" onClick={async () => {
                    if (window.confirm("Remove all trusted devices?")) {
                      try {
                        await axios.delete(`${getAPI()}/auth/trusted-devices`, { withCredentials: true });
                        toast.success("All trusted devices removed");
                        setTrustedDevices([]);
                      } catch (error) {
                        toast.error("Failed to remove devices");
                      }
                    }
                  }}>
                    <Trash2 className="w-4 h-4 mr-1" /> Clear All
                  </Button>
                )}
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {trustedDevices.map((device) => (
                  <div key={device.fingerprint} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Monitor className="w-4 h-4 text-gray-500" />
                      <div>
                        <p className="text-sm font-medium">{device.browser || 'Unknown'} on {device.os || 'Unknown'}</p>
                        <p className="text-xs text-gray-500">
                          Added {formatDate(device.created_at)} • Expires {formatDate(device.expires_at)}
                        </p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={async () => {
                      try {
                        await axios.delete(`${getAPI()}/auth/trusted-devices/${device.fingerprint}`, { withCredentials: true });
                        setTrustedDevices(prev => prev.filter(d => d.fingerprint !== device.fingerprint));
                        toast.success("Device removed");
                      } catch (error) {
                        toast.error("Failed to remove device");
                      }
                    }}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {trustedDevices.length === 0 && (
                  <p className="text-center text-gray-500 py-4">No trusted devices</p>
                )}
              </div>
            </Card>

            {/* Active Sessions */}
            <Card className="p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Active Sessions</h3>
                  <p className="text-sm text-gray-500">
                    {activeSessions.length} active session{activeSessions.length !== 1 ? 's' : ''}
                  </p>
                </div>
                {activeSessions.length > 1 && (
                  <Button variant="outline" size="sm" onClick={() => setShowLogoutAllConfirm(true)}>
                    <LogOut className="w-4 h-4 mr-1" /> Logout All Other
                  </Button>
                )}
              </div>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {(() => {
                  const grouped = {};
                  activeSessions.forEach(session => {
                    const key = `${session.ip_address}-${session.device_info?.browser}-${session.device_info?.os}`;
                    if (!grouped[key] || session.is_current) {
                      grouped[key] = session;
                    }
                  });
                  return Object.values(grouped);
                })().map((session) => (
                  <div key={session.session_id} className={`flex justify-between items-center p-3 rounded-lg ${session.is_current ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-3">
                      {session.device_info?.device_type === 'Mobile' ? (
                        <Smartphone className="w-5 h-5 text-gray-500" />
                      ) : session.device_info?.device_type === 'Tablet' ? (
                        <Tablet className="w-5 h-5 text-gray-500" />
                      ) : (
                        <Monitor className="w-5 h-5 text-gray-500" />
                      )}
                      <div>
                        <p className="font-medium text-sm">
                          {session.device_info?.browser || 'Unknown'} on {session.device_info?.os || 'Unknown'}
                          {session.is_current && <span className="ml-2 text-xs bg-green-600 text-white px-2 py-0.5 rounded">Current</span>}
                        </p>
                        <p className="text-xs text-gray-500">
                          {session.location?.city && session.location?.country 
                            ? `${session.location.city}, ${session.location.country} • ` 
                            : ''}{session.ip_address}
                        </p>
                        <p className="text-xs text-gray-400">{formatDate(session.created_at)}</p>
                      </div>
                    </div>
                    {!session.is_current && (
                      <Button variant="ghost" size="sm" onClick={() => handleLogoutSession(session.session_id)}>
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                {activeSessions.length === 0 && (
                  <p className="text-center text-gray-500 py-4">No active sessions</p>
                )}
              </div>
            </Card>

            {/* Login History */}
            <Card className="p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Login History</h3>
                  <p className="text-sm text-gray-500">{loginHistory.length} records</p>
                </div>
                {loginHistory.length > 0 && (
                  <Button variant="outline" size="sm" onClick={handleClearLoginHistory} disabled={clearingHistory}>
                    <Trash2 className="w-4 h-4 mr-1" /> {clearingHistory ? "Clearing..." : "Clear"}
                  </Button>
                )}
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {loginHistory.map((login, index) => (
                  <div key={login.login_id || index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <LogIn className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium">
                          {login.browser || 'Unknown'} on {login.os || 'Unknown'}
                          {login.is_2fa && <span className="ml-2 text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded">2FA</span>}
                        </p>
                        <p className="text-xs text-gray-500">
                          {login.city && login.country ? `${login.city}, ${login.country} • ` : ''}{login.ip_address || login.ip}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-gray-400">{formatDate(login.timestamp)}</span>
                  </div>
                ))}
                {loginHistory.length === 0 && (
                  <p className="text-center text-gray-500 py-4">No login history</p>
                )}
              </div>
            </Card>

            {/* Recent Activity */}
            <Card className="p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
                  <p className="text-sm text-gray-500">Last 30 days</p>
                </div>
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {recentActivity.length > 0 ? recentActivity.map((activity, index) => (
                  <div key={activity.id || activity.timestamp || `act-${index}`} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className={`p-2 rounded-full flex-shrink-0 ${
                      activity.type === 'login' ? 'bg-blue-100' :
                      activity.type === 'security' ? 'bg-amber-100' :
                      activity.type === 'support' ? 'bg-violet-100' :
                      'bg-gray-100'
                    }`}>
                      {activity.icon === 'log-in' && <LogIn className="w-4 h-4 text-blue-600" />}
                      {activity.icon === 'key' && <Key className="w-4 h-4 text-amber-600" />}
                      {activity.icon === 'shield-check' && <Shield className="w-4 h-4 text-green-600" />}
                      {activity.icon === 'shield-off' && <Shield className="w-4 h-4 text-red-600" />}
                      {activity.icon === 'user' && <User className="w-4 h-4 text-gray-600" />}
                      {activity.icon === 'building' && <Building2 className="w-4 h-4 text-gray-600" />}
                      {activity.icon === 'headphones' && <Headphones className="w-4 h-4 text-violet-600" />}
                      {!['log-in', 'key', 'shield-check', 'shield-off', 'user', 'building', 'headphones'].includes(activity.icon) && 
                        <Activity className="w-4 h-4 text-gray-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                      {activity.description && (
                        <p className="text-xs text-gray-500 truncate">{activity.description}</p>
                      )}
                      {activity.location && (
                        <p className="text-xs text-gray-400">{activity.location}</p>
                      )}
                    </div>
                    <span className="text-xs text-gray-400 flex-shrink-0">{formatDate(activity.timestamp)}</span>
                  </div>
                )) : (
                  <p className="text-center text-gray-500 py-4">No recent activity</p>
                )}
              </div>
            </Card>

            {/* Notification Preferences */}
            <Card className="p-6 mb-6" id="notifications">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Bell className="w-5 h-5 text-violet-600" />
                    Notification Preferences
                  </h3>
                  <p className="text-sm text-gray-500">Control how you receive notifications</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={toggleNotificationSound}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      notificationPrefs?.sound_muted 
                        ? 'bg-gray-100 text-gray-600' 
                        : 'bg-violet-100 text-violet-700'
                    }`}
                  >
                    {notificationPrefs?.sound_muted ? (
                      <>
                        <EyeOff className="w-4 h-4" />
                        Sound Muted
                      </>
                    ) : (
                      <>
                        <Bell className="w-4 h-4" />
                        Sound On
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Notification Categories */}
              <div className="space-y-4">
                {Object.entries(notificationCategories).map(([categoryKey, categoryInfo]) => (
                  <div key={categoryKey} className="border rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${
                        categoryInfo.color === 'red' ? 'bg-red-100' :
                        categoryInfo.color === 'green' ? 'bg-green-100' :
                        categoryInfo.color === 'violet' ? 'bg-violet-100' :
                        categoryInfo.color === 'blue' ? 'bg-blue-100' :
                        categoryInfo.color === 'amber' ? 'bg-amber-100' :
                        categoryInfo.color === 'indigo' ? 'bg-indigo-100' :
                        'bg-gray-100'
                      }`}>
                        {categoryInfo.icon === 'Shield' && <Shield className={`w-5 h-5 ${
                          categoryInfo.color === 'red' ? 'text-red-600' : 'text-gray-600'
                        }`} />}
                        {categoryInfo.icon === 'CreditCard' && <CreditCard className="w-5 h-5 text-green-600" />}
                        {categoryInfo.icon === 'Headphones' && <Headphones className="w-5 h-5 text-violet-600" />}
                        {categoryInfo.icon === 'Activity' && <Activity className="w-5 h-5 text-blue-600" />}
                        {categoryInfo.icon === 'Star' && <Sparkles className="w-5 h-5 text-amber-600" />}
                        {categoryInfo.icon === 'Users' && <Users className="w-5 h-5 text-indigo-600" />}
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{categoryInfo.name}</h4>
                        <p className="text-xs text-gray-500">
                          {categoryInfo.types?.slice(0, 3).map(t => t.replace(/_/g, ' ')).join(', ')}
                          {categoryInfo.types?.length > 3 && '...'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-6 ml-12">
                      {/* Email Toggle */}
                      <label className="flex items-center gap-2 cursor-pointer">
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={notificationPrefs?.email?.[categoryKey] ?? true}
                            onChange={() => toggleNotificationPref('email', categoryKey)}
                            disabled={savingNotificationPrefs || categoryKey === 'security' || categoryKey === 'payments'}
                            className="sr-only peer"
                          />
                          <div className={`w-10 h-5 rounded-full transition-colors ${
                            notificationPrefs?.email?.[categoryKey] ?? true
                              ? 'bg-violet-600' 
                              : 'bg-gray-300'
                          } ${(categoryKey === 'security' || categoryKey === 'payments') ? 'opacity-60 cursor-not-allowed' : ''}`}>
                            <div className={`absolute w-4 h-4 bg-white rounded-full top-0.5 transition-transform ${
                              notificationPrefs?.email?.[categoryKey] ?? true ? 'translate-x-5' : 'translate-x-0.5'
                            }`} />
                          </div>
                        </div>
                        <span className="text-sm text-gray-600 flex items-center gap-1">
                          <Mail className="w-3.5 h-3.5" />
                          Email
                          {(categoryKey === 'security' || categoryKey === 'payments') && (
                            <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Required</span>
                          )}
                        </span>
                      </label>

                      {/* Push Toggle */}
                      <label className="flex items-center gap-2 cursor-pointer">
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={notificationPrefs?.push?.[categoryKey] ?? true}
                            onChange={() => toggleNotificationPref('push', categoryKey)}
                            disabled={savingNotificationPrefs}
                            className="sr-only peer"
                          />
                          <div className={`w-10 h-5 rounded-full transition-colors ${
                            notificationPrefs?.push?.[categoryKey] ?? true
                              ? 'bg-violet-600' 
                              : 'bg-gray-300'
                          }`}>
                            <div className={`absolute w-4 h-4 bg-white rounded-full top-0.5 transition-transform ${
                              notificationPrefs?.push?.[categoryKey] ?? true ? 'translate-x-5' : 'translate-x-0.5'
                            }`} />
                          </div>
                        </div>
                        <span className="text-sm text-gray-600 flex items-center gap-1">
                          <Bell className="w-3.5 h-3.5" />
                          Push
                        </span>
                      </label>
                    </div>
                  </div>
                ))}

                {Object.keys(notificationCategories).length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Bell className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>Loading notification preferences...</p>
                  </div>
                )}
              </div>

              {/* Info about mandatory notifications */}
              <div className="mt-4 p-3 bg-amber-50 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">
                  <strong>Note:</strong> Some notifications cannot be disabled for security and compliance reasons, 
                  including password changes, 2FA updates, and payment-related notifications.
                </p>
              </div>
            </Card>

            {/* Logout All Confirmation Dialog */}
            <Dialog open={showLogoutAllConfirm} onOpenChange={setShowLogoutAllConfirm}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Logout from All Devices?</DialogTitle>
                  <DialogDescription>
                    This will terminate all active sessions except your current one.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="p-4 bg-amber-50 rounded-lg flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      You will remain logged in on this device. All other sessions will be terminated immediately.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" onClick={() => setShowLogoutAllConfirm(false)}>
                      Cancel
                    </Button>
                    <Button className="flex-1" onClick={() => handleLogoutAllDevices(false)} disabled={loggingOutAll}>
                      {loggingOutAll ? "Logging out..." : "Logout Other Devices"}
                    </Button>
                  </div>
                  <Button variant="destructive" className="w-full" onClick={() => handleLogoutAllDevices(true)} disabled={loggingOutAll}>
                    Logout from ALL Devices (Including This One)
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* 2FA Verification Dialog */}
            <Dialog open={show2FAVerify} onOpenChange={(open) => {
              if (!open) handleCancel2FAChange();
            }}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-violet-600" />
                    {twoFactorEnabled ? "Disable" : "Enable"} Two-Factor Authentication
                  </DialogTitle>
                  <DialogDescription>
                    We sent a verification code to your email. Enter it to confirm this change.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleConfirm2FAChange} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Enter 6-digit code</label>
                    <Input
                      type="text"
                      value={twoFAVerifyCode}
                      onChange={(e) => setTwoFAVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="text-center text-2xl tracking-widest font-mono"
                      maxLength={6}
                      autoFocus
                      required
                    />
                  </div>
                  <div className={`p-3 rounded-lg text-sm ${twoFactorEnabled ? 'bg-amber-50 text-amber-800' : 'bg-blue-50 text-blue-800'}`}>
                    <p>
                      {twoFactorEnabled 
                        ? "After disabling, your account will be less secure. Anyone with your password can access your account."
                        : "Once enabled, you'll need to enter a code from your email each time you sign in."}
                    </p>
                  </div>
                  <Button type="submit" className="w-full" disabled={verifying2FAEnable || twoFAVerifyCode.length !== 6}>
                    {verifying2FAEnable ? "Verifying..." : `${twoFactorEnabled ? "Disable" : "Enable"} 2FA`}
                  </Button>
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={handleResend2FACode}
                      className="text-sm text-violet-600 hover:text-violet-700"
                    >
                      Didn&apos;t receive the code? Resend
                    </button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            
            {/* Email Change Dialog */}
            <Dialog open={showEmailChange} onOpenChange={(open) => {
              setShowEmailChange(open);
              if (!open) {
                setEmailChangeStep(1);
                setEmailChangeData({ otp: "", new_email: "" });
              }
            }}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Change Email Address</DialogTitle>
                </DialogHeader>
                
                {emailChangeStep === 1 && (
                  <div className="space-y-4">
                    <p className="text-gray-600">We&apos;ll send a verification code to your current email ({user?.email}) to confirm this change.</p>
                    <Button onClick={handleRequestEmailChange} className="w-full" disabled={emailChangeLoading}>
                      {emailChangeLoading ? "Sending..." : "Send Verification Code"}
                    </Button>
                  </div>
                )}
                
                {emailChangeStep === 2 && (
                  <div className="space-y-4">
                    <p className="text-gray-600">Enter the 6-digit code sent to {user?.email}</p>
                    <Input
                      value={emailChangeData.otp}
                      onChange={(e) => setEmailChangeData({...emailChangeData, otp: e.target.value})}
                      placeholder="123456"
                      maxLength={6}
                    />
                    <Button onClick={handleVerifyEmailChange} className="w-full" disabled={emailChangeLoading}>
                      {emailChangeLoading ? "Verifying..." : "Verify Code"}
                    </Button>
                  </div>
                )}
                
                {emailChangeStep === 3 && (
                  <div className="space-y-4">
                    <p className="text-gray-600">Enter your new email address</p>
                    <Input
                      type="email"
                      value={emailChangeData.new_email}
                      onChange={(e) => setEmailChangeData({...emailChangeData, new_email: e.target.value})}
                      placeholder="newemail@example.com"
                    />
                    <Button onClick={handleCompleteEmailChange} className="w-full" disabled={emailChangeLoading}>
                      {emailChangeLoading ? "Updating..." : "Update Email"}
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
            
            {/* Password Change Dialog */}
            <Dialog open={showPasswordChange} onOpenChange={setShowPasswordChange}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Change Password</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                    <Input
                      type="password"
                      value={passwordForm.current_password}
                      onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                    <Input
                      type="password"
                      value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                    <Input
                      type="password"
                      value={passwordForm.confirm_password}
                      onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={passwordLoading}>
                    {passwordLoading ? "Updating..." : "Update Password"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Super Admin Tab */}
        {activeTab === "admin" && isSuperAdmin && (
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-6">Admin Panel</h1>
            <Button onClick={() => navigate('/backoffice/dashboard')} className="gap-2">
              <Shield className="w-4 h-4" /> Open Admin Panel
            </Button>
          </div>
        )}
        </div>
      </main>

      {/* Promotional Popup */}
      <PromotionalPopup page="dashboard" isLoggedIn={true} />

      {/* Plan Change Dialog */}
      <Dialog open={showChangePlan} onOpenChange={setShowChangePlan}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {(!subscription || subscription.status === 'cancelled') ? 'Activate a Plan' : 'Change Your Plan'}
            </DialogTitle>
            <DialogDescription>
              {(!subscription || subscription.status === 'cancelled') 
                ? 'Choose a plan to get started with SeatPilot. You can change your plan anytime.'
                : 'Select a new plan. Upgrades require immediate payment of the prorated difference. Downgrades take effect at the end of your current billing period.'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            {/* Current Plan Info */}
            {subscription && (
              <div className="p-4 bg-violet-50 rounded-lg border border-violet-200">
                <p className="text-sm text-violet-600 font-medium">Current Plan</p>
                <p className="text-lg font-bold text-violet-900">
                  {subscription.plan_id === 'free_trial' ? 'Free Trial' :
                   subscription.plan_id === 'pay_as_you_go' ? 'Pay As You Go' :
                   subscription.plan_id === 'monthly' ? 'Monthly Plan' :
                   subscription.plan_id === 'enterprise' ? 'Enterprise' : subscription.plan_id}
                </p>
                {subscription.expires_at && (
                  <p className="text-sm text-violet-600">Valid until: {formatDate(subscription.expires_at)}</p>
                )}
              </div>
            )}

            {/* Available Plans */}
            <div className="grid gap-3">
              {(availablePlans.length > 0 ? availablePlans : [
                { id: 'pay_as_you_go', name: 'Pay As You Go', price: '£0.50/seat', description: 'No monthly fee. Pay per booking. Card required.', needsCard: true },
                { id: 'monthly', name: 'Monthly Plan', price: '£50/month', description: '10,000 seats included, then £0.30/seat.', needsCard: true },
                { id: 'monthly_yearly', name: 'Yearly Plan', price: '£480/year', description: 'Save 20%! 10,000 seats/month, £0.25/seat overage.', needsCard: true },
                { id: 'enterprise', name: 'Enterprise', price: 'Custom', description: 'Unlimited everything. Contact us for pricing.', needsCard: false, isEnterprise: true },
              ]).filter(p => p.id !== subscription?.plan_id).map(plan => (
                <div 
                  key={plan.id}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedNewPlan === plan.id 
                      ? plan.isEnterprise ? 'border-purple-500 bg-purple-50' : 'border-violet-500 bg-violet-50' 
                      : 'border-gray-200 hover:border-violet-300'
                  }`}
                  onClick={() => previewPlanChange(plan.id)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-gray-900">{plan.name}</p>
                      <p className="text-sm text-gray-500">{plan.description}</p>
                      {plan.isEnterprise && (
                        <p className="text-xs text-purple-600 mt-1">📧 Submit enquiry - our team will contact you</p>
                      )}
                    </div>
                    <p className={`text-lg font-bold ${plan.isEnterprise ? 'text-purple-600' : 'text-violet-600'}`}>{plan.price}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Enterprise Ticket Form */}
            {showEnterpriseTicket && selectedNewPlan === 'enterprise' && (
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-purple-800 mb-3">🏢 Enterprise Plan Enquiry</h4>
                <p className="text-sm text-purple-700 mb-3">
                  Tell us about your requirements and our team will prepare a custom quote for you.
                </p>
                <textarea
                  className="w-full p-3 border rounded-lg text-sm"
                  rows={4}
                  placeholder="Please describe:&#10;- Expected number of events per month&#10;- Expected seat bookings&#10;- Any special requirements (white-label, custom integrations, SLA)&#10;- Preferred billing cycle"
                  value={enterpriseRequirements}
                  onChange={(e) => setEnterpriseRequirements(e.target.value)}
                />
                <div className="flex gap-2 mt-3">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setShowEnterpriseTicket(false);
                      setSelectedNewPlan(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    size="sm"
                    className="bg-purple-600 hover:bg-purple-700"
                    onClick={submitEnterpriseEnquiry}
                    disabled={changingPlan}
                  >
                    {changingPlan ? "Submitting..." : "Submit Enquiry"}
                  </Button>
                </div>
              </div>
            )}

            {/* Preview - Only for non-Enterprise plans */}
            {planChangePreview && selectedNewPlan !== 'enterprise' && (
              <div className="space-y-4">
                {/* Timing Choice */}
                <div className="p-4 bg-gray-50 rounded-lg border">
                  <p className="font-medium text-gray-900 mb-3">When would you like to switch?</p>
                  <div className="space-y-2">
                    <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      changeImmediately ? 'border-violet-500 bg-violet-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}>
                      <input
                        type="radio"
                        name="timing"
                        checked={changeImmediately}
                        onChange={() => setChangeImmediately(true)}
                        className="mt-1"
                      />
                      <div>
                        <p className="font-medium text-gray-900">Switch Immediately</p>
                        {planChangePreview.is_upgrade ? (
                          <p className="text-sm text-gray-600">
                            Pay <strong>{formatCurrency(planChangePreview.amount_due)}</strong> now (prorated for {planChangePreview.days_remaining || 0} remaining days)
                          </p>
                        ) : (
                          <p className="text-sm text-gray-600">
                            Switch to new plan today. No refund for unused time.
                          </p>
                        )}
                      </div>
                    </label>
                    
                    <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      !changeImmediately ? 'border-violet-500 bg-violet-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}>
                      <input
                        type="radio"
                        name="timing"
                        checked={!changeImmediately}
                        onChange={() => setChangeImmediately(false)}
                        className="mt-1"
                      />
                      <div>
                        <p className="font-medium text-gray-900">Switch at End of Billing Period</p>
                        <p className="text-sm text-gray-600">
                          Current plan remains active until <strong>{formatDate(planChangePreview.effective_date || subscription?.expires_at)}</strong>
                        </p>
                        <p className="text-sm text-gray-600">
                          New plan starts: <strong>{formatDate(planChangePreview.effective_date || subscription?.expires_at)}</strong>
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Summary */}
                <div className={`p-4 rounded-lg border ${
                  planChangePreview.is_upgrade ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'
                }`}>
                  <p className={`font-medium ${planChangePreview.is_upgrade ? 'text-green-800' : 'text-amber-800'}`}>
                    {planChangePreview.is_upgrade ? '⬆️ Upgrade Summary' : '⬇️ Downgrade Summary'}
                  </p>
                  <div className="mt-2 text-sm space-y-1">
                    <p className={planChangePreview.is_upgrade ? 'text-green-700' : 'text-amber-700'}>
                      <strong>New Plan:</strong> {planChangePreview.new_plan_name || selectedNewPlan?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </p>
                    <p className={planChangePreview.is_upgrade ? 'text-green-700' : 'text-amber-700'}>
                      <strong>Plan Price:</strong> {formatCurrency(planChangePreview.new_plan_price || 0)}
                      {planChangePreview.prices_include_vat ? ' (inc. VAT)' : ''}
                    </p>
                    {planChangePreview.proration_credit > 0 && changeImmediately && (
                      <p className="text-green-600">
                        <strong>Credit (unused plan):</strong> -{formatCurrency(planChangePreview.proration_credit)}
                      </p>
                    )}
                    {changeImmediately ? (
                      <>
                        {planChangePreview.vat_amount > 0 && !planChangePreview.prices_include_vat && (
                          <p className={planChangePreview.is_upgrade ? 'text-green-700' : 'text-amber-700'}>
                            <strong>VAT ({planChangePreview.vat_rate}%):</strong> {formatCurrency(planChangePreview.vat_amount)}
                          </p>
                        )}
                        <p className={`font-bold ${planChangePreview.is_upgrade ? 'text-green-800' : 'text-amber-800'}`}>
                          <strong>Total to Pay:</strong> {formatCurrency(planChangePreview.amount_due || 0)}
                        </p>
                        <p className={planChangePreview.is_upgrade ? 'text-green-700' : 'text-amber-700'}>
                          <strong>Effective:</strong> Immediately
                        </p>
                      </>
                    ) : (
                      <>
                        <p className={planChangePreview.is_upgrade ? 'text-green-700' : 'text-amber-700'}>
                          <strong>Charge Today:</strong> £0.00
                        </p>
                        <p className={planChangePreview.is_upgrade ? 'text-green-700' : 'text-amber-700'}>
                          <strong>Effective:</strong> {formatDate(planChangePreview.effective_date || subscription?.expires_at)}
                        </p>
                      </>
                    )}
                    {planChangePreview.downgrade_note && (
                      <p className="text-amber-600 text-xs mt-2 p-2 bg-amber-100 rounded">
                        {planChangePreview.downgrade_note}
                      </p>
                    )}
                    {planChangePreview.api_key_impact?.action_required && (
                      <div className="text-red-600 text-xs mt-2 p-2 bg-red-50 border border-red-200 rounded" data-testid="api-key-downgrade-warning">
                        <strong>API Keys:</strong> Your new plan allows {planChangePreview.api_key_impact.new_plan_limit} keys. 
                        You have {planChangePreview.api_key_impact.current_active_keys} active keys. 
                        You&apos;ll need to choose which {planChangePreview.api_key_impact.new_plan_limit} to keep.
                      </div>
                    )}
                    {planChangePreview.api_key_impact && !planChangePreview.api_key_impact.action_required && planChangePreview.api_key_impact.message && (
                      <p className="text-green-600 text-xs mt-2 p-2 bg-green-50 rounded">
                        {planChangePreview.api_key_impact.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => {
              setShowChangePlan(false);
              setSelectedNewPlan(null);
              setPlanChangePreview(null);
              setShowEnterpriseTicket(false);
              setEnterpriseRequirements("");
              setChangeImmediately(true);
            }}>
              Cancel
            </Button>
            {selectedNewPlan !== 'enterprise' && planChangePreview && (
              <Button 
                onClick={confirmPlanChange}
                disabled={!selectedNewPlan || changingPlan}
                className="bg-violet-600 hover:bg-violet-700"
              >
                {changingPlan ? "Processing..." : 
                  changeImmediately 
                    ? (planChangePreview?.amount_due > 0 
                        ? `Pay ${formatCurrency(planChangePreview?.amount_due || 0)} & Switch Now` 
                        : "Switch Now")
                    : `Schedule Change for ${formatDate(planChangePreview?.effective_date || subscription?.expires_at)}`
                }
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* API Key Selection Dialog (shown during plan downgrade) */}
      <Dialog open={showApiKeySelection} onOpenChange={(open) => { if (!open) { setShowApiKeySelection(false); setApiKeySelectionData(null); setSelectedKeysToKeep([]); } }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-amber-600">Select API Keys to Keep</DialogTitle>
            <DialogDescription>
              Your new plan allows <strong>{apiKeySelectionData?.new_plan_limit}</strong> API keys. 
              You currently have <strong>{apiKeySelectionData?.current_active_keys}</strong> active keys.
              Please select which {apiKeySelectionData?.new_plan_limit} key{apiKeySelectionData?.new_plan_limit > 1 ? 's' : ''} you want to keep.
              The remaining {apiKeySelectionData?.keys_to_remove} key{apiKeySelectionData?.keys_to_remove > 1 ? 's' : ''} will be revoked.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-3">
            {(apiKeySelectionData?.active_keys || []).map((key) => (
              <div 
                key={key.key_id}
                onClick={() => toggleKeySelection(key.key_id)}
                data-testid={`api-key-select-${key.key_id}`}
                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedKeysToKeep.includes(key.key_id)
                    ? 'border-violet-500 bg-violet-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      selectedKeysToKeep.includes(key.key_id)
                        ? 'border-violet-500 bg-violet-500'
                        : 'border-gray-300'
                    }`}>
                      {selectedKeysToKeep.includes(key.key_id) && (
                        <svg className="w-3 h-3 text-white" viewBox="0 0 12 12"><path d="M3.5 6L5.5 8L8.5 4" stroke="currentColor" strokeWidth="2" fill="none"/></svg>
                      )}
                    </div>
                    <span className="font-medium text-sm">{key.name}</span>
                  </div>
                  {key.last_used_at && (
                    <span className="text-[10px] text-gray-400">Last used: {new Date(key.last_used_at).toLocaleDateString()}</span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1 ml-7">
                  Created: {key.created_at ? new Date(key.created_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between pt-2 border-t">
            <span className="text-sm text-gray-500">
              {selectedKeysToKeep.length} of {apiKeySelectionData?.new_plan_limit} selected
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => {
                setShowApiKeySelection(false);
                setApiKeySelectionData(null);
                setSelectedKeysToKeep([]);
              }}>
                Cancel
              </Button>
              <Button 
                size="sm"
                disabled={selectedKeysToKeep.length !== apiKeySelectionData?.new_plan_limit || processingKeySelection}
                onClick={confirmKeySelectionAndChangePlan}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="confirm-key-selection-btn"
              >
                {processingKeySelection ? "Processing..." : `Keep ${selectedKeysToKeep.length} Keys & Change Plan`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Plan Dialog - Multi-Step Retention Flow */}
      <Dialog open={showCancelPlan} onOpenChange={(open) => { setShowCancelPlan(open); if (!open) { setCancelReason(""); setCancelImmediately(false); } }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          
          {/* STEP 1: Discount Offer (Skip for PAYG or if already used or disabled) */}
          {cancelStep === 1 && subscription?.plan_id !== 'pay_as_you_go' && retentionConfig?.discount_enabled && !retentionConfig?.discount_already_used && (
            <>
              <DialogHeader>
                <DialogTitle className="text-amber-600">Wait! We&apos;d love you to stay</DialogTitle>
                <DialogDescription>
                  Before you go, we have a special offer just for you.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="p-5 bg-gradient-to-br from-violet-50 to-indigo-50 border-2 border-violet-200 rounded-xl text-center">
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-violet-100 rounded-full mb-3">
                    <span className="text-2xl font-bold text-violet-600">{retentionConfig?.discount_percent || 20}%</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Get {retentionConfig?.discount_percent || 20}% Off Your Plan</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    We&apos;ll apply a <strong>{retentionConfig?.discount_percent || 20}% discount</strong> to your <strong>{subscription?.plan_name || subscription?.plan_id?.replace(/_/g, ' ')}</strong> plan immediately.
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg border text-sm text-gray-600">
                  <p className="font-medium text-gray-700 mb-1">What you keep:</p>
                  <ul className="space-y-1 text-xs">
                    <li className="flex items-center gap-1.5"><span className="text-green-500">&#10003;</span> All your layouts and configurations</li>
                    <li className="flex items-center gap-1.5"><span className="text-green-500">&#10003;</span> Active API keys and integrations</li>
                    <li className="flex items-center gap-1.5"><span className="text-green-500">&#10003;</span> Event links and booking history</li>
                  </ul>
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" size="sm" onClick={() => {
                  const freeAvailable = retentionConfig?.free_month_enabled && !retentionConfig?.free_month_already_used;
                  setCancelStep(freeAvailable ? 2 : 3);
                }} className="text-gray-500">
                  No thanks, continue
                </Button>
                <Button 
                  className="bg-gradient-to-r from-violet-600 to-indigo-600"
                  disabled={applyingRetention}
                  data-testid="accept-discount-btn"
                  onClick={async () => {
                    setApplyingRetention(true);
                    try {
                      const res = await axios.post(`${getAPI()}/subscriptions/cancel-retention/accept-discount`, {}, { withCredentials: true });
                      toast.success(res.data.message);
                      setShowCancelPlan(false);
                      fetchCompanyData();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Failed to apply discount');
                    } finally { setApplyingRetention(false); }
                  }}
                >
                  {applyingRetention ? "Applying..." : `Apply ${retentionConfig?.discount_percent || 20}% Discount`}
                </Button>
              </div>
            </>
          )}

          {/* STEP 2: Free Month Offer (Skip for PAYG or if already used or disabled) */}
          {cancelStep === 2 && subscription?.plan_id !== 'pay_as_you_go' && retentionConfig?.free_month_enabled && !retentionConfig?.free_month_already_used && (
            <>
              <DialogHeader>
                <DialogTitle className="text-blue-600">One more thing...</DialogTitle>
                <DialogDescription>
                  How about {(retentionConfig?.free_months || 1) > 1 ? `${retentionConfig.free_months} free months` : 'a free month'} on us? No strings attached.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="p-5 bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-xl text-center">
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-100 rounded-full mb-3">
                    <span className="text-xl font-bold text-blue-600">FREE</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">{(retentionConfig?.free_months || 1)} Month{(retentionConfig?.free_months || 1) > 1 ? 's' : ''} Free</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Take {(retentionConfig?.free_months || 1) > 1 ? `${retentionConfig.free_months} full months` : 'a full month'} to explore features you may have missed. <strong>Zero charge.</strong>
                  </p>
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" size="sm" onClick={() => setCancelStep(3)} className="text-gray-500">
                  No thanks, I want to cancel
                </Button>
                <Button 
                  className="bg-gradient-to-r from-blue-600 to-cyan-600"
                  disabled={applyingRetention}
                  data-testid="accept-free-month-btn"
                  onClick={async () => {
                    setApplyingRetention(true);
                    try {
                      const res = await axios.post(`${getAPI()}/subscriptions/cancel-retention/accept-free-month`, {}, { withCredentials: true });
                      toast.success(res.data.message);
                      setShowCancelPlan(false);
                      fetchCompanyData();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Failed to apply free month');
                    } finally { setApplyingRetention(false); }
                  }}
                >
                  {applyingRetention ? "Applying..." : `Get ${(retentionConfig?.free_months || 1)} Month${(retentionConfig?.free_months || 1) > 1 ? 's' : ''} Free`}
                </Button>
              </div>
            </>
          )}

          {/* STEP 3: Final Cancel Confirmation */}
          {(cancelStep === 3 || subscription?.plan_id === 'pay_as_you_go') && (
            <>
              <DialogHeader>
                <DialogTitle className="text-red-600">Cancel Subscription</DialogTitle>
                <DialogDescription>
                  Are you sure you want to cancel your subscription? You will lose access to premium features.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Current Plan Info */}
                <div className="p-4 bg-gray-50 rounded-lg border">
                  <p className="text-sm text-gray-500">Current Plan</p>
                  <p className="font-semibold text-lg text-gray-900">
                    {subscription?.plan_name || subscription?.plan_id?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </p>
                  {subscription?.expires_at && (
                    <p className="text-sm text-gray-500">Renews: {formatDate(subscription.expires_at)}</p>
                  )}
                </div>

                {/* When to Cancel - Only show for term-based plans (not PAYG) */}
                {subscription?.plan_id !== 'pay_as_you_go' ? (
                  <div>
                    <Label className="mb-3 block font-medium">When should the cancellation take effect?</Label>
                    <div className="space-y-2">
                      <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        !cancelImmediately ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:bg-gray-50'
                      }`}>
                        <input type="radio" name="cancelTiming" checked={!cancelImmediately} onChange={() => setCancelImmediately(false)} className="mt-1" />
                        <div>
                          <p className="font-medium text-gray-900">At End of Billing Period (Recommended)</p>
                          <p className="text-sm text-gray-600">Continue using your plan until <strong>{formatDate(subscription?.expires_at || new Date())}</strong></p>
                          <p className="text-xs text-amber-600 mt-1">No refund required - you&apos;ve already paid for this period</p>
                        </div>
                      </label>
                      <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        cancelImmediately ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:bg-gray-50'
                      }`}>
                        <input type="radio" name="cancelTiming" checked={cancelImmediately} onChange={() => setCancelImmediately(true)} className="mt-1" />
                        <div>
                          <p className="font-medium text-gray-900">Cancel Immediately</p>
                          <p className="text-sm text-gray-600">Lose access right now. No refund for remaining time.</p>
                        </div>
                      </label>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="font-medium text-amber-800">Pay As You Go Plan</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Your subscription will be cancelled immediately. There is no billing period for Pay As You Go plans.
                    </p>
                  </div>
                )}

                {/* Reason */}
                <div>
                  <Label className="mb-2 block">Why are you cancelling? (Optional)</Label>
                  <textarea
                    className="w-full p-3 border rounded-lg text-sm"
                    rows={3}
                    placeholder="Help us improve by sharing your reason..."
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                  />
                </div>

                {/* Warning */}
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700"><strong>What happens after cancellation:</strong></p>
                  <ul className="text-sm text-red-600 mt-1 list-disc list-inside">
                    <li>Cannot create new layouts</li>
                    <li>Cannot link events to layouts</li>
                    <li>Existing layouts become read-only</li>
                    <li><strong>All API keys will be revoked immediately</strong></li>
                    <li>Any active integrations will stop working</li>
                  </ul>
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => {
                  setShowCancelPlan(false);
                  setCancelReason("");
                  setCancelImmediately(false);
                  setCancelStep(subscription?.plan_id === 'pay_as_you_go' ? 3 : 1);
                }}>
                  Keep My Plan
                </Button>
                <Button 
                  variant="destructive"
                  onClick={cancelPlan}
                  disabled={cancellingPlan}
                >
                  {cancellingPlan ? "Cancelling..." : (subscription?.plan_id === 'pay_as_you_go' || cancelImmediately) ? "Cancel Now" : "Schedule Cancellation"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* White Label Cancel Dialog */}
      <Dialog open={showCancelWhiteLabel} onOpenChange={setShowCancelWhiteLabel}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-red-600">Cancel White Label</DialogTitle>
            <DialogDescription>
              Cancel your white-label subscription. &quot;Powered by SeatPilot&quot; will reappear on your checkout widgets.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Current Status */}
            <div className="p-4 bg-gray-50 rounded-lg border">
              <p className="text-sm text-gray-500">White Label Subscription</p>
              <p className="font-semibold text-lg text-gray-900">£20.00/month</p>
            </div>

            {/* When to Cancel */}
            <div>
              <Label className="mb-3 block font-medium">When should the cancellation take effect?</Label>
              <div className="space-y-2">
                <div 
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    !cancelWhiteLabelImmediately ? 'border-violet-500 bg-violet-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setCancelWhiteLabelImmediately(false)}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded-full border-2 ${!cancelWhiteLabelImmediately ? 'border-violet-500 bg-violet-500' : 'border-gray-300'}`}>
                      {!cancelWhiteLabelImmediately && <div className="w-full h-full rounded-full bg-white scale-50" />}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">At end of billing period</p>
                      <p className="text-xs text-gray-500">Keep white-label until your current period ends</p>
                    </div>
                  </div>
                </div>
                <div 
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    cancelWhiteLabelImmediately ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setCancelWhiteLabelImmediately(true)}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded-full border-2 ${cancelWhiteLabelImmediately ? 'border-red-500 bg-red-500' : 'border-gray-300'}`}>
                      {cancelWhiteLabelImmediately && <div className="w-full h-full rounded-full bg-white scale-50" />}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Cancel immediately</p>
                      <p className="text-xs text-red-600">&quot;Powered by SeatPilot&quot; will reappear immediately</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Warning */}
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">
                <strong>No refunds:</strong> Cancellation is final. No partial refunds will be issued.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => {
              setShowCancelWhiteLabel(false);
              setCancelWhiteLabelImmediately(false);
            }}>
              Keep White Label
            </Button>
            <Button 
              variant="destructive"
              disabled={cancellingWhiteLabel}
              onClick={async () => {
                setCancellingWhiteLabel(true);
                try {
                  const res = await axios.post(`${getAPI()}/subscriptions/white-label/cancel`, {
                    immediate: cancelWhiteLabelImmediately
                  }, { withCredentials: true });
                  toast.success(res.data.message);
                  setShowCancelWhiteLabel(false);
                  setCancelWhiteLabelImmediately(false);
                  fetchData();
                  fetchCompanyData();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Failed to cancel");
                } finally {
                  setCancellingWhiteLabel(false);
                }
              }}
            >
              {cancellingWhiteLabel ? "Cancelling..." : cancelWhiteLabelImmediately ? "Cancel Now" : "Schedule Cancellation"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Role Manager Dialog */}
      <Dialog open={showRoleManager} onOpenChange={setShowRoleManager}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingRole ? 'Edit Role' : 'Create Custom Role'}</DialogTitle>
            <DialogDescription>
              Define permissions for this role. Team members with this role will only have access to selected features.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            {/* Predefined Templates */}
            {!editingRole && (
              <div className="mb-4">
                <Label className="mb-2 block">Start from a template</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { 
                      name: 'Admin', 
                      permissions: ['view_layouts', 'edit_layouts', 'delete_layouts', 'manage_events', 'manage_organisers', 'view_reports', 'export_data', 'view_billing', 'manage_billing', 'view_api_keys', 'manage_api_keys', 'manage_team', 'manage_roles', 'view_integration', 'manage_integration', 'view_support', 'manage_support', 'view_settings', 'manage_settings', 'view_audit_log'],
                      description: 'Full access to everything'
                    },
                    { 
                      name: 'Layout Manager', 
                      permissions: ['view_layouts', 'edit_layouts', 'manage_events', 'manage_organisers', 'view_reports'],
                      description: 'Full layout and event management'
                    },
                    { 
                      name: 'Event Coordinator', 
                      permissions: ['view_layouts', 'manage_events', 'view_reports', 'manage_organisers'],
                      description: 'Manage events and organisers'
                    },
                    { 
                      name: 'Finance/Reports', 
                      permissions: ['view_layouts', 'view_reports', 'view_billing', 'export_data'],
                      description: 'View reports and billing'
                    },
                    { 
                      name: 'Viewer Only', 
                      permissions: ['view_layouts', 'view_reports'],
                      description: 'Read-only access'
                    },
                    { 
                      name: 'Team Lead', 
                      permissions: ['view_layouts', 'edit_layouts', 'manage_events', 'view_reports', 'manage_team', 'manage_organisers', 'view_audit_log'],
                      description: 'Full access including team management'
                    },
                    { 
                      name: 'API Developer', 
                      permissions: ['view_layouts', 'view_api_keys', 'manage_api_keys', 'view_integration', 'manage_integration', 'view_reports'],
                      description: 'API integration access'
                    },
                    { 
                      name: 'Support Agent', 
                      permissions: ['view_layouts', 'view_reports', 'view_support', 'manage_support'],
                      description: 'Handle support tickets'
                    },
                  ].map(template => (
                    <button
                      key={template.name}
                      type="button"
                      onClick={() => setRoleForm({ name: template.name, permissions: template.permissions })}
                      className={`p-3 text-left border rounded-lg hover:border-violet-300 hover:bg-violet-50 transition-colors ${
                        roleForm.name === template.name ? 'border-violet-500 bg-violet-50' : 'border-gray-200'
                      }`}
                    >
                      <p className="font-medium text-sm text-gray-900">{template.name}</p>
                      <p className="text-xs text-gray-500">{template.description}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label>Role Name</Label>
              <Input 
                value={roleForm.name}
                onChange={(e) => setRoleForm({...roleForm, name: e.target.value})}
                placeholder="e.g., Layout Editor, Event Manager"
              />
            </div>

            <div>
              <Label className="mb-2 block">Permissions</Label>
              <p className="text-xs text-gray-500 mb-3">Set access level for each area (similar to Stripe)</p>
              <div className="space-y-4 max-h-80 overflow-y-auto p-3 border rounded-lg">
                {/* Layouts Section */}
                <div className="pb-3 border-b">
                  <p className="font-medium text-gray-900 text-sm mb-2">📐 Layouts</p>
                  <div className="flex items-center gap-4 ml-4">
                    {['none', 'view', 'edit', 'full'].map(level => (
                      <label key={level} className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="layouts_access"
                          checked={
                            level === 'full' ? roleForm.permissions.includes('delete_layouts') :
                            level === 'edit' ? roleForm.permissions.includes('edit_layouts') && !roleForm.permissions.includes('delete_layouts') :
                            level === 'view' ? roleForm.permissions.includes('view_layouts') && !roleForm.permissions.includes('edit_layouts') :
                            !roleForm.permissions.includes('view_layouts')
                          }
                          onChange={() => {
                            let perms = roleForm.permissions.filter(p => !['view_layouts','edit_layouts','delete_layouts'].includes(p));
                            if (level === 'view') perms.push('view_layouts');
                            if (level === 'edit') perms.push('view_layouts', 'edit_layouts');
                            if (level === 'full') perms.push('view_layouts', 'edit_layouts', 'delete_layouts');
                            setRoleForm({...roleForm, permissions: perms});
                          }}
                        />
                        <span className={`text-xs ${level === 'none' ? 'text-gray-400' : level === 'view' ? 'text-blue-600' : level === 'edit' ? 'text-amber-600' : 'text-green-600'}`}>
                          {level === 'none' ? 'None' : level === 'view' ? 'View' : level === 'edit' ? 'Edit' : 'Full'}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Events Section */}
                <div className="pb-3 border-b">
                  <p className="font-medium text-gray-900 text-sm mb-2">📅 Events & Organisers</p>
                  <div className="flex items-center gap-4 ml-4">
                    {['none', 'view', 'write'].map(level => (
                      <label key={level} className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="events_access"
                          checked={
                            level === 'write' ? roleForm.permissions.includes('manage_events') :
                            level === 'view' ? roleForm.permissions.includes('manage_organisers') && !roleForm.permissions.includes('manage_events') :
                            !roleForm.permissions.includes('manage_organisers')
                          }
                          onChange={() => {
                            let perms = roleForm.permissions.filter(p => !['manage_events','manage_organisers'].includes(p));
                            if (level === 'view') perms.push('manage_organisers');
                            if (level === 'write') perms.push('manage_events', 'manage_organisers');
                            setRoleForm({...roleForm, permissions: perms});
                          }}
                        />
                        <span className={`text-xs ${level === 'none' ? 'text-gray-400' : level === 'view' ? 'text-blue-600' : 'text-green-600'}`}>
                          {level === 'none' ? 'None' : level === 'view' ? 'View' : 'Write'}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Reports Section */}
                <div className="pb-3 border-b">
                  <p className="font-medium text-gray-900 text-sm mb-2">📊 Reports & Data</p>
                  <div className="flex items-center gap-4 ml-4">
                    {['none', 'view', 'export'].map(level => (
                      <label key={level} className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="reports_access"
                          checked={
                            level === 'export' ? roleForm.permissions.includes('export_data') :
                            level === 'view' ? roleForm.permissions.includes('view_reports') && !roleForm.permissions.includes('export_data') :
                            !roleForm.permissions.includes('view_reports')
                          }
                          onChange={() => {
                            let perms = roleForm.permissions.filter(p => !['view_reports','export_data'].includes(p));
                            if (level === 'view') perms.push('view_reports');
                            if (level === 'export') perms.push('view_reports', 'export_data');
                            setRoleForm({...roleForm, permissions: perms});
                          }}
                        />
                        <span className={`text-xs ${level === 'none' ? 'text-gray-400' : level === 'view' ? 'text-blue-600' : 'text-green-600'}`}>
                          {level === 'none' ? 'None' : level === 'view' ? 'View' : 'Export'}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Billing Section */}
                <div className="pb-3 border-b">
                  <p className="font-medium text-gray-900 text-sm mb-2">💳 Billing</p>
                  <div className="flex items-center gap-4 ml-4">
                    {['none', 'view', 'write'].map(level => (
                      <label key={level} className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="billing_access"
                          checked={
                            level === 'write' ? roleForm.permissions.includes('manage_billing') :
                            level === 'view' ? roleForm.permissions.includes('view_billing') && !roleForm.permissions.includes('manage_billing') :
                            !roleForm.permissions.includes('view_billing')
                          }
                          onChange={() => {
                            let perms = roleForm.permissions.filter(p => !['view_billing','manage_billing'].includes(p));
                            if (level === 'view') perms.push('view_billing');
                            if (level === 'write') perms.push('view_billing', 'manage_billing');
                            setRoleForm({...roleForm, permissions: perms});
                          }}
                        />
                        <span className={`text-xs ${level === 'none' ? 'text-gray-400' : level === 'view' ? 'text-blue-600' : 'text-green-600'}`}>
                          {level === 'none' ? 'None' : level === 'view' ? 'View' : 'Write'}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* API Keys Section */}
                <div className="pb-3 border-b">
                  <p className="font-medium text-gray-900 text-sm mb-2">🔑 API Keys</p>
                  <div className="flex items-center gap-4 ml-4">
                    {['none', 'view', 'manage'].map(level => (
                      <label key={level} className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="api_access"
                          checked={
                            level === 'manage' ? roleForm.permissions.includes('manage_api_keys') :
                            level === 'view' ? roleForm.permissions.includes('view_api_keys') && !roleForm.permissions.includes('manage_api_keys') :
                            !roleForm.permissions.includes('view_api_keys')
                          }
                          onChange={() => {
                            let perms = roleForm.permissions.filter(p => !['view_api_keys','manage_api_keys'].includes(p));
                            if (level === 'view') perms.push('view_api_keys');
                            if (level === 'manage') perms.push('view_api_keys', 'manage_api_keys');
                            setRoleForm({...roleForm, permissions: perms});
                          }}
                        />
                        <span className={`text-xs ${level === 'none' ? 'text-gray-400' : level === 'view' ? 'text-blue-600' : 'text-green-600'}`}>
                          {level === 'none' ? 'None' : level === 'view' ? 'View' : 'Manage'}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Team Section */}
                <div className="pb-3 border-b">
                  <p className="font-medium text-gray-900 text-sm mb-2">👥 Team Management</p>
                  <div className="flex items-center gap-4 ml-4">
                    {['none', 'invite', 'full'].map(level => (
                      <label key={level} className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="team_access"
                          checked={
                            level === 'full' ? roleForm.permissions.includes('manage_roles') :
                            level === 'invite' ? roleForm.permissions.includes('manage_team') && !roleForm.permissions.includes('manage_roles') :
                            !roleForm.permissions.includes('manage_team')
                          }
                          onChange={() => {
                            let perms = roleForm.permissions.filter(p => !['manage_team','manage_roles'].includes(p));
                            if (level === 'invite') perms.push('manage_team');
                            if (level === 'full') perms.push('manage_team', 'manage_roles');
                            setRoleForm({...roleForm, permissions: perms});
                          }}
                        />
                        <span className={`text-xs ${level === 'none' ? 'text-gray-400' : level === 'invite' ? 'text-blue-600' : 'text-green-600'}`}>
                          {level === 'none' ? 'None' : level === 'invite' ? 'Invite Only' : 'Full'}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Integration Section */}
                <div className="pb-3 border-b">
                  <p className="font-medium text-gray-900 text-sm mb-2">🔗 Integration</p>
                  <div className="flex items-center gap-4 ml-4">
                    {['none', 'view', 'manage'].map(level => (
                      <label key={level} className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="integration_access"
                          checked={
                            level === 'manage' ? roleForm.permissions.includes('manage_integration') :
                            level === 'view' ? roleForm.permissions.includes('view_integration') && !roleForm.permissions.includes('manage_integration') :
                            !roleForm.permissions.includes('view_integration')
                          }
                          onChange={() => {
                            let perms = roleForm.permissions.filter(p => !['view_integration','manage_integration'].includes(p));
                            if (level === 'view') perms.push('view_integration');
                            if (level === 'manage') perms.push('view_integration', 'manage_integration');
                            setRoleForm({...roleForm, permissions: perms});
                          }}
                        />
                        <span className={`text-xs ${level === 'none' ? 'text-gray-400' : level === 'view' ? 'text-blue-600' : 'text-green-600'}`}>
                          {level === 'none' ? 'None' : level === 'view' ? 'View' : 'Manage'}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Support Section */}
                <div className="pb-3 border-b">
                  <p className="font-medium text-gray-900 text-sm mb-2">🎧 Support</p>
                  <div className="flex items-center gap-4 ml-4">
                    {['none', 'view', 'manage'].map(level => (
                      <label key={level} className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="support_access"
                          checked={
                            level === 'manage' ? roleForm.permissions.includes('manage_support') :
                            level === 'view' ? roleForm.permissions.includes('view_support') && !roleForm.permissions.includes('manage_support') :
                            !roleForm.permissions.includes('view_support')
                          }
                          onChange={() => {
                            let perms = roleForm.permissions.filter(p => !['view_support','manage_support'].includes(p));
                            if (level === 'view') perms.push('view_support');
                            if (level === 'manage') perms.push('view_support', 'manage_support');
                            setRoleForm({...roleForm, permissions: perms});
                          }}
                        />
                        <span className={`text-xs ${level === 'none' ? 'text-gray-400' : level === 'view' ? 'text-blue-600' : 'text-green-600'}`}>
                          {level === 'none' ? 'None' : level === 'view' ? 'View' : 'Manage'}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Settings Section */}
                <div className="pb-3 border-b">
                  <p className="font-medium text-gray-900 text-sm mb-2">⚙️ Settings</p>
                  <div className="flex items-center gap-4 ml-4">
                    {['none', 'view', 'manage'].map(level => (
                      <label key={level} className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="settings_access"
                          checked={
                            level === 'manage' ? roleForm.permissions.includes('manage_settings') :
                            level === 'view' ? roleForm.permissions.includes('view_settings') && !roleForm.permissions.includes('manage_settings') :
                            !roleForm.permissions.includes('view_settings')
                          }
                          onChange={() => {
                            let perms = roleForm.permissions.filter(p => !['view_settings','manage_settings'].includes(p));
                            if (level === 'view') perms.push('view_settings');
                            if (level === 'manage') perms.push('view_settings', 'manage_settings');
                            setRoleForm({...roleForm, permissions: perms});
                          }}
                        />
                        <span className={`text-xs ${level === 'none' ? 'text-gray-400' : level === 'view' ? 'text-blue-600' : 'text-green-600'}`}>
                          {level === 'none' ? 'None' : level === 'view' ? 'View' : 'Manage'}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Activity Log */}
                <div className="pb-3 border-b">
                  <p className="font-medium text-gray-900 text-sm mb-2">📋 Activity Log</p>
                  <label className="flex items-center gap-2 ml-4 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={roleForm.permissions.includes('view_audit_log')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setRoleForm({...roleForm, permissions: [...roleForm.permissions, 'view_audit_log']});
                        } else {
                          setRoleForm({...roleForm, permissions: roleForm.permissions.filter(p => p !== 'view_audit_log')});
                        }
                      }}
                    />
                    <span className="text-xs text-gray-600">Can view team activity log</span>
                  </label>
                </div>

                {/* Rewards & Referrals Section */}
                <div className="pb-3 border-b">
                  <p className="font-medium text-gray-900 text-sm mb-2">🎁 Rewards & Referrals</p>
                  <div className="flex items-center gap-4 ml-4">
                    {['none', 'view', 'manage'].map(level => (
                      <label key={level} className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="rewards_access"
                          checked={
                            level === 'manage' ? roleForm.permissions.includes('manage_referrals') :
                            level === 'view' ? roleForm.permissions.includes('view_referrals') && !roleForm.permissions.includes('manage_referrals') :
                            !roleForm.permissions.includes('view_referrals')
                          }
                          onChange={() => {
                            let perms = roleForm.permissions.filter(p => !['view_referrals','manage_referrals'].includes(p));
                            if (level === 'view') perms.push('view_referrals');
                            if (level === 'manage') perms.push('view_referrals', 'manage_referrals');
                            setRoleForm({...roleForm, permissions: perms});
                          }}
                        />
                        <span className={`text-xs ${level === 'none' ? 'text-gray-400' : level === 'view' ? 'text-blue-600' : 'text-green-600'}`}>
                          {level === 'none' ? 'None' : level === 'view' ? 'View' : 'Manage'}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Admin Panel Access */}
                <div>
                  <p className="font-medium text-gray-900 text-sm mb-2">🔐 Admin Panel Access</p>
                  <label className="flex items-center gap-2 ml-4 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={roleForm.permissions.includes('access_admin_panel')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setRoleForm({...roleForm, permissions: [...roleForm.permissions, 'access_admin_panel']});
                        } else {
                          setRoleForm({...roleForm, permissions: roleForm.permissions.filter(p => p !== 'access_admin_panel')});
                        }
                      }}
                    />
                    <span className="text-xs text-gray-600">Can access backoffice admin panel</span>
                  </label>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                {roleForm.permissions.length} permission(s) selected
              </p>
            </div>
          </div>

          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => {
              setShowRoleManager(false);
              setEditingRole(null);
              setRoleForm({ name: "", permissions: [] });
            }}>
              Cancel
            </Button>
            <Button onClick={saveCustomRole} disabled={savingRole}>
              {savingRole ? "Saving..." : editingRole ? "Update Role" : "Create Role"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unpublish Confirmation Dialog */}
      <Dialog open={unpublishDialog.open} onOpenChange={(open) => !open && setUnpublishDialog({ open: false, layout: null, checking: false, canUnpublish: true, reason: '' })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              {unpublishDialog.canUnpublish ? 'Unpublish Layout?' : 'Cannot Unpublish Layout'}
            </DialogTitle>
            <DialogDescription>
              {unpublishDialog.checking ? (
                <div className="flex items-center gap-2 py-4">
                  <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                  Checking if layout can be unpublished...
                </div>
              ) : unpublishDialog.canUnpublish ? (
                <div className="space-y-3 py-2">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-amber-800 text-sm font-medium mb-1">⚠️ Warning</p>
                    <p className="text-amber-700 text-sm">{unpublishDialog.reason}</p>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Layout: <strong>{unpublishDialog.layout?.name}</strong>
                  </p>
                </div>
              ) : (
                <div className="space-y-3 py-2">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-red-800 text-sm font-medium mb-1">🚫 Cannot Unpublish</p>
                    <p className="text-red-700 text-sm">{unpublishDialog.reason}</p>
                  </div>
                  {unpublishDialog.active_events?.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-gray-700 text-sm font-medium mb-2">Active Events:</p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {unpublishDialog.active_events.map((evt, i) => (
                          <li key={i} className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            {evt.name} - {evt.bookings} bookings
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {unpublishDialog.total_bookings > 0 && (
                    <p className="text-gray-600 text-sm">
                      Total active bookings: <strong className="text-red-600">{unpublishDialog.total_bookings}</strong>
                    </p>
                  )}
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setUnpublishDialog({ open: false, layout: null, checking: false, canUnpublish: true, reason: '' })}>
              Cancel
            </Button>
            {unpublishDialog.canUnpublish && !unpublishDialog.checking && (
              <Button 
                variant="destructive"
                onClick={() => executePublishToggle(unpublishDialog.layout)}
                disabled={publishing === unpublishDialog.layout?.id}
              >
                {publishing === unpublishDialog.layout?.id ? 'Unpublishing...' : 'Yes, Unpublish'}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* White Label Subscribe Confirmation Dialog */}
      <Dialog open={showWhiteLabelConfirm} onOpenChange={setShowWhiteLabelConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-600" />
              Subscribe to White Label
            </DialogTitle>
            <DialogDescription>
              Remove SeatPilot branding from your checkout widgets
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Price Card */}
            <div className="p-4 bg-gradient-to-r from-violet-50 to-indigo-50 rounded-lg border border-violet-200">
              <div className="flex justify-between items-center mb-3">
                <span className="font-semibold text-gray-900">White Label Add-on</span>
                <div className="text-right">
                  <span className="text-2xl font-bold text-violet-600">£20</span>
                  <span className="text-gray-500 text-sm">/month</span>
                </div>
              </div>
              <p className="text-sm text-gray-600">Added to your current subscription</p>
            </div>
            
            {/* Benefits */}
            <div className="space-y-2">
              <p className="font-medium text-gray-900 text-sm">What you'll get:</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-500" />
                  Remove "Powered by SeatPilot" branding
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-500" />
                  Professional look for your customers
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-500" />
                  Cancel anytime - no commitment
                </li>
              </ul>
            </div>
            
            {/* Payment Method Info */}
            {paymentMethod && (
              <div className="p-3 bg-gray-50 rounded-lg border">
                <p className="text-xs text-gray-500 mb-1">Payment method</p>
                <p className="text-sm font-medium text-gray-900">
                  {paymentMethod.brand?.toUpperCase() || "Card"} •••• {paymentMethod.last4}
                </p>
              </div>
            )}
          </div>
          
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => setShowWhiteLabelConfirm(false)}
            >
              Cancel
            </Button>
            <Button 
              className="flex-1 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700"
              disabled={subscribingWhiteLabel}
              onClick={async () => {
                setSubscribingWhiteLabel(true);
                try {
                  const res = await axios.post(`${getAPI()}/subscriptions/white-label/subscribe`, {
                    origin_url: window.location.origin
                  }, { withCredentials: true });
                  
                  if (res.data.checkout_url) {
                    window.location.href = res.data.checkout_url;
                  } else {
                    // Success - subscription activated
                    toast.success(res.data.message || "White Label subscription activated!");
                    setShowWhiteLabelConfirm(false);
                    await fetchCompanyData();
                  }
                } catch (e) {
                  const errorDetail = e.response?.data?.detail || "";
                  
                  // Check if already subscribed (this is actually success)
                  if (errorDetail.includes("Already subscribed")) {
                    toast.success("White Label is already active!");
                    setShowWhiteLabelConfirm(false);
                    await fetchCompanyData();
                    return;
                  }
                  
                  // Real error
                  toast.error(errorDetail || "Failed to subscribe. Please try again.");
                } finally {
                  setSubscribingWhiteLabel(false);
                }
              }}
            >
              {subscribingWhiteLabel ? "Subscribing..." : "Confirm Subscription"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* White Label Reactivation Confirmation Dialog */}
      <Dialog open={showReactivateConfirm} onOpenChange={setShowReactivateConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-green-600" />
              Reactivate White Label
            </DialogTitle>
            <DialogDescription>
              Your White Label subscription is currently scheduled to be cancelled
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Info Card */}
            <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
              <div className="flex justify-between items-center mb-3">
                <span className="font-semibold text-gray-900">White Label Add-on</span>
                <div className="text-right">
                  <span className="text-2xl font-bold text-green-600">£20</span>
                  <span className="text-gray-500 text-sm">/month</span>
                </div>
              </div>
              <p className="text-sm text-gray-600">Reactivating will resume your monthly billing</p>
            </div>
            
            {/* What happens */}
            <div className="space-y-2">
              <p className="font-medium text-gray-900 text-sm">When you reactivate:</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-500" />
                  Cancellation will be reversed immediately
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-500" />
                  "Powered by SeatPilot" stays hidden
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-500" />
                  Normal billing continues at £20/month
                </li>
              </ul>
            </div>
            
            {/* Payment Method Info */}
            {paymentMethod && (
              <div className="p-3 bg-gray-50 rounded-lg border">
                <p className="text-xs text-gray-500 mb-1">Payment method</p>
                <p className="text-sm font-medium text-gray-900">
                  {paymentMethod.brand?.toUpperCase() || "Card"} •••• {paymentMethod.last4}
                </p>
              </div>
            )}
          </div>
          
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => setShowReactivateConfirm(false)}
            >
              Keep Cancelled
            </Button>
            <Button 
              className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
              disabled={reactivatingWhiteLabel}
              onClick={async () => {
                setReactivatingWhiteLabel(true);
                try {
                  const res = await axios.post(`${getAPI()}/subscriptions/white-label/reactivate`, {}, { withCredentials: true });
                  toast.success(res.data.message || "White Label reactivated successfully!");
                  setShowReactivateConfirm(false);
                  await fetchCompanyData();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Failed to reactivate. Please try again.");
                } finally {
                  setReactivatingWhiteLabel(false);
                }
              }}
            >
              {reactivatingWhiteLabel ? "Reactivating..." : "Confirm Reactivation"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Auto-renew OFF re-confirmation — explicit warning that Stripe
          will be told to cancel at period end so the customer cannot say
          we surprise-cancelled them. Audit trail captures the action. */}
      <Dialog open={showAutoRenewOffConfirm} onOpenChange={setShowAutoRenewOffConfirm}>
        <DialogContent className="max-w-md" data-testid="auto-renew-off-dialog">
          <DialogHeader>
            <DialogTitle>Turn auto-renew OFF?</DialogTitle>
            <DialogDescription>
              We&apos;ll keep your plan running until the current period ends, but it won&apos;t renew after that.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2 text-sm">
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-900">
              <p className="font-semibold">What happens next</p>
              <ul className="text-xs mt-1 list-disc ml-4 space-y-1">
                <li>Your card will <strong>NOT</strong> be charged on {subscription?.expires_at ? new Date(subscription.expires_at).toLocaleDateString('en-GB', {day:'numeric', month:'short', year:'numeric'}) : 'the next renewal date'}.</li>
                <li>We&apos;ll tell Stripe to cancel your recurring subscription at the end of the current period (no refunds for partial months).</li>
                <li>You keep full access until the expiry date, then your plan moves to <strong>Expired</strong>.</li>
                <li>You can turn auto-renew back ON anytime to restore the recurring charge.</li>
              </ul>
            </div>
            <p className="text-xs text-gray-500">
              This action is logged with your IP address and timestamp.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAutoRenewOffConfirm(false)} disabled={autoRenewSaving}>Keep auto-renew ON</Button>
            <Button
              variant="destructive"
              data-testid="auto-renew-off-confirm-btn"
              disabled={autoRenewSaving}
              onClick={async () => {
                setAutoRenewSaving(true);
                try {
                  const r = await axios.post(
                    `${getAPI()}/subscriptions/auto-renew`,
                    { enabled: false },
                    { withCredentials: true }
                  );
                  toast.success(r.data?.message || "Auto-renew disabled");
                  setShowAutoRenewOffConfirm(false);
                  fetchCompanyData();
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Failed to update auto-renew");
                } finally {
                  setAutoRenewSaving(false);
                }
              }}
            >
              {autoRenewSaving ? "Saving…" : "Yes, turn OFF"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto-renew T&C confirmation — user must tick the box before we
          flip auto-renew ON. Acceptance is forwarded to the backend which
          timestamps + IP-stamps it in the payment audit log. */}
      <Dialog open={showAutoRenewTerms} onOpenChange={(v) => { if (!v) setShowAutoRenewTerms(false); }}>
        <DialogContent className="max-w-lg" data-testid="auto-renew-terms-dialog">
          <DialogHeader>
            <DialogTitle>Enable auto-renewal</DialogTitle>
            <DialogDescription>
              Please confirm you agree to the recurring charge terms below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            <div className="p-3 rounded-lg bg-violet-50 border border-violet-200 text-violet-900">
              <p className="font-semibold">You will be charged monthly, upfront</p>
              <p className="text-xs mt-1">
                Your saved card will be charged {subscription?.monthly_price ? `£${subscription.monthly_price}` : 'the plan price'} on every renewal date ({subscription?.expires_at ? new Date(subscription.expires_at).toLocaleDateString('en-GB', {day:'numeric', month:'short', year:'numeric'}) : 'the next billing date'} and then monthly) until you turn auto-renew OFF or cancel your plan.
              </p>
            </div>
            <ul className="text-xs text-gray-700 space-y-1 list-disc ml-5">
              <li>You can turn auto-renew OFF at any time from this page — you'll keep access until your current period ends.</li>
              <li>Turning OFF also schedules Stripe to stop the recurring charge — no refunds for partial months.</li>
              <li>All changes are logged with your IP, timestamp, and the logged-in user for your own security.</li>
            </ul>
            <label className="flex items-start gap-2 cursor-pointer pt-2 border-t">
              <input
                type="checkbox"
                data-testid="auto-renew-terms-checkbox"
                checked={autoRenewTermsAccepted}
                onChange={(e) => setAutoRenewTermsAccepted(e.target.checked)}
                className="mt-1"
              />
              <span className="text-sm text-gray-800">
                I agree to the auto-renewal terms and authorise SeatPilot to charge my saved payment method on every renewal date until I cancel.
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAutoRenewTerms(false)} disabled={autoRenewSaving}>Cancel</Button>
            <Button
              disabled={!autoRenewTermsAccepted || autoRenewSaving}
              data-testid="auto-renew-confirm-btn"
              onClick={async () => {
                setAutoRenewSaving(true);
                try {
                  const r = await axios.post(
                    `${getAPI()}/subscriptions/auto-renew`,
                    { enabled: true, terms_accepted: true, terms_version: 'v1' },
                    { withCredentials: true }
                  );
                  toast.success(r.data?.message || "Auto-renew enabled");
                  setShowAutoRenewTerms(false);
                  setAutoRenewTermsAccepted(false);
                  fetchCompanyData();
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Failed to enable auto-renew");
                } finally {
                  setAutoRenewSaving(false);
                }
              }}
            >
              {autoRenewSaving ? "Saving…" : "Confirm & enable"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dashboard walkthrough — auto-opens for new users, replayable via Help button */}
      <CanvasWalkthrough 
        open={dashboardWalkthrough.open}
        onClose={dashboardWalkthrough.close}
        steps={STEPS_DASHBOARD}
      />

      {/* Floating Help button to replay the tour anytime */}
      <button
        onClick={dashboardWalkthrough.replay}
        className="fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full bg-violet-600 hover:bg-violet-700 text-white shadow-lg flex items-center justify-center transition-transform hover:scale-110"
        title="Replay dashboard tour"
        data-testid="dashboard-help-btn"
      >
        <HelpCircle className="w-6 h-6" />
      </button>
    </div>
  );
}
